import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { api } from '../api/client';
import QuotedExternal from './QuotedExternal';
import { useContacts } from '../contexts/ContactsContext';
import { useInvoices } from '../contexts/InvoicesContext';
import { loadSession, persistMessages, newSessionId, generateSessionTitle, saveSessionToDb, getLastDbSession, loadSessionFromDb, cacheSession } from '../services/aideySessionsService';
import { getTravelInfo, getBookingLinks, findSameDayInPersonMeetings, MODE_EMOJI, MODE_LABEL } from '../services/travelTimeService';
import { createGoogleMeet, formatMeetingForGoogleMeet, isGoogleMeetAvailable } from '../utils/googleMeetUtils';
import { isConfirmReply, isDeclineReply, replaceApprovalCard, settlePlaceholder, outcomeMessage, declinedMarker } from '../utils/confirmationHistory';
import { isGoogleConnected } from '../utils/googleAuth';
import { createTeamsMeeting, isTeamsConnected } from '../utils/teamsUtils';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { useWorkspaceRoster, useWorkspaceRosterMap, rosterParseMentions } from '../hooks/useWorkspaceRoster';
import { loadStyleProfile, buildStyleSystemPrompt, analyseWritingSample, loadLearnedPatterns } from '../services/writingMemoryService';
import { suggestPrioritySwap } from '../utils/taskUtils';
import useVoiceInput from '../hooks/useVoiceInput';
import VoiceInput from './AideyChat/VoiceInput';
import { detectIntent } from '../services/voiceService';
import MeetingMode from './MeetingMode';
import MeetingTranscripts from './Meetings/MeetingTranscripts';
import MeetingRecorder from './MeetingRecorder';
import DocumentImporter, { IMPORT_ACCEPT, importFileProblem } from './Projects/DocumentImporter';
import { toLocalDateStr } from '../utils/calendarUtils';
import { formatTaskClock, formatTaskWhen } from '../utils/taskTime';   // fix(task-dates)
import { Button, StatusPill, Textarea, Select, IconTile, Banner, Avatar, Modal, useBreakpoint, v, useRevealForm } from '../ui';   // Block C
// C-b (decision 77): the toggles, the tags' areas, the action cards and the popup's summary.
import { AREA_TAG, TOGGLE_AREAS, markCardsDone, markCardsDoneFromOutcomes } from '../utils/chatAreas';
import { popupSummaryFacts } from '../utils/chatSummary';
import { setDeepLink } from '../utils/deepLink';
import { messageFromError } from '../utils/apiError';
import { formatMoney } from '../utils/currency';
import { safeUnsubscribeUrl } from './Email/EmailParts';   // Email Phase 2: the one check of a sender's link
import { shouldAutoCreateTravelTask } from '../utils/travelTask';   // Settings → Travel: the auto-create switch
import './AideyChat/chat.css';
import { downloadInvoicePdf } from '../utils/invoicePdf';   // build everything: the chat's PDF card
const ALL_AREAS_ON = Object.freeze(Object.fromEntries(TOGGLE_AREAS.map(a => [a, true])));   // decision 84

/**
 * === Helper Functions ===
 */
function getTodayISO() {
  return toLocalDateStr(new Date());
}
function getTomorrowISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return toLocalDateStr(d);
}

function getOrdinalSuffix(n) {
  const j = n % 10, k = n % 100;
  if (j === 1 && k !== 11) return "st";
  if (j === 2 && k !== 12) return "nd";
  if (j === 3 && k !== 13) return "rd";
  return "th";
}

// "14:00" → "2pm"  |  "14:30" → "2:30pm"  |  "09:00" → "9am"
function fmtTime(t) {
  return formatTaskClock(t, { compact: true });   // fix(task-dates): the shared formatter, chat style
}

// "2026-04-08" → "8th April"  (year appended only if ≠ current year)
function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d + 'T12:00:00');
  const day = dt.getDate();
  const month = dt.toLocaleDateString('en-GB', { month: 'long' });
  const base = `${day}${getOrdinalSuffix(day)} ${month}`;
  return dt.getFullYear() === new Date().getFullYear() ? base : `${base} ${dt.getFullYear()}`;
}

// "2pm on 8th April"
function fmtDateTime(dateStr, timeStr) {
  const d = fmtDate(dateStr), t = fmtTime(timeStr);
  if (d && t) return `${t} on ${d}`;
  return d || t || '';
}

// Builds a brief "[User confirmed: ...]" summary for chat history continuity
function buildConfirmSummary(tool, input = {}) {
  if (tool === 'send_email' || tool === 'send_email_legacy') {
    return `[User confirmed: send the email to ${input.to || 'recipient'}]`;
  }
  if (tool === 'create_invoice') {
    return `[User confirmed: create invoice for ${input.client_name || 'client'}, £${input.amount || '?'}]`;
  }
  if (tool === 'send_invoice_chase') {
    return `[User confirmed: send invoice chase to ${input.contact_name || input.to || 'client'}]`;
  }
  if (tool === 'reschedule_meeting') {
    return `[User confirmed: reschedule meeting "${input.title || 'meeting'}"]`;
  }
  if (tool === 'update_task') {
    return `[User confirmed: update task "${input.title || input.taskId || 'task'}"]`;
  }
  if (tool === 'send_overdue_task_nudges') {
    const count = Array.isArray(input.draftedNudges) ? input.draftedNudges.length : 'overdue';
    return `[User confirmed: send ${count} task nudge(s)]`;
  }
  if (tool === 'redistribute_workload') {
    const count = Array.isArray(input.approvedSuggestions) ? input.approvedSuggestions.length : 0;
    return `[User confirmed: move ${count} task(s) to balance team workload]`;
  }
  return `[User confirmed: ${tool.replace(/_/g, ' ')}]`;
}

function stripMarkdown(text) {
  return text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`{1,3}(.*?)`{1,3}/gs, '$1')
    .replace(/^[-*•]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '');
  // [label](url) intentionally kept — renderMessage converts these to <a> elements
}

// ─── Message renderer — converts Aidey's markdown subset to React elements ────
// Handles: [label](url) links (including mailto:), and \n line breaks.
function renderMessage(text) {
  if (!text) return null;
  const cleaned = stripMarkdown(text);
  // Split into lines, then parse each line for links
  return cleaned.split('\n').map((line, li) => {
    // Tokenise the line into segments: text | link | bold
    const tokens = [];
    const pattern = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;
    let last = 0, m;
    while ((m = pattern.exec(line)) !== null) {
      if (m.index > last) tokens.push({ type: 'text', value: line.slice(last, m.index) });
      if (m[1] !== undefined) {
        // Link: [label](url)
        const isMailto = m[2].startsWith('mailto:');
        tokens.push({ type: 'link', label: m[1], href: m[2], isMailto });
      } else {
        // Bold: **text**
        tokens.push({ type: 'bold', value: m[3] });
      }
      last = m.index + m[0].length;
    }
    if (last < line.length) tokens.push({ type: 'text', value: line.slice(last) });

    const rendered = tokens.map((tok, ti) => {
      if (tok.type === 'bold') return <strong key={ti}>{tok.value}</strong>;
      if (tok.type === 'link') {
        // Block C: the colour and underline come from chat.css (.achat-bubble a, .achat-user a).
        return (
          <a key={ti} href={tok.href} target={tok.isMailto ? '_self' : '_blank'} rel="noopener noreferrer">
            {tok.label}
          </a>
        );
      }
      return tok.value;
    });

    return (
      <span key={li}>
        {rendered}
        {li < text.split('\n').length - 1 && <br />}
      </span>
    );
  });
}

// ─── Meeting Notes Parser ─────────────────────────────────────────────────────
// Extracts action items from free-form meeting notes.
// Supports name, @mention, department ("the marketing team will…"), and job title assignment.
function parseMeetingNotes(text, allUsers) {
  const items = [];
  const lines = text.split(/\n|;/);

  // Match a user by name, @handle, job title, or email prefix
  const findUser = (hint) => {
    if (!hint) return null;
    const q = hint.trim().replace(/^@/, '').toLowerCase();
    return allUsers.find(u =>
      u.name.toLowerCase().startsWith(q) ||
      u.name.toLowerCase().includes(q) ||
      (u.email && u.email.toLowerCase().startsWith(q)) ||
      (u.jobTitle && u.jobTitle.toLowerCase().includes(q))
    ) || null;
  };

  // Return all users in a named department
  const findDeptUsers = (deptHint) => {
    if (!deptHint) return [];
    const q = deptHint.trim().toLowerCase();
    return allUsers.filter(u => u.department && u.department.toLowerCase().includes(q));
  };

  for (const line of lines) {
    const trimmed = line.trim().replace(/^[-*•]\s*/, "");
    if (!trimmed || trimmed.length < 4) continue;

    // Pattern 1: "the [dept] team/department will/should/to [action]"
    const p1 = trimmed.match(/^(?:the\s+)?(.+?)\s+(?:team|department|dept)\s+(?:will|should|to|needs? to|must|has to)\s+(.+)/i);
    if (p1) {
      const deptUsers = findDeptUsers(p1[1]);
      if (deptUsers.length > 0) {
        items.push({ title: p1[2].trim(), assigneeDept: p1[1].trim(), assigneeIds: deptUsers.map(u => u.id), assigneeName: null, assigneeId: null });
        continue;
      }
    }

    // Pattern 2: "Name/JobTitle will/should/to/needs to action…"
    const p2 = trimmed.match(/^([A-Z][a-zÀ-ÿ]+(?:\s[A-Za-zÀ-ÿ]+)*)\s+(?:will|should|to|needs to|has to|must)\s+(.+)/i);
    if (p2) {
      const user = findUser(p2[1]);
      items.push({ title: p2[2].trim(), assigneeName: user?.name || p2[1], assigneeId: user?.id || null, assigneeIds: null });
      continue;
    }

    // Pattern 3: "@handle action"
    const p3 = trimmed.match(/^@(\w+)\s+(.+)/);
    if (p3) {
      const user = findUser(p3[1]);
      items.push({ title: p3[2].trim(), assigneeName: user?.name || p3[1], assigneeId: user?.id || null, assigneeIds: null });
      continue;
    }

    // Pattern 4: "action item: text – Name/Dept"
    const p4 = trimmed.match(/^action items?:?\s+(.+?)(?:\s*[-–(]\s*(?:assigned to\s+)?(@?\w+(?:\s\w+)?)[\s)]*)?$/i);
    if (p4) {
      const hint = p4[2];
      const user = hint ? findUser(hint) : null;
      const deptUsers = (hint && !user) ? findDeptUsers(hint) : [];
      if (deptUsers.length > 0) {
        items.push({ title: p4[1].trim(), assigneeDept: hint, assigneeIds: deptUsers.map(u => u.id), assigneeName: null, assigneeId: null });
      } else {
        items.push({ title: p4[1].trim(), assigneeName: user?.name || hint || null, assigneeId: user?.id || null, assigneeIds: null });
      }
      continue;
    }

    // Pattern 5: "[ ] task text – Name/Dept" or "task text – Name"
    const p5 = trimmed.match(/^(?:\[\s*\]\s+)?(.+?)\s*[-–]\s*@?([A-Za-z]\w+(?:\s[A-Z][a-z]+)?)$/);
    if (p5 && p5[1].length > 3) {
      const user = findUser(p5[2]);
      const deptUsers = !user ? findDeptUsers(p5[2]) : [];
      if (deptUsers.length > 0) {
        items.push({ title: p5[1].trim(), assigneeDept: p5[2], assigneeIds: deptUsers.map(u => u.id), assigneeName: null, assigneeId: null });
      } else {
        items.push({ title: p5[1].trim(), assigneeName: user?.name || p5[2], assigneeId: user?.id || null, assigneeIds: null });
      }
      continue;
    }

    // Pattern 6: bare checkbox/bullet — no assignment
    const p6 = trimmed.match(/^\[?\s*\]?\s*(.{5,})/);
    if (p6 && !/^(present|attendees|agenda|date|time|location|re:|subject):/i.test(p6[1])) {
      items.push({ title: p6[1].trim(), assigneeName: null, assigneeId: null, assigneeIds: null });
    }
  }

  return items;
}

// Recurring, Delegation, Multi-Action, Advanced Field Detection!
function parseUserInput(input, members = []) {
  // MULTI-ACTION SPLIT
  const actions = input.split(/\b(?:and|then)\b/gi).map(s => s.trim());
  if (actions.length > 1) return actions.map(parseUserInput);

  const lower = input.toLowerCase();
  const details = {};

  // ACTION DETECTION
  if (/move|reschedule/.test(lower)) details.action = "move";
  else if (/cancel|delete|remove/.test(lower)) details.action = "cancel";
  else if (/\b(write|generate|draft|compose)\b.+\b(blog|post|article|caption|copy|pr|press\s*release|social|tweet|newsletter|announcement)\b/i.test(lower) ||
    /\b(blog post|pr copy|press release|social caption|tweet|newsletter)\b/i.test(lower)) {
    details.action = "generate-content";
    const cMatch = lower.match(/\b(blog post|press release|social caption|tweet|caption|pr copy|newsletter|email draft|announcement|article)\b/i);
    details.contentType = cMatch ? cMatch[1] : "content";
  }
  else if (/\b(when am i free|find.*slot|available.*time|free.*time|what.*available|open slot|find me a time)\b/i.test(lower)) details.action = "find-slots";
  else if (/\b(what.*agenda|agenda today|on my schedule|my day|tasks today|i have \d+ tasks)\b/i.test(lower)) details.action = "agenda";
  else if (/\b(send|share)\b.+\b(booking link|meeting link|scheduling link|meeting request)\b/i.test(lower) ||
    /\b(booking link|scheduling link)\b.+\b(to|with)\b/i.test(lower)) details.action = "send-booking-link";
  else if (/\b(reply to email|draft email|send email|email to|summarize.*emails?|check.*emails?)\b/i.test(lower)) details.action = "email-command";
  else if (/\b(add contact|manage contact|find contact|show contacts|new contact)\b/i.test(lower)) details.action = "contacts";
  else if (/\b(zoom.*transcript|zoom.*recording|google meet.*transcript|meet.*transcript|teams.*transcript|recent.*meeting notes|process.*zoom|process.*meet|show.*zoom|my zoom meetings|zoom meetings|meeting transcript|meeting recordings|my.*recordings|show.*meeting notes|show my meetings|process.*meetings|recent transcripts|upload.*transcript|upload.*meeting)\b/i.test(lower)) details.action = "zoom-transcripts";
  else if (/\b(meeting notes|log.*meeting|take.*notes|action items from|meeting summary|summarise.*meeting|summarize.*meeting|start.*meeting|open.*meeting|meeting recorder)\b/i.test(lower)) details.action = "meeting-notes";
  else if (/\b(help me (write|draft|phrase|word|improve|edit|rephrase|rewrite)|improve (this|my)|make (this|it) (better|clearer|shorter|more professional|punchier)|how does this sound|edit this|fix this|review my|suggest.*subject|rephrase|rewrite this|check my writing)\b/i.test(lower)) details.action = "writing-help";
  else if (/\b(create invoice|new invoice|send invoice|add invoice|invoice.*for|draft invoice|bill.*client|raise invoice)\b/i.test(lower)) details.action = "create-invoice";
  else if (/\b(book.*hotel|book.*flight|book.*travel|travel.*to|flight.*to|arrange.*travel|plan.*trip)\b/i.test(lower)) details.action = "travel";
  else if (/\b(add|invite)\s+\S.+\s+(to|into)\s+(?:the\s+)?\S/i.test(lower) && !/\b(task|contact|note|list|team)\b/i.test(lower)) {
    details.action = "add-attendee";
    const addMatch = input.match(/\b(?:add|invite)\s+(.+?)\s+(?:to|into)\s+(?:the\s+)?(.+?)(?:\s+meeting|\s+call|\s+appointment)?\s*$/i);
    if (addMatch) {
      details.addPersonName = addMatch[1].trim();
      details.addToMeetingTitle = addMatch[2].trim();
    }
  }
  else details.action = "create";

  // Reclassify generic 'create' as 'schedule_meeting' when meeting keywords are present
  if (details.action === 'create' && (
    /\b(meeting|call|catch[- ]?up|sync|video\s+call|google\s*meet|zoom|teams|skype|appointment)\b/i.test(lower) ||
    /\b(face[\s-]to[\s-]face|in[\s-]person|schedule\s+with|book\s+with)\b/i.test(lower)
  )) {
    details.action = 'schedule_meeting';
  }

  // DATE DETECTION
  let dateTextToStrip = '';
  if (lower.includes("today")) details.date = getTodayISO();
  if (lower.includes("tomorrow")) details.date = getTomorrowISO();
  const isoDateMatch = input.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (isoDateMatch) details.date = isoDateMatch[1];

  // Natural language dates: "8th April", "April 8th", "8 April", "the 8th of April"
  if (!details.date) {
    const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
    const monthPat = MONTHS.join('|');
    // "8th April" / "8 April" / "the 8th of April"
    const nlDate1 = input.match(new RegExp(`\\b(?:the\\s+)?(\\d{1,2})(?:st|nd|rd|th)?(?:\\s+of)?\\s+(${monthPat})`, 'i'));
    // "April 8th" / "April 8"
    const nlDate2 = !nlDate1 && input.match(new RegExp(`\\b(${monthPat})\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b`, 'i'));
    if (nlDate1 || nlDate2) {
      let day, monthName;
      if (nlDate1) { day = parseInt(nlDate1[1]); monthName = nlDate1[2].toLowerCase(); }
      else { monthName = nlDate2[1].toLowerCase(); day = parseInt(nlDate2[2]); }
      const monthIdx = MONTHS.indexOf(monthName);
      if (monthIdx !== -1 && day >= 1 && day <= 31) {
        const now = new Date();
        let year = now.getFullYear();
        const candidate = new Date(year, monthIdx, day);
        if (candidate < now && candidate.toDateString() !== now.toDateString()) year++;
        details.date = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        dateTextToStrip = (nlDate1 || nlDate2)[0];
      }
    }
  }

  // Weekday names: "next Monday", "this Friday", "on Wednesday"
  if (!details.date) {
    const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayMatch = input.match(/\b(?:next|this)?\s*(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/i);
    if (dayMatch) {
      const targetDay = DAYS.indexOf(dayMatch[1].toLowerCase());
      const now = new Date();
      let diff = targetDay - now.getDay();
      if (diff <= 0) diff += 7;
      const d = new Date(now);
      d.setDate(d.getDate() + diff);
      details.date = toLocalDateStr(d);
    }
  }

  // DURATION DETECTION — must come before time detection to avoid false positives
  const durationMatch = input.match(/\b(\d+(?:\.\d+)?)\s*(hours?|hrs?|minutes?|mins?)\b/i);
  if (durationMatch) {
    const val = parseFloat(durationMatch[1]);
    const unit = durationMatch[2].toLowerCase();
    details.durationMinutes = /^h/.test(unit) ? Math.round(val * 60) : Math.round(val);
  }

  // TIME DETECTION — strip duration token and natural date text first to avoid false positives
  let inputForTime = durationMatch ? input.replace(durationMatch[0], '') : input;
  if (dateTextToStrip) inputForTime = inputForTime.replace(dateTextToStrip, '');
  const timeMatch = inputForTime.match(/(\d{1,2})(?:[.:](\d{2}))?\s*(am|pm)?/i);
  if (timeMatch) {
    let hours = parseInt(timeMatch[1], 10);
    let minutes = timeMatch[2] ? parseInt(timeMatch[2], 10) : 0;
    if (/pm/i.test(timeMatch[3]) && hours < 12) hours += 12;
    if (/am/i.test(timeMatch[3]) && hours === 12) hours = 0;

    // Assume PM for ambiguous times 6–11 unless am/pm specified
    if (!timeMatch[3] && hours >= 6 && hours <= 11) {
      hours += 12;
    }

    details.time = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }

  // RECURRING
  const recurDay = input.match(/every\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i);
  if (recurDay) {
    details.recurrence = { type: "weekly", day: recurDay[1].toLowerCase() };
  }
  const recurMonth = input.match(/every\s+(\d+)(st|nd|rd|th)\s*of\s*the\s*month/i);
  if (recurMonth) {
    details.recurrence = { type: "monthly", date: parseInt(recurMonth[1]) };
  }

  // TASK TYPE & "WITH" PERSON - Enhanced title parsing
  const typeMatch = input.match(/(meeting|call|appointment|event|task|zoom|meet)/i);
  const withMatch = input.match(/with\s+([A-Za-z\s]+?)(?:\s+(?:today|tomorrow|on|at|\d))/i);

  // Check for specific meeting platform requests
  const isZoomRequest = /zoom/i.test(input);
  const isGoogleMeetRequest = /google\s*meet/i.test(input) && !/zoom/i.test(input); // ✅ More specific - require "google meet" not just "meet"

  if (typeMatch && withMatch) {
    details.title = `${typeMatch[1][0].toUpperCase() + typeMatch[1].slice(1)} with ${withMatch[1].trim()}`;
    details.taskType = typeMatch[1][0].toUpperCase() + typeMatch[1].slice(1);
    if (isZoomRequest) {
      details.meetingType = "Zoom";
    } else if (isGoogleMeetRequest) {
      details.meetingType = "Google Meet";
    }
  } else if (typeMatch) {
    // For standalone task types, create a more descriptive title
    const taskType = typeMatch[1][0].toUpperCase() + typeMatch[1].slice(1);
    details.taskType = taskType;
    if (isZoomRequest) {
      details.meetingType = "Zoom";
      details.taskType = "Meeting";
    } else if (isGoogleMeetRequest) {
      details.meetingType = "Google Meet";
      details.taskType = "Meeting";
    }
    // Try to extract more context for the title
    if (withMatch) {
      details.title = `${taskType} with ${withMatch[1].trim()}`;
    } else {
      details.title = taskType;
    }
  } else if (withMatch) {
    details.title = `Meeting with ${withMatch[1].trim()}`;
    details.taskType = "Meeting";
    if (isZoomRequest) {
      details.meetingType = "Zoom";
    } else if (isGoogleMeetRequest) {
      details.meetingType = "Google Meet";
    }
  } else if (isZoomRequest) {
    details.title = details.title || "Zoom Meeting";
    details.taskType = "Meeting";
    details.meetingType = "Zoom";
  } else if (isGoogleMeetRequest) {
    details.title = details.title || "Google Meet";
    details.taskType = "Meeting";
    details.meetingType = "Google Meet";
  }

  // PRIORITY
  if (/high[- ]?priority/i.test(lower)) details.priority = "High";
  else if (/low[- ]?priority/i.test(lower)) details.priority = "Low";
  else if (/medium[- ]?priority/i.test(lower)) details.priority = "Medium";

  // REMINDER (minutes before)
  const reminderMatch = input.match(/remind(?:er)?\s*(?:me)?\s*(\d+)\s*(minutes?|hours?)\s*before/i);
  if (reminderMatch) {
    const n = parseInt(reminderMatch[1]);
    details.reminderOffset = /hour/.test(reminderMatch[2]) ? n * 60 : n;
  }

  // ASSIGNEE/DELEGATION & @MENTIONS
  const assignMatch = input.match(/(?:assign|delegate)\s+(?:this\s+)?(?:to\s+)?([A-Za-z\s]+)/i);
  if (assignMatch) {
    details.assignedTo = assignMatch[1].trim();
  }

  // @MENTION PARSING — resolve against the active-workspace roster.
  const mentions = rosterParseMentions(members, input);
  if (mentions.length > 0) {
    details.mentions = mentions;
    details.assignedTo = mentions.map(m => m.id); // Store as array of user IDs
    details.attendees = mentions.map(m => m.id); // Also add as attendees for meetings

    // If it's a meeting with mentions, enhance the title
    if (details.taskType === "Meeting" || /meeting|call|appointment/i.test(input)) {
      const mentionNames = mentions.map(m => m.name).join(", ");
      if (!details.title || details.title === "Meeting") {
        details.title = `Meeting with ${mentionNames}`;
      }
    }
  }

  // TASK DETAILS
  const detailMatch = input.match(/details?\s*:\s*([\w\s.,'-]+)/i);
  if (detailMatch) details.details = detailMatch[1].trim();

  // LOCATION DETECTION — must start with a letter so "at 2pm" is never captured as a location
  const locationMatch = input.match(/\bat\s+(?!\d)([A-Za-z][A-Za-z0-9\s.,'-]{1,50}?)(?=\s+(?:today|tomorrow|\bon\s|\d{1,2}(?::\d{2})?\s*(?:am|pm))|,|$)/i);
  if (locationMatch) {
    const locText = locationMatch[1].trim();
    // Also reject if the text is just a meeting-mode phrase
    if (!/^(?:in\s*person|online|virtual|remote|the\s+office)$/i.test(locText)) {
      details.location = locText;
    }
  }

  // MEETING MODE DETECTION
  if (/online|virtual|remote|zoom|google\s*meet|teams|video/i.test(lower)) {
    details.meetingMode = "online";
    if (/zoom/i.test(lower)) details.meetingType = "Zoom";
    else if (/google\s*meet/i.test(lower) && !/zoom/i.test(lower)) details.meetingType = "Google Meet"; // ✅ More specific
    else if (/teams/i.test(lower)) details.meetingType = "Teams";
  } else if (/in\s*person|office|room|address|conference\s*room/i.test(lower)) {
    details.meetingMode = "in person";
  }

  // CATCH ALL: Better fallback for quick tasks - don't split on "at" if it's part of time
  if (!details.title) {
    let cleanTitle = input
      // Strip action verbs at the start
      .replace(/^(schedule|book|add|create|set\s+up|remind\s+me\s+to|log|record|note)\s+(a|an|the)?\s*/i, '')
      // Strip duration patterns
      .replace(/\b\d+(?:\.\d+)?\s*(?:hours?|hrs?|minutes?|mins?)\b/gi, '')
      // Strip date/time patterns
      .replace(/\b(?:today|tomorrow)\b/gi, '')
      .replace(/\b\d{4}-\d{2}-\d{2}\b/g, '')
      .replace(/\b(\d{1,2})(?:[.:](\d{2}))?\s*(am|pm)?\b/gi, '')
      // Strip trailing filler
      .replace(/\b(on|at|for|in)\s*$/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    details.title = cleanTitle || "New Task";
  }

  details.rawInput = input;
  return details;
}

// Enhanced conflict check that includes all calendar sources
function findConflict(details, schedule, googleEvents = [], icalEvents = [], outlookEvents = []) {
  if (!details.date || !details.time) return null;

  const start = new Date(`${details.date}T${details.time}`);
  const durationMs = (details.durationMinutes || 60) * 60 * 1000;
  const end = new Date(start.getTime() + durationMs);

  // Check against Aidey tasks
  const aideyConflict = schedule.find(t => {
    if (!t.date || !t.time) return false;
    const tStart = new Date(`${t.date}T${t.time}`);
    const tEnd = new Date(t.deadline || tStart.getTime() + 60 * 60 * 1000);
    return t.date === details.date && (start < tEnd && end > tStart);
  });

  if (aideyConflict) return aideyConflict;

  // Check against Google Calendar events
  const googleConflict = googleEvents.find(event => {
    if (!event.date || !event.time) return false;
    const eventStart = new Date(`${event.date}T${event.time}`);
    const eventEnd = new Date(eventStart.getTime() + 60 * 60 * 1000); // Assume 1 hour if no end time
    return event.date === details.date && (start < eventEnd && end > eventStart);
  });

  if (googleConflict) return googleConflict;

  // Check against iCal events
  const icalConflict = icalEvents.find(event => {
    if (!event.date || !event.time) return false;
    const eventStart = new Date(`${event.date}T${event.time}`);
    const eventEnd = new Date(eventStart.getTime() + 60 * 60 * 1000);
    return event.date === details.date && (start < eventEnd && end > eventStart);
  });

  if (icalConflict) return icalConflict;

  // Check against Outlook events
  const outlookConflict = outlookEvents.find(event => {
    if (!event.date || !event.time) return false;
    const eventStart = new Date(`${event.date}T${event.time}`);
    const eventEnd = new Date(eventStart.getTime() + 60 * 60 * 1000);
    return event.date === details.date && (start < eventEnd && end > eventStart);
  });

  return outlookConflict || null;
}

// Returns { time, date, dayOffset } or null. Searches up to 3 days ahead within 07:00–21:00.
function findNextAvailable(details, schedule, durationMinutes = 60) {
  if (!details.date || !details.time) return null;
  const duration = (durationMinutes || 60) * 60 * 1000;
  const startDate = new Date(`${details.date}T${details.time}`);

  for (let dayOffset = 0; dayOffset <= 3; dayOffset++) {
    const baseDate = new Date(startDate);
    baseDate.setDate(baseDate.getDate() + dayOffset);

    // Same day: start searching 30 min after the requested slot; subsequent days: start at 07:00
    let slotStart = new Date(baseDate);
    if (dayOffset === 0) {
      slotStart = new Date(startDate.getTime() + 30 * 60 * 1000);
    } else {
      slotStart.setHours(7, 0, 0, 0);
    }

    const dayLimit = new Date(baseDate);
    dayLimit.setHours(21, 0, 0, 0);

    while (new Date(slotStart.getTime() + duration) <= dayLimit) {
      const slotEnd = new Date(slotStart.getTime() + duration);
      const checkStart = slotStart;
      const checkEnd = slotEnd;
      const hasConflict = schedule.some(t => {
        if (!t.date || !t.time) return false;
        const tStart = t.datetime ? new Date(t.datetime) : new Date(`${t.date}T${t.time}`);
        const tEnd = t.deadline ? new Date(t.deadline) : new Date(tStart.getTime() + 60 * 60 * 1000);
        return checkStart < tEnd && checkEnd > tStart;
      });
      if (!hasConflict) {
        return {
          time: slotStart.toTimeString().slice(0, 5),
          date: toLocalDateStr(slotStart),
          dayOffset,
        };
      }
      slotStart = new Date(slotStart.getTime() + 30 * 60 * 1000);
    }
  }
  return null;
}

// ─── AI chat helper ───────────────────────────────────────────────────────────
// Routes all AI calls through the backend (key never exposed to the browser).
// The local rule-based parser in parseUserInput is the offline backup —
// it handles all structured commands (tasks, meetings, etc.) without any network.
// Returns the parsed backend payload, or null if the backend is unreachable.
async function callAideyAI(messages, options = {}) {
  try {
    const res = await api('/api/aidey', {
      method: 'POST',
      body: JSON.stringify({ messages, ...options }),
    });
    if (res && res.ok) {
      return res.json();
    }
    const errData = await res.json().catch(() => ({}));
    const errMsg = errData?.error || '';
    console.warn('[Aidey] Backend error', res?.status, errMsg);
    // Bubble up credit / quota errors so the user sees a real message
    if (/credit|balance|quota|billing|payment/i.test(errMsg)) {
      return { __creditsError: true };
    }
  } catch (err) {
    console.warn('[Aidey] Backend unreachable:', err?.message);
  }
  return null;
}

// chatAreas: this chat's Tasks / Calendar / Money toggles (C-b); the server refuses a switched-off
// area here too, so a card confirmed after its area was turned off does nothing.
//
// THE CARD'S PAYLOAD IS SERVER-HELD (2026-10-21): the server signed it when it showed the card
// (confirmToken) and runs exactly that. The client sends the token and the card's own edits (an
// edited body, the ticked suggestions), never the input, so nothing here can change a recipient.
async function confirmAideyTool(tool, confirmToken, chatAreas, edits) {
  const res = await api('/api/aidey/confirm', {
    method: 'POST',
    body: JSON.stringify({ tool, confirmToken, ...(edits ? { edits } : {}), ...(chatAreas ? { chatAreas } : {}) }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error || 'Failed to confirm action');
  }
  return res.json();
}

// Block 204: "YYYY-MM-DD" -> "today" / "tomorrow" / "Thu 4 Sep". The plan's dates are
// bare wall-clock day strings, so they are compared against the browser's local date
// rather than parsed as instants.
function planDayLabel(dateStr) {
  const key = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dateStr === key(today)) return 'today';
  if (dateStr === key(tomorrow)) return 'tomorrow';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

// Block 204: describe what the plan ACTUALLY did, from its own dates and times.
// This used to be a fixed sentence claiming "across the next 3 days" whatever the plan
// contained, so "move them to this afternoon" was reported as a three-day spread. Every
// number here is derived from the plan; nothing is assumed. Returns null when the plan
// carries no usable dates, so the caller can fall back rather than invent a shape.
function describeReschedulePlan(plan) {
  const items = (Array.isArray(plan) ? plan : []).filter((p) => p && p.newDate);
  if (!items.length) return null;

  const noun = `${items.length} overdue task${items.length === 1 ? '' : 's'}`;
  const days = [...new Set(items.map((p) => p.newDate))].sort();
  const times = items.map((p) => p.newTime).filter(Boolean).sort();

  const first = times[0];
  const last = times[times.length - 1];

  if (days.length === 1) {
    // One day: state the day and the span of times actually used.
    const when = planDayLabel(days[0]);
    if (!times.length) return `Done. I moved ${noun} to ${when}.`;
    if (first === last) return `Done. I moved ${noun} to ${when} at ${first}.`;
    return `Done. I moved ${noun} to ${when}, ${first} to ${last}.`;
  }

  // Several days: name the real range. "between X and Y" is a bound the data supports —
  // it does not claim every day runs those hours.
  const span = `${planDayLabel(days[0])} to ${planDayLabel(days[days.length - 1])}`;
  const dayCount = `${days.length} day${days.length === 1 ? '' : 's'}`;
  if (!times.length) return `Done. I moved ${noun} across ${dayCount}, ${span}.`;
  if (first === last) return `Done. I moved ${noun} across ${dayCount}, ${span}, at ${first}.`;
  return `Done. I moved ${noun} across ${dayCount}, ${span}, between ${first} and ${last}.`;
}

// Decision 75 split (Carla, 2026-10-22): an item other people are in (invitees with an address, a
// teammate on it, or someone else's) is never moved or cancelled without a card. The paths that act
// without the server (the offline parser, a clash's swap) ask this and leave such an item alone.
export function othersInItem(t, meId) {
  if (!t) return false;
  const invited = (Array.isArray(t.invitees) ? t.invitees : []).some((i) => i && i.email);
  const team = (Array.isArray(t.assignedTeam) ? t.assignedTeam : []).some((m) => { const id = typeof m === 'string' ? m : m?.userId; return id && id !== meId; });
  const theirs = !!(t.userId && meId && t.userId !== meId);
  return invited || team || theirs;
}

function formatConfirmedActionSummary(tool, result) {
  const payload = result?.result || result || {};
  if (tool === 'send_email') return 'Done, email sent.';
  // Email Phase 1 (2026-10-21): said from the server's answer, which only comes once it is done.
  if (tool === 'reply_email') return payload.to ? `Done, reply sent to ${payload.to}.` : 'Done, reply sent.';
  if (tool === 'delete_email') return payload.for_good ? 'Done, deleted for good.' : 'Done, moved to Trash.';
  if (tool === 'mark_email_junk') return 'Done, moved to Junk.';
  // Folders (2026-10-21): what went, and whether its emails went with it (from the server's answer).
  if (tool === 'delete_mail_folder') {
    const name = payload.deleted ? ` "${payload.deleted}"` : '';
    if (payload.emails_kept) return `Done, label${name} deleted. Its emails are still in your mailbox.`;
    const n = payload.emails_deleted;
    if (n == null) return `Done, folder${name} deleted with its emails.`;
    return n === 0 ? `Done, folder${name} deleted. It was empty.` : `Done, folder${name} deleted with its ${n} email${n === 1 ? '' : 's'}.`;
  }
  if (tool === 'delete_task') return 'Done, task deleted.';
  // Chat gaps (c): who was told, and anyone who could not be, from the server's answer.
  if (tool === 'cancel_meeting') {
    const told = Array.isArray(payload.told) && payload.told.length ? ` Told ${payload.told.join(', ')}.` : '';
    const missed = Array.isArray(payload.not_told) && payload.not_told.length
      ? ` Could not email ${payload.not_told.map((n) => `${n.email} (${n.error})`).join(', ')}.` : '';
    return `Done, meeting cancelled.${told}${missed}`;
  }
  if (tool === 'cancel_booking') return 'Done, booking cancelled. The client has been emailed.';
  // Chat gaps (d), from the server's answer.
  if (tool === 'send_invoice') return payload.number ? `Done, invoice ${payload.number} sent to ${payload.to}.` : 'Done, invoice sent.';
  if (tool === 'mark_invoice_paid') return payload.number ? `Done, invoice ${payload.number} marked paid.` : 'Done, invoice marked paid.';
  if (tool === 'create_email_rule') return payload.rule ? `Done, the rule "${payload.rule}" is on.` : 'Done, the rule is on.';
  if (tool === 'delete_email_rule') return 'Done, rule deleted.';
  if (tool === 'send_proposal' || tool === 'send_contract') return `Done, ${payload.kind || 'it'} sent${payload.to ? ` to ${payload.to}` : ''}.`;
  if (tool === 'edit_invoice') return `Done, ${payload.number} is changed.`;
  if (tool === 'cancel_invoice') return `Done, ${payload.number} is cancelled.`;
  if (tool === 'restore_invoice') return `Done, ${payload.number} is restored as sent.`;
  if (tool === 'delete_invoice') return payload.deleted ? `Done, the draft ${payload.deleted} is deleted.` : 'Done.';
  if (tool === 'delete_refund') return `Done, the refund is removed from ${payload.number}.`;
  if (tool === 'send_to_xero') return `Done, ${payload.number} is in Xero.`;
  if (tool === 'connected_accounts') return payload.disconnected ? `Done, ${payload.disconnected} is disconnected.` : 'Done.';
  if (tool === 'aidey_automations') return payload.automation ? `Done, "${payload.automation}" is changed.` : (payload.all_on ? 'Done, Aidey Intelligence is on.' : 'Done.');
  if (tool === 'money_in_out') return payload.deleted ? `Done, "${payload.deleted}" deleted.` : 'Done.';
  if (tool === 'resend_invite') return `Done, the invitation is sent again to ${payload.resent_to}.`;
  if (tool === 'backup_owner') return payload.backup_owner ? `Done, ${payload.backup_owner} is the backup owner.` : 'Done, there is no backup owner now.';
  if (tool === 'team_chat') return 'Done, posted in the team chat.';
  if (tool === 'edit_form') return `Done, "${payload.form}" is changed.`;
  if (tool === 'edit_workflow') return `Done, "${payload.workflow}" is changed.`;
  if (tool === 'template_stages') return payload.deleted ? `Done, "${payload.deleted}" deleted.` : 'Done.';
  if (tool === 'delete_form') return payload.deleted ? `Done, the form "${payload.deleted}" is deleted.` : 'Done.';
  if (tool === 'delete_workflow') return payload.deleted ? `Done, the workflow "${payload.deleted}" is deleted.` : 'Done.';
  if (tool === 'edit_workflow_steps') return payload.workflow ? `Done, "${payload.workflow}" runs its new steps.` : 'Done.';
  if (tool === 'board_templates') return payload.deleted ? `Done, the board template "${payload.deleted}" is deleted.` : 'Done.';
  if (tool === 'edit_proposal' || tool === 'edit_contract') return `Done, "${payload.proposal || payload.contract}" is changed. The client's link shows the new version.`;
  if (tool === 'delete_document') return payload.deleted ? `Done, the ${payload.kind} "${payload.deleted}" is deleted.` : 'Done.';
  if (tool === 'contract_templates') return payload.deleted ? `Done, the template "${payload.deleted}" is deleted.` : 'Done.';
  if (tool === 'delete_contact') return payload.deleted ? `Done, ${payload.deleted} is deleted.` : 'Done, contact deleted.';
  if (tool === 'delete_interaction') return payload.contact ? `Done, the ${payload.deleted} is deleted from ${payload.contact}'s history.` : 'Done.';
  if (tool === 'crm_boards' || tool === 'crm_stages') return payload.deleted ? `Done, "${payload.deleted}" deleted.` : 'Done.';
  if (tool === 'crm_board_contact') return payload.removed ? `Done, ${payload.removed} is off "${payload.board}".` : 'Done.';
  if (tool === 'delete_project') return payload.deleted ? `Done, the project "${payload.deleted}" is deleted.` : 'Done, project deleted.';
  if (['project_sprints', 'project_sections', 'project_stages', 'project_columns'].includes(tool)) return payload.deleted ? `Done, "${payload.deleted}" deleted.` : 'Done.';
  if (tool === 'apply_board_template') return payload.template ? `Done, ${payload.project} now uses "${payload.template}".` : 'Done.';
  if (tool === 'delete_task_occurrence') return payload.deleted ? `Done, "${payload.deleted}" on ${payload.date} is deleted. The other repeats stay.` : 'Done.';
  if (tool === 'change_task_occurrences') return payload.task ? `Done, "${payload.task}" is changed.` : 'Done.';
  if (tool === 'delete_booking') return 'Done, booking deleted. The client was not told.';
  if (tool === 'confirm_spam') return 'Done, it is in your mailbox\'s Spam.';
  if (tool === 'empty_deleted') return payload.deleted_for_good != null ? `Done, ${payload.deleted_for_good} email${payload.deleted_for_good === 1 ? '' : 's'} deleted for good.` : 'Done, the Trash is empty.';
  if (tool === 'edit_email_rule') return payload.rule ? `Done, the rule "${payload.rule}" is changed.` : 'Done, the rule is changed.';
  if (tool === 'aidey_folders') return payload.deleted ? `Done, the Aidey folder "${payload.deleted}" is deleted. Its emails are still in your mailbox.` : 'Done.';
  if (tool === 'auto_replies') return payload.sends_itself ? 'Done, Aidey now sends those replies by itself.' : 'Done, automatic replies changed.';
  if (tool === 'delete_purchase_order') return payload.deleted ? `Done, purchase order ${payload.deleted} deleted.` : 'Done, purchase order deleted.';
  if (tool === 'decide_overtime') return payload.for ? `Done, ${payload.hours} hour${payload.hours === 1 ? '' : 's'} of overtime ${payload.decision} for ${payload.for}.` : 'Done.';
  if (tool === 'approve_timesheet') return payload.for ? `Done, ${payload.for}'s week of ${payload.week_of} is approved.` : 'Done, the week is approved.';
  if (tool === 'remind_timesheet') return payload.for ? `Done, ${payload.for} has been reminded in Aidey.` : 'Done, reminder sent.';
  if (tool === 'record_leave_for_member') return payload.for ? `Done, ${payload.booked} booked for ${payload.for}. They have been told.` : 'Done, time off booked.';
  if (tool === 'cancel_leave') return payload.cancelled ? `Done, ${payload.cancelled} cancelled.${payload.days_back ? ` ${payload.days_back} day${payload.days_back === 1 ? '' : 's'} back on the allowance.` : ''}` : 'Done, leave cancelled.';
  if (tool === 'set_leave_allowance') return payload.for ? `Done, ${payload.for}'s allowance is now ${payload.allowance} days.` : 'Done, allowance changed.';
  if (tool === 'edit_note') return payload.title ? `Done, the writing in "${payload.title}" is replaced.` : 'Done, the note is replaced.';
  if (tool === 'delete_note') return payload.deleted ? `Done, "${payload.deleted}" deleted.` : 'Done, note deleted.';
  if (tool === 'delete_project_task') return payload.deleted ? `Done, "${payload.deleted}" deleted from ${payload.project}.` : 'Done, task deleted.';
  if (tool === 'project_notes') return payload.project ? `Done, the notes on ${payload.project} are replaced.` : 'Done, the notes are replaced.';
  if (tool === 'delete_expense') return payload.deleted ? `Done, "${payload.deleted}" deleted.` : 'Done, expense deleted.';
  if (tool === 'merge_contacts') return payload.kept ? `Done, "${payload.merged}" is merged into "${payload.kept}".` : 'Done, the contacts are merged.';
  if (tool === 'update_project_task' || tool === 'add_project_task') return `Done, "${payload.title || (payload.task && payload.task.title) || 'the task'}" is ${tool === 'add_project_task' ? 'added' : 'changed'}.`;
  if (tool === 'project_files' || tool === 'crm_columns') return payload.deleted ? `Done, "${payload.deleted}" deleted.` : 'Done.';
  if (tool === 'task_attachments') return payload.deleted ? `Done, "${payload.deleted}" deleted.` : 'Done.';
  if (tool === 'saved_email_templates') return payload.deleted ? `Done, "${payload.deleted}" deleted.` : 'Done.';
  if (tool === 'auto_reply_senders') return payload.sends_itself ? `Done, Aidey now replies to ${payload.sender} by itself.` : 'Done.';
  if (tool === 'email_templates') return payload.reset ? `Done, the "${payload.reset}" email is back to Aidey's wording.` : `Done, the "${payload.changed}" email is changed.`;
  if (tool === 'client_portal') return `Done, ${payload.client}'s portal is changed.`;
  if (tool === 'send_portal_link') return `Done, the sign-in link is sent to ${payload.sent_to}.`;
  if (tool === 'portal_messages') return `Done, your message is in ${payload.to}'s portal.`;
  if (tool === 'portal_files') return payload.deleted ? `Done, "${payload.deleted}" deleted.` : 'Done.';
  if (tool === 'booking_link') return `Done, your booking link is now /${payload.username}.`;
  if (tool === 'meeting_types' || tool === 'booking_categories') return payload.deleted ? `Done, "${payload.deleted}" deleted.` : 'Done.';
  if (tool === 'family_members') return payload.removed ? `Done, ${payload.removed} is removed from your family.` : 'Done.';
  if (tool === 'invite_family_member') return `Done, ${payload.invited} is invited.`;
  if (tool === 'family_login') return payload.paused ? `Done, ${payload.paused}'s sign-in is paused.` : 'Done.';
  if (tool === 'family_seats') return 'Done, an extra seat is added.';
  if (tool === 'rewards') return payload.deleted ? `Done, "${payload.deleted}" deleted.` : 'Done.';
  if (tool === 'family_polls') return payload.poll ? `Done, the family has been asked "${payload.poll}".` : payload.deleted ? `Done, the poll "${payload.deleted}" is deleted.` : 'Done.';
  if (tool === 'family_chat') return 'Done, posted in the family chat.';
  if (tool === 'shopping_lists') return payload.deleted ? `Done, "${payload.deleted}" deleted.` : 'Done.';
  if (tool === 'pantry') return payload.deleted ? `Done, ${payload.deleted} is out of the pantry.` : 'Done.';
  if (tool === 'journal_categories') return payload.deleted ? `Done, "${payload.deleted}" deleted. Its entries are kept.` : 'Done.';
  if (tool === 'share_note') return `Done, "${payload.note}" is shared with ${(payload.shared_with || []).join(' and ') || 'nobody'}.`;
  if (tool === 'note_categories') return payload.deleted ? `Done, the category "${payload.deleted}" is deleted. Its notes are kept.` : 'Done.';
  if (tool === 'edit_hr_details') return `Done, ${payload.for}'s HR record is changed.`;
  if (['onboarding_task', 'delete_hr_document', 'training_record'].includes(tool)) return payload.deleted ? `Done, "${payload.deleted}" deleted.` : 'Done.';
  if (tool === 'review') return payload.shared_with ? `Done, the review is shared with ${payload.shared_with}.` : 'Done.';
  if (tool === 'invite_member') return `Done, ${payload.invited} is invited.`;
  if (tool === 'change_member_role') return `Done, ${payload.for} is now ${payload.role}.`;
  if (tool === 'remove_member') return `Done, ${payload.removed} is removed from the team.`;
  if (tool === 'delete_time_entry') return 'Done, the time entry is deleted.';
  if (tool === 'delete_recurring_invoice') return `Done, "${payload.title}" is deleted. The invoices it made stay.`;
  if (tool === 'generate_recurring_now') return payload.status === 'draft' ? `Done, ${payload.invoice || 'the invoice'} is made as a draft.` : `Done, ${payload.invoice || 'the invoice'} is made and marked sent. It was not emailed: send it from Invoices.`;
  if (tool === 'set_up_recurring_invoice') return payload.title ? `Done, "${payload.title}" is set up.` : 'Done, the recurring invoice is set up.';
  if (tool === 'change_recurring_invoice') return payload.title ? `Done, "${payload.title}" is changed.` : 'Done, the recurring invoice is changed.';
  if (tool === 'stop_recurring_invoice') return `Done, ${payload.title ? `"${payload.title}"` : 'the recurring invoice'} is stopped. Nothing was deleted.`;
  if (tool === 'start_workflow') return payload.name ? `Done, "${payload.name}" has started.` : 'Done, the workflow has started.';
  if (tool === 'resume_workflow') return payload.name ? `Done, "${payload.name}" is on.` : 'Done, the workflow is on.';
  if (tool === 'send_form') return payload.title ? `Done, "${payload.title}" sent to ${payload.sent}.` : 'Done, questionnaire sent.';
  if (tool === 'delete_personal') return payload.deleted ? `Done, ${payload.deleted} deleted.` : 'Done, deleted.';
  if (tool === 'clear_shopping_ticked') return 'Done, the ticked items are cleared.';
  if (tool === 'move_contact_stage') return payload.moved_to ? `Done, moved to "${payload.moved_to}" on ${payload.board}.` : 'Done, moved.';
  if (tool === 'record_refund') return payload.number ? `Done, refund recorded on invoice ${payload.number}.` : 'Done, refund recorded.';
  if (tool === 'move_booking') return 'Done, booking moved. The client has been emailed the new time.';
  if (tool === 'block_sender') return payload.blocked ? `Done, ${payload.blocked} is blocked. Their email will go to Junk.` : 'Done, sender blocked.';
  // These said "Done." with no invoice named, so the record could not show what
  // had been created (device sitting, 2026-09-21).
  if (tool === 'create_invoice') {
    const inv = payload.invoice;
    return inv?.number ? `Done, draft invoice ${inv.number} created. It has not been sent to the client.` : 'Done, draft invoice created. It has not been sent.';
  }
  if (tool === 'approve_invoice') {
    return payload.number ? `Done, invoice ${payload.number} approved and emailed to the client.` : 'Done, invoice approved and emailed to the client.';
  }
  if (tool === 'reject_invoice') {
    return payload.number ? `Done, invoice ${payload.number} rejected. Nothing was sent to the client.` : 'Done, invoice rejected. Nothing was sent to the client.';
  }
  if (tool === 'send_invoice_chase') {
    const inv = payload.invoice;
    if (inv?.number) return `Done. Chase email sent for invoice ${inv.number}. I've added a 5-day follow-up reminder too.`;
    return 'Done. Invoice chase sent and follow-up reminder set.';
  }
  if (tool === 'reschedule_overdue_tasks_plan' && Array.isArray(payload.plan)) {
    // Fallback states the count only — never a day count the plan cannot support.
    return describeReschedulePlan(payload.plan)
      || `Done. I rescheduled ${payload.plan.length} overdue task${payload.plan.length === 1 ? '' : 's'}.`;
  }
  if (tool === 'send_overdue_task_nudges') {
    return `Done. Sent ${payload.nudgesSent || 0} friendly nudge${payload.nudgesSent === 1 ? '' : 's'} to your team.`;
  }
  if (tool === 'create_task') {
    let msg = payload.task?.title ? `Done, "${payload.task.title}" is added to your tasks.` : 'Task created.';
    if (payload.joinUrl) msg += ` Zoom link: ${payload.joinUrl}`;
    else if (payload.zoomLinkFailed) msg += ' (Zoom meeting could not be created, check your Zoom connection in Settings → Video meetings.)';
    return msg;
  }
  if (tool === 'update_task') {
    if (payload.delegated) return `Done, task delegated successfully.`;
    return payload.task?.title ? `Done, "${payload.task.title}" updated.` : 'Task updated.';
  }
  if (tool === 'log_expense') {
    const e = payload.expense;
    return e ? `Done, logged £${e.amount} for "${e.title}" under ${e.category}.` : 'Expense logged.';
  }
  if (tool === 'create_project') {
    const p = payload.project;
    return p ? `Done, project "${p.name}" created${p.client ? ` for ${p.client}` : ''}.` : 'Project created.';
  }
  if (tool === 'update_project_status') {
    const p = payload.project;
    return p ? `Done, "${p.name}" is now marked as ${p.status}.` : 'Project updated.';
  }
  if (tool === 'redistribute_workload') {
    const moved = payload.moved ?? 0;
    return moved === 0 ? 'No tasks were moved.' : `Done, moved ${moved} task${moved === 1 ? '' : 's'} to balance the team's workload.`;
  }
  return `Done.`;
}

function isLikelyStructuredCommand(input) {
  return /\b(create|add|schedule|book|move|reschedule|cancel|delete|send|email|invoice|note|contact|assign|delegate|task|meeting|overdue|capacity|availability|travel|follow[-\s]?up|nudge|transcrib|expense|log|project|status|summary|financial|revenue|budget|team|workload|complete|done|finish|mark)\b/i.test(input || '');
}

// Fire DOM events so context providers refresh their data without a page reload.
// ─────────────────────────────────────────────────────────────────────────────
// WHAT THE USER SEES WHEN A TOOL RAN AND THE MODEL SAID NOTHING.
// ─────────────────────────────────────────────────────────────────────────────
//
// This line used to read `aiReply || 'Done.'`. A tool that FAILED reaches the
// same branch as one that succeeded — the outcome list is appended when a tool
// is CALLED — and 102 handler paths can return ok:false. So an empty reply after
// a failed action told the user "Done."
//
// That was not the model confabulating. That was this component asserting
// success on its own account, which is the worse of the two: the model at least
// had the failed result in front of it.
//
// The replacement names what was attempted and says it did not work, which is
// the deliveryState shape from the booking emails, in chat.
const TOOL_ACTION = {
  send_email:               'send that email',
  send_invoice_chase:       'send that payment chase',
  send_overdue_task_nudges: 'send those nudges',
  create_invoice:           'create that invoice',
  approve_invoice:          'approve and send that invoice',
  reject_invoice:           'reject that invoice',
  approve_expense:          'approve that expense',
  reject_expense:           'reject that expense',
  create_task:              'create that',
  update_task:              'update that',
  reschedule_meeting:       'move that meeting',
  submit_leave_request:     'submit that leave request',
  action_leave_request:     'action that leave request',
  cancel_leave:             'cancel that leave',
};

function actionLabel(name) {
  // A tool with no entry still reads as English rather than as an identifier.
  return TOOL_ACTION[name] || `do that (${String(name || 'action').replace(/_/g, ' ')})`;
}

function fallbackForOutcomes(outcomes) {
  const list = Array.isArray(outcomes) ? outcomes : [];
  const failed = list.filter((o) => o && o.ok === false);

  // Nothing failed: "Done." is true, and is what it always said.
  if (failed.length === 0) return 'Done.';

  // The refusal reasons are written for a person in most handlers ("Portal not
  // found", "Invoicing is only available in a business workspace"), so the first
  // one is worth showing. It is the server's own words, not a stack trace.
  const reason = failed.find((f) => f.reason && f.reason.trim())?.reason?.trim();

  const what = failed.length === 1
    ? `I tried to ${actionLabel(failed[0].name)}, and it did not work.`
    : `I tried to ${failed.map((f) => actionLabel(f.name)).join(', and to ')}, and none of it worked.`;

  return reason
    ? `${what} ${reason.replace(/\s+$/, '').replace(/([^.!?])$/, '$1.')} Nothing has been sent or changed on your behalf, so it is safe to try again.`
    : `${what} Nothing has been sent or changed on your behalf, so it is safe to try again.`;
}

function fireToolEvents(toolNames) {
  if (!Array.isArray(toolNames) || !toolNames.length) return;
  const TASK_TOOLS = new Set(['create_task', 'update_task', 'reschedule_meeting', 'block_travel_time', 'reschedule_overdue_tasks_plan', 'send_invoice_chase']);
  if (toolNames.some(t => TASK_TOOLS.has(t))) window.dispatchEvent(new Event('aidey:tasksUpdated'));
  if (toolNames.includes('create_note')) window.dispatchEvent(new Event('aidey:notesUpdated'));
  if (toolNames.includes('add_contact')) window.dispatchEvent(new Event('aidey:contactsUpdated'));
  if (toolNames.includes('create_invoice')) window.dispatchEvent(new Event('aidey:invoicesUpdated'));
  if (toolNames.includes('log_expense')) window.dispatchEvent(new Event('aidey:expensesUpdated'));
  if (toolNames.some(t => ['create_project', 'update_project_status'].includes(t))) window.dispatchEvent(new Event('aidey:projectsUpdated'));
  if (toolNames.includes('open_email_inbox')) window.dispatchEvent(new Event('aidey:openEmailInbox'));
}

// ─── Approval Card ────────────────────────────────────────────────────────────
// Rendered inline in the chat message list for any action that requires user
// confirmation before the backend executes it.

const APPROVAL_CARD_META = {
  send_email: { icon: '📧', title: 'Send Email' },
  send_email_legacy: { icon: '📧', title: 'Send Email' },
  send_invoice_chase: { icon: '🧾', title: 'Send Invoice Chase' },
  reschedule_meeting: { icon: '📅', title: 'Reschedule Meeting' },
  reschedule_overdue_tasks_plan: { icon: '🗂️', title: 'Reschedule Overdue Tasks' },
  send_overdue_task_nudges: { icon: '📣', title: 'Send Team Nudges' },
  create_task: { icon: '✅', title: 'Create Task' },
  create_invoice: { icon: '📄', title: 'Create Invoice' },
  update_task: { icon: '✏️', title: 'Update Task' },
  confirm_event_proposal: { icon: '📅', title: 'Move Meeting' },
  reply_email: { icon: '↩️', title: 'Reply' },
  delete_email: { icon: '🗑️', title: 'Delete Email' },
  mark_email_junk: { icon: '🚫', title: 'Move to Junk' },
  delete_mail_folder: { icon: '🗂️', title: 'Delete Folder' },
  block_sender: { icon: '⛔', title: 'Block Sender' },
  delete_task: { icon: '🗑️', title: 'Delete Task' },
  cancel_meeting: { icon: '📅', title: 'Cancel Meeting' },
  cancel_booking: { icon: '📅', title: 'Cancel Booking' },
  move_booking: { icon: '📅', title: 'Move Booking' },
  send_invoice: { icon: '🧾', title: 'Send Invoice' },
  mark_invoice_paid: { icon: '🧾', title: 'Mark Paid' },
  record_refund: { icon: '🧾', title: 'Record Refund' },
  move_contact_stage: { icon: '👤', title: 'Move Stage' },
  delete_personal: { icon: '🗑️', title: 'Delete' },
  send_proposal: { icon: '📄', title: 'Send Proposal' },
  create_email_rule: { icon: '📬', title: 'New Rule' },
  delete_email_rule: { icon: '📬', title: 'Delete Rule' },
  send_contract: { icon: '📄', title: 'Send Contract' },
  send_form: { icon: '📝', title: 'Send Questionnaire' },
  start_workflow: { icon: '⚙️', title: 'Start Workflow' },
  resume_workflow: { icon: '⚙️', title: 'Switch Workflow On' },
  set_up_recurring_invoice: { icon: '🔁', title: 'Set Up Recurring Invoice' },
  merge_contacts: { icon: '👥', title: 'Merge Contacts' },
  delete_expense: { icon: '🧾', title: 'Delete Expense' },
  delete_project_task: { icon: '📁', title: 'Delete Project Task' },
  project_notes: { icon: '📁', title: 'Replace Project Notes' },
  edit_note: { icon: '📝', title: 'Replace Note' },
  delete_note: { icon: '📝', title: 'Delete Note' },
  decide_overtime: { icon: '⏱️', title: 'Overtime' },
  approve_timesheet: { icon: '⏱️', title: 'Approve Timesheet' },
  remind_timesheet: { icon: '⏱️', title: 'Timesheet Reminder' },
  record_leave_for_member: { icon: '🌴', title: 'Book Time Off' },
  set_leave_allowance: { icon: '🌴', title: 'Holiday Allowance' },
  delete_purchase_order: { icon: '🧾', title: 'Delete Purchase Order' },
  empty_deleted: { icon: '🗑️', title: 'Empty Trash' },
  confirm_spam: { icon: '🚫', title: 'Mark as Spam' },
  delete_booking: { icon: '📅', title: 'Delete Booking' },
  delete_task_occurrence: { icon: '🗓️', title: 'Delete One Occurrence' },
  delete_project: { icon: '📁', title: 'Delete Project' },
  delete_contact: { icon: '👤', title: 'Delete Contact' },
  edit_proposal: { icon: '📄', title: 'Change Sent Proposal' },
  delete_form: { icon: '📝', title: 'Delete Form' },
  edit_form: { icon: '📝', title: 'Remove Form Question' },
  resend_invite: { icon: '👥', title: 'Resend Invitation' },
  connected_accounts: { icon: '🔌', title: 'Disconnect Account' },
  aidey_automations: { icon: '✨', title: 'Aidey Automations' },
  money_in_out: { icon: '🧾', title: 'Delete Money Entry' },
  backup_owner: { icon: '👥', title: 'Backup Owner' },
  team_chat: { icon: '💬', title: 'Post in Team Chat' },
  edit_workflow: { icon: '⚙️', title: 'Change Workflow' },
  template_stages: { icon: '📁', title: 'Delete Template Stage' },
  edit_invoice: { icon: '🧾', title: 'Change Sent Invoice' },
  cancel_invoice: { icon: '🧾', title: 'Cancel Invoice' },
  restore_invoice: { icon: '🧾', title: 'Restore Invoice' },
  delete_invoice: { icon: '🧾', title: 'Delete Draft Invoice' },
  delete_refund: { icon: '🧾', title: 'Remove Refund' },
  send_to_xero: { icon: '🧾', title: 'Send to Xero' },
  delete_workflow: { icon: '⚙️', title: 'Delete Workflow' },
  edit_workflow_steps: { icon: '⚙️', title: 'Change Workflow Steps' },
  board_templates: { icon: '📁', title: 'Delete Board Template' },
  edit_contract: { icon: '📄', title: 'Change Sent Contract' },
  delete_document: { icon: '📄', title: 'Delete Document' },
  contract_templates: { icon: '📄', title: 'Delete Template' },
  delete_interaction: { icon: '👤', title: 'Delete History Entry' },
  crm_boards: { icon: '🎯', title: 'Delete Board' },
  crm_stages: { icon: '🎯', title: 'Delete Stage' },
  crm_board_contact: { icon: '🎯', title: 'Take Off Board' },
  project_sprints: { icon: '📁', title: 'Delete Sprint' },
  project_sections: { icon: '📁', title: 'Delete Section' },
  project_stages: { icon: '📁', title: 'Delete Stage' },
  project_columns: { icon: '📁', title: 'Delete Column' },
  apply_board_template: { icon: '📁', title: 'Apply Board Template' },
  change_task_occurrences: { icon: '🗓️', title: 'Change Repeating Task' },
  edit_email_rule: { icon: '📬', title: 'Change Rule' },
  aidey_folders: { icon: '📁', title: 'Delete Aidey Folder' },
  auto_replies: { icon: '📬', title: 'Automatic Replies' },
  change_recurring_invoice: { icon: '🔁', title: 'Change Recurring Invoice' },
  stop_recurring_invoice: { icon: '🔁', title: 'Stop Recurring Invoice' },
  delete_recurring_invoice: { icon: '🔁', title: 'Delete Recurring Invoice' },
  delete_time_entry: { icon: '⏱️', title: 'Delete Time Entry' },
  edit_hr_details: { icon: '👥', title: 'Change Pay' },
  share_note: { icon: '📝', title: 'Share Note' },
  shopping_lists: { icon: '🛒', title: 'Delete Shopping List' },
  family_members: { icon: '🏡', title: 'Remove Family Member' },
  booking_link: { icon: '📅', title: 'Change Booking Link' },
  client_portal: { icon: '🔓', title: 'Switch Off Portal' },
  email_templates: { icon: '✉️', title: 'Client Email' },
  saved_email_templates: { icon: '✉️', title: 'Delete Saved Email' },
  task_attachments: { icon: '📎', title: 'Delete Attachment' },
  update_project_task: { icon: '📁', title: 'Give Board Task' },
  add_project_task: { icon: '📁', title: 'Give Board Task' },
  project_files: { icon: '📁', title: 'Delete Project File' },
  crm_columns: { icon: '🎯', title: 'Delete CRM Column' },
  auto_reply_senders: { icon: '📬', title: 'Automatic Replies for a Sender' },
  send_portal_link: { icon: '🔓', title: 'Send Portal Link' },
  portal_messages: { icon: '🔓', title: 'Send Portal Message' },
  portal_files: { icon: '🔓', title: 'Delete Portal File' },
  meeting_types: { icon: '📅', title: 'Delete Meeting Type' },
  booking_categories: { icon: '📅', title: 'Delete Booking Category' },
  invite_family_member: { icon: '🏡', title: 'Invite Family Member' },
  family_login: { icon: '🏡', title: 'Pause Sign-in' },
  family_seats: { icon: '🏡', title: 'Buy Extra Seat' },
  rewards: { icon: '⭐', title: 'Delete Reward' },
  family_polls: { icon: '🗳️', title: 'Family Poll' },
  family_chat: { icon: '💬', title: 'Post in Family Chat' },
  pantry: { icon: '🥫', title: 'Take Out of Pantry' },
  journal_categories: { icon: '📔', title: 'Delete Journal Category' },
  note_categories: { icon: '📝', title: 'Delete Category' },
  onboarding_task: { icon: '👥', title: 'Delete Onboarding Task' },
  delete_hr_document: { icon: '👥', title: 'Delete HR Document' },
  review: { icon: '👥', title: 'Share Review' },
  training_record: { icon: '👥', title: 'Delete Training Record' },
  invite_member: { icon: '👥', title: 'Invite to Team' },
  change_member_role: { icon: '👥', title: 'Change Role' },
  remove_member: { icon: '👥', title: 'Remove from Team' },
  generate_recurring_now: { icon: '🔁', title: 'Make Invoice Now' },
  clear_shopping_ticked: { icon: '🛒', title: 'Clear Shopping' },
  approve_expense: { icon: '🧾', title: 'Approve Expense' },
  reject_expense: { icon: '🧾', title: 'Reject Expense' },
  submit_leave_request: { icon: '🌴', title: 'Request Leave' },
  action_leave_request: { icon: '🌴', title: 'Leave Request' },
  cancel_leave: { icon: '🌴', title: 'Cancel Leave' },
  add_contact: { icon: '👤', title: 'Add Contact' },
  block_travel_time: { icon: '🚗', title: 'Block Travel Time' },
  log_expense: { icon: '🧾', title: 'Log Expense' },
  create_project: { icon: '📁', title: 'Create Project' },
  update_project_status: { icon: '📁', title: 'Update Project' },
};

export function ApprovalCard({ approvalData, onConfirm, onCancel }) {   // exported for its test
  const [editMode, setEditMode] = React.useState(false);
  const [editedBody, setEditedBody] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const editRef = useRevealForm(editMode);   // Edit: the draft in view, the cursor in it (2026-10-21)

  const { tool, input = {}, confirmationPrompt } = approvalData;
  // A card with no tool used to crash here (tool.replace on undefined): draft_email's old shape.
  const meta = APPROVAL_CARD_META[tool] || { icon: '⚡', title: String(tool || 'Action').replace(/_/g, ' ') };

  // Build summary rows per tool
  const summaryRows = [];
  if (tool === 'send_email' || tool === 'send_email_legacy' || tool === 'reply_email' || tool === 'send_proposal' || tool === 'send_contract') {
    if (input.to) summaryRows.push(['To', input.to]);
    if (input.subject) summaryRows.push(['Subject', input.subject]);
  } else if (tool === 'send_invoice_chase') {
    if (input.invoice_number) summaryRows.push(['Invoice', input.invoice_number]);
    if (input.invoice_contact_name) summaryRows.push(['Client', input.invoice_contact_name]);
    if (input.draft_subject) summaryRows.push(['Subject', input.draft_subject]);
  } else if (tool === 'reschedule_meeting') {
    if (input.title_match) summaryRows.push(['Meeting', input.title_match]);
    if (input.new_datetime) {
      const dt = new Date(input.new_datetime);
      if (!isNaN(dt.getTime())) {
        summaryRows.push(['New time', formatTaskWhen(dt)]);
      }
    }
  } else if (tool === 'create_task') {
    if (input.title) summaryRows.push(['Task', input.title]);
    if (input.due_date || input.date) summaryRows.push(['Due', input.due_date || input.date]);
    if (input.priority) summaryRows.push(['Priority', input.priority]);
  } else if (tool === 'create_invoice') {
    if (input.client_name || input.contactName) summaryRows.push(['Client', input.client_name || input.contactName]);
    if (input.amount) summaryRows.push(['Amount', `£${input.amount}`]);
    if (input.due_date || input.dueDate) summaryRows.push(['Due', input.due_date || input.dueDate]);
  } else if (tool === 'update_task') {
    if (input.title_match || input.title) summaryRows.push(['Task', input.title_match || input.title]);
    if (input.due_date || input.date) summaryRows.push(['New Date', input.due_date || input.date]);
  }

  const hasDraftBody = ['send_email', 'send_email_legacy', 'send_invoice_chase', 'reply_email', 'send_proposal'].includes(tool);
  const draftBody = input.draft_body || input.body || '';

  async function handleConfirmClick() {
    if (busy) return;
    setBusy(true);
    await onConfirm(editMode ? editedBody : undefined);
    // onConfirm replaces this card with a result message; no need to reset busy
  }

  function handleEditStart() {
    setEditedBody(draftBody);
    setEditMode(true);
  }

  // Strip the "Reply yes to..." tail from confirmationPrompt for the preview pane
  const planPreview = !hasDraftBody && confirmationPrompt
    ? confirmationPrompt.replace(/\n?Reply [""]?yes[""]? to[\s\S]*$/i, '').trim()
    : '';

  return (
    <div className="achat-act">
      <span className="achat-act__stripe" style={{ '--ui-bg': v('highlight') }} aria-hidden="true" />
      <div className="achat-act__body">
        <div className="achat-act__head">
          <IconTile icon={meta.icon} color="pastel-yellow" />
          <p className="achat-act__title">{meta.title}</p>
          <StatusPill tone="pending">Action required</StatusPill>
        </div>

        {/* Summary rows */}
        {summaryRows.length > 0 && (
          <dl className="achat-act__rows">
            {summaryRows.map(([label, value]) => (
              <React.Fragment key={label}><dt>{label}</dt><dd>{value}</dd></React.Fragment>
            ))}
          </dl>
        )}

        {/* Chat gaps (m): both records and the result, side by side (merge_contacts). */}
        {approvalData.compare && Array.isArray(approvalData.compare.rows) && (
          <>
            <table className="achat-act__compare" aria-label="The two records and the result">
              <thead><tr><th scope="col" />{(approvalData.compare.columns || []).map((c) => <th key={c} scope="col">{c}</th>)}</tr></thead>
              <tbody>
                {approvalData.compare.rows.map(([field, ...vals]) => (
                  <tr key={field}><th scope="row">{field}</th>{vals.map((val, i) => <td key={i}>{val || '—'}</td>)}</tr>
                ))}
              </tbody>
            </table>
            {approvalData.compare.moves && <p className="achat-act__note">{approvalData.compare.moves}</p>}
          </>
        )}

        {/* Plan preview (for reschedule plans, nudge lists, etc.) */}
        {planPreview && <div className="achat-act__preview">{planPreview}</div>}

        {/* Draft body preview / inline edit area */}
        {hasDraftBody && (editMode ? (
          <Textarea ref={editRef} className="achat-act__edit" aria-label="Edit the draft" value={editedBody} onChange={e => setEditedBody(e.target.value)} />
        ) : (
          <div className="achat-act__preview">{draftBody || '(no preview available)'}</div>
        ))}

        {/* Action buttons */}
        <div className="achat-act__actions">
          <Button variant="confirm" size="sm" disabled={busy} aria-busy={busy || undefined} onClick={handleConfirmClick}>
            {editMode ? 'Send edited version' : (busy ? 'Working…' : 'Confirm')}
          </Button>
          {!editMode && hasDraftBody && (
            <Button variant="secondary" size="sm" onClick={handleEditStart}>Edit</Button>
          )}
          {editMode && (
            <Button variant="tertiary" size="sm" onClick={() => setEditMode(false)}>Back</Button>
          )}
          <Button variant="tertiary" size="sm" className="achat-act__end" onClick={onCancel} disabled={busy}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}

export function EmailDraftCard({ draft, onSend, onCancel }) {   // exported for its test
  const [editMode, setEditMode] = React.useState(false);
  const [editedBody, setEditedBody] = React.useState(draft.body || '');
  const editRef = useRevealForm(editMode);
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);

  async function handleSend() {
    if (busy || done) return;
    setBusy(true);
    await onSend(editMode ? editedBody : draft.body);
    setDone(true);
  }

  return (
    <div className="achat-act">
      <span className="achat-act__stripe" style={{ '--ui-bg': v('pastel-blue') }} aria-hidden="true" />
      <div className="achat-act__body">
        <div className="achat-act__head">
          <IconTile icon="📧" color="pastel-blue" />
          <p className="achat-act__title">Draft email</p>
          <StatusPill tone="neutral">Review before sending</StatusPill>
        </div>

        {(draft.to || draft.subject) && (
          <dl className="achat-act__rows">
            {draft.to && <><dt>To</dt><dd>{draft.to}</dd></>}
            {draft.subject && <><dt>Subject</dt><dd>{draft.subject}</dd></>}
          </dl>
        )}

        {editMode ? (
          <Textarea ref={editRef} className="achat-act__edit" aria-label="Edit the email" value={editedBody} onChange={e => setEditedBody(e.target.value)} />
        ) : (
          <div className="achat-act__preview">{draft.body || '(no body)'}</div>
        )}

        <div className="achat-act__actions">
          <Button variant="confirm" size="sm" disabled={busy || done} aria-busy={busy || undefined} onClick={handleSend}>
            {busy ? 'Sending…' : done ? 'Sent' : (editMode ? 'Send edited version' : 'Send')}
          </Button>
          {!editMode && !done && (
            <Button variant="secondary" size="sm" onClick={() => { setEditMode(true); setEditedBody(draft.body || ''); }}>Edit</Button>
          )}
          {editMode && (
            <Button variant="tertiary" size="sm" onClick={() => setEditMode(false)}>Back</Button>
          )}
          {!done && (
            <Button variant="tertiary" size="sm" className="achat-act__end" onClick={onCancel} disabled={busy}>Cancel</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function WorkloadRedistributionCard({ suggestions, onConfirm, onCancel }) {
  const [checked, setChecked] = React.useState(() => new Set(suggestions.map((_, i) => i)));
  const [busy, setBusy] = React.useState(false);

  const allSelected = checked.size === suggestions.length;
  const toggle = (i) => setChecked(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });
  const toggleAll = () => setChecked(allSelected ? new Set() : new Set(suggestions.map((_, i) => i)));

  async function handleConfirm() {
    const approved = suggestions.filter((_, i) => checked.has(i));
    if (approved.length === 0) return;
    setBusy(true);
    await onConfirm(approved);
  }

  return (
    <div className="achat-act">
      <span className="achat-act__stripe" style={{ '--ui-bg': v('pastel-periwinkle') }} aria-hidden="true" />
      <div className="achat-act__body">
        <div>
          <p className="achat-act__title">Workload redistribution suggestions</p>
          <p className="achat-act__sub">Review and approve which tasks to move</p>
        </div>
        <button type="button" className="achat-act__link" onClick={toggleAll}>
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
        <div className="achat-pick">
          {suggestions.map((s, i) => (
            <label key={i} className={`achat-pick__item${checked.has(i) ? ' is-on' : ''}`}>
              <input type="checkbox" className="achat-check" checked={checked.has(i)} onChange={() => toggle(i)} />
              <span className="achat-pick__main">
                <span className="achat-pick__title">{s.taskTitle}</span>
                <span className="achat-pick__meta">
                  <strong>{s.fromMember}</strong> ({s.fromCount} tasks) → <strong>{s.toMember}</strong> ({s.toCount} tasks)
                  {s.dueDate && <> · due {new Date(s.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</>}
                </span>
              </span>
            </label>
          ))}
        </div>
        <div className="achat-act__actions">
          <Button variant="confirm" size="sm" onClick={handleConfirm} disabled={busy || checked.size === 0} aria-busy={busy || undefined}>
            {busy ? 'Moving…' : `Move ${checked.size} task${checked.size === 1 ? '' : 's'}`}
          </Button>
          <Button variant="tertiary" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}

function SessionResumeCard({ sessionTitle, sessionTime, onContinue, onFresh }) {
  return (
    <div className="achat-act">
      <span className="achat-act__stripe" style={{ '--ui-bg': v('pastel-mint') }} aria-hidden="true" />
      <div className="achat-act__body">
        <p className="achat-act__title">Welcome back!</p>
        <p className="achat-act__text">
          Last time we were working on: <strong>{sessionTitle}</strong>
          {sessionTime && <span> ({sessionTime})</span>}.
          {' '}Would you like to carry on, or start something new?
        </p>
        <div className="achat-act__actions">
          <Button variant="confirm" size="sm" onClick={onContinue}>Carry on</Button>
          <Button variant="tertiary" size="sm" onClick={onFresh}>Start fresh</Button>
        </div>
      </div>
    </div>
  );
}

function AbsenceDelegationCard({ absenceData, leaveRequestId, onConfirm, onCancel }) {
  const { absentMember, leaveStart, leaveEnd, atRiskTasks } = absenceData || {};
  const [selections, setSelections] = React.useState(() => {
    const init = {};
    (atRiskTasks || []).forEach((t, i) => {
      init[i] = t.suggestedAssignees?.[0]?.id || '';
    });
    return init;
  });
  const [checked, setChecked] = React.useState(() => new Set((atRiskTasks || []).map((_, i) => i)));
  const [busy, setBusy] = React.useState(false);

  const toggle = (i) => setChecked(prev => {
    const next = new Set(prev); next.has(i) ? next.delete(i) : next.add(i); return next;
  });

  const fmtDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  async function handleConfirm() {
    if (checked.size === 0) return;
    setBusy(true);
    const delegations = [];
    (atRiskTasks || []).forEach((t, i) => {
      if (checked.has(i) && selections[i]) {
        delegations.push({ taskId: t.id, assigneeId: selections[i] });
      }
    });
    if (!delegations.length) { setBusy(false); return; }
    await onConfirm({ leaveRequestId, delegations });
  }

  const leaveRange = leaveStart && leaveEnd ? ` (${fmtDate(leaveStart)} – ${fmtDate(leaveEnd)})` : '';

  return (
    <div className="achat-act">
      <span className="achat-act__stripe" style={{ '--ui-bg': v('pastel-lilac') }} aria-hidden="true" />
      <div className="achat-act__body">
        <div>
          <p className="achat-act__title">Task coverage, {absentMember?.name} on leave{leaveRange}</p>
          <p className="achat-act__sub">Select tasks to delegate and choose an assignee for each</p>
        </div>
        <div className="achat-pick">
          {(atRiskTasks || []).map((t, i) => (
            <label key={i} className={`achat-pick__item${checked.has(i) ? ' is-on' : ''}`}>
              <input type="checkbox" className="achat-check" checked={checked.has(i)} onChange={() => toggle(i)} />
              <span className="achat-pick__main">
                <span className="achat-pick__title">
                  {t.title}
                  {t.dueDate && <span className="achat-pick__meta"> · due {fmtDate(t.dueDate)}</span>}
                  {t.priority && t.priority !== 'medium' && <span className="achat-pick__flag">{t.priority}</span>}
                </span>
                {t.projectName && <span className="achat-pick__meta">{t.projectName}</span>}
                {checked.has(i) && t.suggestedAssignees?.length > 0 && (
                  <Select
                    aria-label={`Assign ${t.title} to`}
                    value={selections[i] || ''}
                    onChange={e => setSelections(prev => ({ ...prev, [i]: e.target.value }))}
                    onClick={e => e.stopPropagation()}
                    placeholder="Assign to…"
                    options={t.suggestedAssignees.map(a => ({ value: a.id, label: `${a.name} (${a.currentTaskCount} task${a.currentTaskCount === 1 ? '' : 's'})` }))}
                  />
                )}
              </span>
            </label>
          ))}
        </div>
        <div className="achat-act__actions">
          <Button variant="confirm" size="sm" onClick={handleConfirm} disabled={busy || checked.size === 0} aria-busy={busy || undefined}>
            {busy ? 'Delegating…' : `Delegate ${checked.size} task${checked.size === 1 ? '' : 's'}`}
          </Button>
          <Button variant="tertiary" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}

// Decision 78 / fresh-eyes item 26: an adult reads "How can I help?", on the page (under the "Good
// morning" hero) and in the popup, where this message is the first bubble. A child keeps their own.
function aideyGreeting(isChild) {
  return isChild ? "Hi, I'm Aidey! How can I help?" : 'How can I help?';
}

// The opening greeting for this person: the child version for a child ACCOUNT. Exported for
// ChatChildGreeting.test.js.
export function chatGreetingFor(currentUser) {
  return aideyGreeting(currentUser?.isChildAccount === true);
}

function IntegrationPrompt({ integrationName, onNavigate }) {
  const MSGS = {
    google: "To create Google Meet links I need access to your Google account. Connect it under Settings → Video meetings, and it only takes a moment.",
    zoom: "To create Zoom links I need access to your Zoom account. Connect it under Settings → Video meetings, and it only takes a moment.",
    microsoft: "To create Microsoft Teams links I need access to your Microsoft account. Connect it under Settings → Video meetings, and it only takes a moment.",
  };
  const message = MSGS[integrationName] || `To use this feature, connect your ${integrationName} account under Settings → Video meetings.`;
  return (
    <div className="achat-act">
      <span className="achat-act__stripe" style={{ '--ui-bg': v('pastel-blue') }} aria-hidden="true" />
      <div className="achat-act__body">
        <p className="achat-act__text">{message}</p>
        <div className="achat-act__actions">
          <Button variant="secondary" size="sm" onClick={onNavigate}>Go to Video meetings</Button>
        </div>
      </div>
    </div>
  );
}

/**
 * === Main Chat Component ===
 */
// Chooser shown when the user asks to turn a meeting into tasks: pull an existing
// transcript (MeetingTranscripts) vs record live / paste one now (MeetingRecorder).
function MeetingChoiceModal({ onTranscript, onRecord, onCancel }) {
  // On the kit's Modal: focus moves in and back, Escape and the backdrop close it.
  return (
    <Modal open onClose={onCancel} title="Turn a meeting into tasks" color="highlight" width={440}
      footer={<Button variant="tertiary" onClick={onCancel}>Cancel</Button>}>
      <div className="achat-choice__opts">
        <button type="button" onClick={onTranscript} className="achat-choice__opt">
          <strong>📄 I have a transcript</strong>
          <span>Pull recent Zoom, Google Meet &amp; Teams recordings</span>
        </button>
        <button type="button" onClick={onRecord} className="achat-choice__opt">
          <strong>🎙️ Record or paste live</strong>
          <span>Listen in person, or paste any transcript</span>
        </button>
      </div>
    </Modal>
  );
}

// The area a local (offline parser) action touches, so a switched-off toggle stops it too (C-b).
function localActionAreas(details) {
  const a = details && details.action;
  if (a === 'create-invoice') return ['money'];
  if (a === 'find-slots' || a === 'send-booking-link' || a === 'add-attendee' || a === 'travel') return ['calendar'];
  if (a === 'agenda') return ['tasks', 'calendar'];
  if (a === 'move' || a === 'cancel') return ['tasks'];
  if (a && a !== 'create') return [];
  // Not a task at all: it goes to the model as a question, which the server's toggles govern.
  if (!(details && (details.title || details.taskType || details.date || details.time))) return [];
  const meeting = /meeting|call|appointment/i.test(details?.taskType || '');
  return meeting ? ['tasks', 'calendar'] : ['tasks'];
}

export default function SuperAideyChat({
  onCreateTask,
  onMoveTask = () => { },
  onCancelTask = () => { },
  userSchedule = [],
  googleEvents = [],
  icalEvents = [],
  outlookEvents = [],
  onClose,
  isPopup = false,
  scrollable = false,
  fullPage = false,
  sessionId: sessionIdProp = null,
  onSessionUpdated = null,
  fromHistory = false,   // the page opened this chat from its history (load it if it is not here)
  setFilter = null,
  onCreateMeetingTask = null,
  proactiveMessage = null,
  onProactiveHandled = null,
  notificationProactive = null,
  onNotificationProactiveHandled = null,
  clashContext = null,
  onClashContextHandled = null,
  initialInput = "",
  notifContext = null,
  onOpenDocumentImporter = null,
  closeRef = null,
  projectId: projectIdProp = null,
  sessionRef = null,   // Block C: the popup's Expand opens the full page on this session
}) {
  const { currentUser, activeWorkspaceId, workspaces } = useCurrentUser();
  const { members: rosterMembers } = useWorkspaceRoster();
  const rosterMap = useWorkspaceRosterMap();
  // Only account-holders are @mentionable / assignable from chat — a mention
  // notifies by userId, which accountless household members don't have.
  const mentionableMembers = useMemo(
    () => rosterMembers.filter(m => m.userId),
    [rosterMembers]
  );
  const activeWorkspace = (workspaces || []).find(w => w.id === activeWorkspaceId) || null;
  const isPersonalWorkspace = activeWorkspace?.type === 'personal';
  // A child is a USER flag (isChildAccount), not a workspace type: the backend never creates a
  // 'child' workspace, so the old `activeWorkspace?.type === 'child'` was always false and every
  // child got the adult greeting. Same flag App.js, Budget, Settings and Family use.
  const isChildAccount = currentUser?.isChildAccount === true;
  const { contacts } = useContacts();
  const { invoices, refresh: refreshInvoices, loaded: invoicesLoaded, loadFailed: invoicesLoadFailed } = useInvoices();
  // Stable session ID for this mount — new popup = new ID = fresh chat
  const sessionIdRef = useRef(sessionIdProp || newSessionId());

  // A chat chosen from the history that has no copy in this browser is loaded from the server.
  const [loadingSession, setLoadingSession] = useState(() => !!(sessionIdProp && fromHistory && !loadSession(sessionIdProp)?.messages?.length));
  const [sessionLoadFailed, setSessionLoadFailed] = useState(false);
  // "Draft created" cards (create_invoice, Carla 2026-10-21): a refused Undo's reason, by card.
  const [draftCardError, setDraftCardError] = useState({});
  const [chatMessages, setChatMessages] = useState(() => {
    if (sessionIdProp) {
      const session = loadSession(sessionIdProp);
      if (session?.messages?.length) return session.messages;
    }
    return [{ role: "assistant", content: chatGreetingFor(currentUser), ts: Date.now(), type: 'greeting' }];
  });
  // C-b: this chat's toggles, read from its own messages (the last hidden chat_settings message).
  // Decision 84 (replaces the toggle part of 77): no Tasks / Calendar / Money pills. Aidey picks the
  // tools itself, and every area is ALWAYS on, including in an older chat whose stored settings
  // turned one off (with no pill left, that chat could never turn it back on). What a person may
  // see or do is still decided on the server, exactly as before.
  const areas = ALL_AREAS_ON;
  const areasRef = useRef(ALL_AREAS_ON);
  function askAidey(messages, options = {}) {
    return callAideyAI(messages, { ...options, chatAreas: areasRef.current });
  }
  // The areas a turn used go on that turn's own message from the person (never replaced, unlike
  // a card), so the chat's history tag survives the card being confirmed or cancelled.
  function recordTurnAreas(used) {
    if (!Array.isArray(used) || !used.length) return;
    setChatMessages(prev => {
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        if (prev[i].role === 'user' && !prev[i].synthetic) {
          const merged = [...new Set([...(prev[i].areas || []), ...used])];
          const next = prev.slice();
          next[i] = { ...prev[i], areas: merged };
          return next;
        }
      }
      return prev;
    });
  }
  const [chatInput, setChatInput] = useState(initialInput);
  const [pendingTask, setPendingTask] = useState(null);
  const [pendingField, setPendingField] = useState(null);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [pendingToolConfirmation, setPendingToolConfirmation] = useState(null);
  const chatScrollRef = useRef(null);

  // Correct the greeting if workspace type becomes known after initial render
  useEffect(() => {
    setChatMessages(prev => {
      if (prev.length !== 1) return prev;
      const first = prev[0];
      if (first.role !== 'assistant' || first.type !== 'greeting') return prev;
      const correct = chatGreetingFor(currentUser);
      if (first.content === correct) return prev;
      return [{ ...first, content: correct }];
    });
  }, [isChildAccount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch a purposeful opener when chat is triggered from a notification
  useEffect(() => {
    if (!notifContext) return;
    let cancelled = false;
    api('/api/aidey/notification-opener', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notificationId: notifContext.notificationId, type: notifContext.type, data: notifContext.data || notifContext }),
    }).then(data => {
      if (!cancelled && data?.opener) {
        setChatMessages([{ role: 'assistant', content: data.opener, ts: Date.now() }]);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [notifContext]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Voice input state ─────────────────────────────────────────────────────
  const [voiceConfirmation, setVoiceConfirmation] = useState(null); // { intent, summary, data }
  const wasVoiceInputRef = useRef(false);
  const [isMeetingTranscribing, setIsMeetingTranscribing] = useState(false);
  const [liveMeetingTranscript, setLiveMeetingTranscript] = useState('');
  const meetingRecognitionRef = useRef(null);
  const isMeetingTranscribingRef = useRef(false);
  const [pendingTranscribePrompt, setPendingTranscribePrompt] = useState(null);
  const lastProactiveIdRef = useRef(null);
  const currentMeetingTitleRef = useRef(null);

  // Meeting Mode (upgraded)
  const [meetingModeActive, setMeetingModeActive] = useState(false);
  const [meetingModeAttendees, setMeetingModeAttendees] = useState([]);
  const [meetingModeTitle, setMeetingModeTitle] = useState('');
  const [pendingMeetingAttendees, setPendingMeetingAttendees] = useState(null); // waiting for attendee names
  const [zoomPanelOpen, setZoomPanelOpen] = useState(false);
  const [recorderOpen, setRecorderOpen] = useState(false);       // live/DIY MeetingRecorder overlay
  const [meetingChoiceOpen, setMeetingChoiceOpen] = useState(false); // "transcript vs record" chooser

  // ── Reply actions (Carla, 2026-10-21) ───────────────────────────────────────
  // The server reads every action out of the model's reply (Aidey Backend/lib/replyActions.js) and
  // sends prose + `actions`. This chat never parses reply text for actions: it shows `reply` and
  // runs each structured action here. One that has no handler is dropped with a console note,
  // never rendered. Adding an action means a prompt line in routes/aidey.js, its name in
  // KNOWN_ACTIONS there, and a handler here.
  //
  // The importer: the popup is handed an opener by App (which closes the popup and opens the
  // App-level importer); the full page is not, so the chat opens its own. Either way the file the
  // paperclip chose goes with it.
  const [ownImporter, setOwnImporter] = useState(null);   // { file } while this chat's importer is open
  function openDocumentImporter(file = null) {
    if (typeof onOpenDocumentImporter === 'function') onOpenDocumentImporter({ file });
    else setOwnImporter({ file });
  }
  const REPLY_ACTIONS = {
    open_document_importer: {
      line: "Sure, opening the document importer now. Upload a PDF, DOCX, or TXT file (or paste a Google Doc link) and I'll turn it into a structured project.",
      run: () => openDocumentImporter(null),
    },
    open_meeting_transcripts: {
      line: "Opening your meeting transcripts now, pick Zoom, Google Meet, Teams, or upload a transcript, and I'll process it.",
      run: () => setZoomPanelOpen(true),
    },
  };
  // Runs a reply's actions. Returns null when the reply carried none, else the line to show if the
  // reply has no prose of its own.
  function runReplyActions(data) {
    const acts = Array.isArray(data?.actions) ? data.actions : [];
    const dropped = Array.isArray(data?.droppedActions) ? data.droppedActions : [];
    if (!acts.length && !dropped.length) return null;
    let line = null;
    for (const a of acts) {
      const h = Object.prototype.hasOwnProperty.call(REPLY_ACTIONS, a?.action) ? REPLY_ACTIONS[a.action] : null;
      if (!h) { console.warn('[Aidey chat] dropped an action this chat cannot run:', String(a?.action).slice(0, 64)); continue; }
      h.run();
      if (!line) line = h.line;
    }
    for (const d of dropped) console.warn('[Aidey chat] dropped an action the server did not recognise:', String(d).slice(0, 64));
    return { line: line || "Sorry, I couldn't complete that action." };
  }

  // ── The paperclip: a file straight to the importer ─────────────────────────
  const attachInputRef = useRef(null);
  const [attachError, setAttachError] = useState('');
  function handleAttachChange(e) {
    const f = e.target.files?.[0];
    e.target.value = '';   // the same file can be chosen again
    if (!f) return;
    const problem = importFileProblem(f);
    if (problem) { setAttachError(problem); return; }
    setAttachError('');
    openDocumentImporter(f);
  }

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  const [micTapped, setMicTapped] = useState(false);
  const pageSaveTimer = useRef(null);
  const { voiceState, permissionState, startListening, requestPermission } =
    useVoiceInput({
      userId: currentUser?.id,
      onTranscript: (text) => {
        setChatInput(text);
        wasVoiceInputRef.current = true;
      },
      onInterimTranscript: (text) => {
        setChatInput(text);
      },
    });

  function startMeetingTranscription() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      addMessage('assistant', 'Live transcription is not supported in this browser.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-GB';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let merged = '';
      for (let i = 0; i < event.results.length; i += 1) {
        merged += `${event.results[i][0].transcript} `;
      }
      setLiveMeetingTranscript(merged.trim());
    };

    recognition.onerror = (event) => {
      addMessage('assistant', `Transcription error: ${event.error || 'unknown error'}`);
      setIsMeetingTranscribing(false);
    };

    recognition.onend = () => {
      if (isMeetingTranscribingRef.current) {
        try { recognition.start(); } catch (_) { }
      }
    };

    meetingRecognitionRef.current = recognition;
    isMeetingTranscribingRef.current = true;
    setLiveMeetingTranscript('');
    setIsMeetingTranscribing(true);
    addMessage('assistant', 'Transcribing now. Say "stop transcribing" or click Stop to finish.');
    recognition.start();
  }

  function processMeetingTranscriptAndAskApproval(transcriptText) {
    const allUsers = mentionableMembers;
    const actionItems = parseMeetingNotes(transcriptText, allUsers);

    const now = new Date();
    const relevantMeeting = [...userSchedule]
      .filter((t) => /meeting|call|appointment/i.test(t.taskType || t.title || ''))
      .map((t) => ({ ...t, _at: new Date(t.datetime || `${t.date}T${t.time || '09:00:00'}`) }))
      .filter((t) => !Number.isNaN(t._at.getTime()))
      .sort((a, b) => Math.abs(a._at - now) - Math.abs(b._at - now))[0];

    const noteTitle = currentMeetingTitleRef.current
      ? `Meeting transcript: ${currentMeetingTitleRef.current}`
      : relevantMeeting
        ? `Meeting transcript: ${relevantMeeting.title}`
        : `Meeting transcript ${now.toISOString().slice(0, 10)}`;
    currentMeetingTitleRef.current = null;
    const linkedMeta = relevantMeeting
      ? `\n\n[Linked Meeting]\nID: ${relevantMeeting.id || 'n/a'}\nTitle: ${relevantMeeting.title || 'Meeting'}\nDate: ${relevantMeeting.date || relevantMeeting._at?.toISOString().slice(0, 10) || 'n/a'}\nTime: ${relevantMeeting.time || relevantMeeting._at?.toTimeString().slice(0, 5) || 'n/a'}`
      : '';

    // Decision 90 v2: Aidey's meeting note, saved by the server as ONE note per transcript (in Meetings,
    // "From Aidey", linked to the meeting's task), not a new note from here on every save.
    const meetingName = noteTitle.startsWith('Meeting transcript: ') ? noteTitle.slice('Meeting transcript: '.length) : 'Meeting';
    const taskId = relevantMeeting && /^\d+$/.test(String(relevantMeeting.id || '')) ? String(relevantMeeting.id) : undefined;
    api('/api/notes/aidey-meeting', {
      method: 'POST',
      body: JSON.stringify({ meetingTitle: meetingName, text: `${transcriptText}${linkedMeta}`, transcript: transcriptText, ...(taskId ? { taskId } : {}) }),
    }).then(() => window.dispatchEvent(new Event('aidey:notesUpdated')))
      .catch((err) => console.error('Failed to save transcript note:', err));

    if (!actionItems.length) {
      addMessage('assistant', 'I captured the transcript and saved it as a note, but I could not extract clear action items.');
      return;
    }

    const preview = actionItems.slice(0, 8).map((item) => `• ${item.title}${item.assigneeName ? `, ${item.assigneeName}` : ''}`).join('\n');
    addMessage('assistant', `I extracted ${actionItems.length} action item(s):\n\n${preview}\n\nReply **approve** to create these tasks or **cancel**.`);
    setPendingTask({
      ...(pendingTask || {}),
      transcript: transcriptText,
      transcriptActionItems: actionItems,
    });
    setPendingField('meetingTranscriptReview');
  }

  function stopMeetingTranscription() {
    setIsMeetingTranscribing(false);
    isMeetingTranscribingRef.current = false;
    try {
      meetingRecognitionRef.current?.stop();
    } catch (_) { }
    const transcript = (liveMeetingTranscript || '').trim();
    if (!transcript) {
      addMessage('assistant', 'No transcript captured yet.');
      return;
    }
    processMeetingTranscriptAndAskApproval(transcript);
  }

  function activateMeetingMode(attendees, title) {
    setMeetingModeAttendees(attendees || []);
    setMeetingModeTitle(title || '');
    setMeetingModeActive(true);
    setPendingTranscribePrompt(null);
    setPendingMeetingAttendees(null);
  }

  function handleMeetingEnd({ transcript, postMeeting, tasksCreated, meetingTitle, draftEmail }) {
    setMeetingModeActive(false);
    setMeetingModeAttendees([]);
    setMeetingModeTitle('');

    if (draftEmail) {
      const cardId = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      setChatMessages(prev => [
        ...prev,
        { role: 'assistant', content: `Meeting complete. I've drafted a summary email ready to review.`, ts: Date.now() },
        { role: 'email_draft', emailDraft: draftEmail, cardId, ts: Date.now() + 1 },
      ]);
      return;
    }

    const msg = postMeeting
      ? `Meeting complete. ${tasksCreated ? `Created ${tasksCreated} task${tasksCreated === 1 ? '' : 's'}.` : ''} Notes saved.`
      : 'Meeting mode closed.';
    addMessage('assistant', msg);

    if (transcript) {
      window.dispatchEvent(new Event('aidey:tasksUpdated'));
      window.dispatchEvent(new Event('aidey:notesUpdated'));
    }
  }

  function handleMeetingDismiss() {
    setMeetingModeActive(false);
    setMeetingModeAttendees([]);
    setMeetingModeTitle('');
    addMessage('assistant', 'Meeting mode closed.');
  }

  // Consume proactiveMessage from parent (App.js) when it changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!proactiveMessage || proactiveMessage.id === lastProactiveIdRef.current) return;
    lastProactiveIdRef.current = proactiveMessage.id;
    onProactiveHandled?.();
    // INTRO-2: a first-use chat intro carries kind:'intro' + a ready body. Render it
    // as a plain assistant message and stop (no meeting-notes handling).
    if (proactiveMessage.kind === 'intro') {
      if (proactiveMessage.body) addMessage('assistant', proactiveMessage.body);
      return;
    }
    const attendees = Array.isArray(proactiveMessage.attendees) ? proactiveMessage.attendees : [];
    const names = attendees.map(a => a.name || a).filter(Boolean).join(', ');
    const withLine = names ? ` with ${names}` : '';
    const mins = proactiveMessage.minsUntil;
    const minsText = mins ? `in ${mins} min${mins === 1 ? '' : 's'}` : 'in a few minutes';
    addMessage('assistant', `Your meeting **"${proactiveMessage.title}"**${withLine} starts ${minsText}. Want me to take notes?`);
    setPendingTranscribePrompt({ title: proactiveMessage.title, attendees });
  }, [proactiveMessage]); // eslint-disable-line

  // Deliver notification-routed message as an Aidey message
  const lastNotifProactiveIdRef = useRef(null);
  useEffect(() => {
    if (!notificationProactive || notificationProactive.id === lastNotifProactiveIdRef.current) return;
    lastNotifProactiveIdRef.current = notificationProactive.id;
    onNotificationProactiveHandled?.();
    addMessage('assistant', notificationProactive.message);
  }, [notificationProactive]); // eslint-disable-line

  // Pre-fill chat input when opened from a scheduling conflict ("Ask Aidey")
  useEffect(() => {
    if (!clashContext) return;
    setChatInput(clashContext);
    onClashContextHandled?.();
  }, [clashContext]); // eslint-disable-line

  // Decision 78: the empty chat is the greeting and the composer only. The unasked nudge from
  // /api/aidey/proactive ("17 tasks are overdue…") was added here on open and again after every
  // message; it is no longer put into the chat. A message the person asked for by tapping a
  // notification still arrives (notificationProactive, notifContext above).

  // Check for a recent session on popup open (only when not resuming an existing session)
  useEffect(() => {
    if (!isPopup || sessionIdProp) return;
    let ignore = false;
    getLastDbSession().then(session => {
      if (ignore || !session) return;
      const relTime = (() => {
        const diffMins = Math.floor((Date.now() - new Date(session.updatedAt).getTime()) / 60000);
        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        const h = Math.floor(diffMins / 60);
        return h < 24 ? `${h}h ago` : null;
      })();
      if (!relTime) return; // > 24h, skip
      setChatMessages(prev => {
        // Don't add if already showing resume card
        if (prev.some(m => m.role === 'session_resume')) return prev;
        // Replace the initial greeting with the resume card
        const withoutGreeting = prev.filter(m => !(m.role === 'assistant' && m.type === 'greeting'));
        return [...withoutGreeting, { role: 'session_resume', sessionId: session.id, sessionTitle: session.title || 'your last conversation', sessionTime: relTime, ts: Date.now() }];
      });
    });
    return () => { ignore = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load unread delegation handover messages on mount and prepend them to chat
  useEffect(() => {
    let ignore = false;
    api('/api/absence-delegation/messages/unread')
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (ignore || !data?.messages?.length) return;
        const ids = data.messages.map(m => m.id);
        const msgs = data.messages.map(m => ({
          role: 'assistant',
          content: m.message,
          ts: new Date(m.createdAt).getTime(),
          meta: { delegationMessageId: m.id },
        }));
        setChatMessages(prev => {
          const alreadySeen = ids.every(id => prev.some(m => m.meta?.delegationMessageId === id));
          if (alreadySeen) return prev;
          return [...msgs, ...prev];
        });
        api('/api/absence-delegation/messages/mark-read', {
          method: 'POST',
          body: JSON.stringify({ ids }),
        }).catch(() => {});
      })
      .catch(() => {});
    return () => { ignore = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    const chatDiv = chatScrollRef.current;
    if (!chatDiv) return;
    // Auto-scroll down on new message (debounced)
    setTimeout(() => {
      chatDiv.scrollTop = chatDiv.scrollHeight;
    }, 10);
    return () => { };
  }, [chatMessages]);

  // Persist messages into the session store, and tell the page only when something was saved.
  useEffect(() => {
    if (loadingSession) return;   // never persist the placeholder while an opened chat loads
    const changed = persistMessages(sessionIdRef.current, chatMessages);
    if (changed && onSessionUpdated) onSessionUpdated(sessionIdRef.current);
    // The full page saves to the server too, shortly after each real change (only the popup's
    // close used to, so a chat held on the page lived in this browser alone and could not be
    // opened anywhere else). One save per burst of messages.
    if (changed && !isPopup) {
      clearTimeout(pageSaveTimer.current);
      pageSaveTimer.current = setTimeout(() => {
        const saved = loadSession(sessionIdRef.current);
        if (saved) saveSessionToDb({ id: saved.id, title: saved.title, messages: saved.messages, createdAt: saved.createdAt, projectId: projectIdProp });
      }, 1500);
    }
  }, [chatMessages]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => clearTimeout(pageSaveTimer.current), []);

  // Opening a chat this browser has no copy of (older, or from another device): read its
  // messages from the server. Before this, such a chat opened on the greeting and nothing was
  // ever fetched. Read only: opening or switching a chat never writes or deletes anything.
  useEffect(() => {
    if (!loadingSession) return undefined;
    let alive = true;
    loadSessionFromDb(sessionIdProp).then((session) => {
      if (!alive) return;
      if (session?.messages?.length) { cacheSession(session); setChatMessages(session.messages); }
      else setSessionLoadFailed(true);
      setLoadingSession(false);
    });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Release 7 (Carla, 2026-10-21): said only once it is done. The offline paths said "added" or
  // "saved" while the save was still on its way, and said nothing when it failed. Now the success
  // line waits for the server, and a failure says so, with the server's reason.
  function sayWhenSaved(request, okText, what) {
    return Promise.resolve(request)
      .then((res) => { addMessage("assistant", okText); return res; })
      .catch((err) => {
        const why = messageFromError(err, 'it did not go through');
        addMessage("assistant", `I couldn't save ${what}: ${why}${/[.!?]$/.test(why) ? '' : '.'} Nothing was saved.`);
        return null;
      });
  }

  function addMessage(role, content) {
    setChatMessages(prev => [...prev, { role, content, ts: Date.now() }]);
  }

  function addLoadingMessage() {
    setChatMessages(prev => [...prev, { role: 'assistant', content: '…', isTyping: true, ts: Date.now() }]);
  }

  // ── Approval card handlers ─────────────────────────────────────────────────
  async function handleApprovalConfirm(msg, editedBody) {
    const { approvalData } = msg;
    const tool = approvalData.tool;
    const input = { ...(approvalData.input || {}) };
    // What the card lets the owner change. The server applies only the fields that card offers.
    const edits = {};

    if (editedBody !== undefined) {
      input.draft_body = editedBody;
      input.body = editedBody;
      edits.draft_body = editedBody;
      edits.body = editedBody;
    }

    // Legacy email send (collected via step-by-step flow, not Anthropic tool-use)
    if (tool === 'send_email_legacy') {
      const emailInput = approvalData.input || {};
      const toAddr = emailInput.to;
      const body = editedBody !== undefined ? editedBody : emailInput.body;
      setChatMessages(prev => prev.map(m =>
        m.cardId === msg.cardId ? { role: 'assistant', content: '…', isTyping: true, ts: Date.now() } : m
      ));
      try {
        const res = await api('/api/email/send', {
          method: 'POST',
          body: JSON.stringify({ to: toAddr, subject: emailInput.subject, body }),
        });
        if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          throw new Error(payload?.error || 'Send failed');
        }
        setChatMessages(prev => prev.map(m =>
          m.isTyping ? { role: 'assistant', content: `Sent. Your email to **${toAddr}** is on its way.`, ts: Date.now() } : m
        ));
      } catch (err) {
        setChatMessages(prev => prev.map(m =>
          m.isTyping ? { role: 'assistant', content: `That email didn't send. ${err.message}. Want me to try again?`, ts: Date.now() } : m
        ));
      }
      setPendingTask(null);
      return;
    }

    // Inject writing style for invoice chases
    if (tool === 'send_invoice_chase') {
      const styleProfile = loadStyleProfile(currentUser);
      const learned = loadLearnedPatterns(currentUser?.id);
      input.writing_style = {
        toneKeywords: styleProfile?.toneKeywords || [],
        wordsToAvoid: styleProfile?.wordsToAvoid || [],
        signOff: styleProfile?.signOff || '',
        learnedSampleCount: learned?.sampleCount || 0,
      };
      edits.writing_style = input.writing_style;
    }

    // THE RECORD GOES WHERE THE CARD WAS: the confirmation, then the outcome.
    // This used to APPEND the confirmation line at the end while replacing the
    // card in place earlier, so the next turn replayed "Done." followed by an
    // instruction to do it — and the model did it again (device sitting,
    // 2026-09-21). See utils/confirmationHistory.
    const pendingKey = `confirm-${msg.cardId}`;
    setChatMessages(prev => replaceApprovalCard(prev, { cardId: msg.cardId }, [
      { role: 'user', content: buildConfirmSummary(tool, input), synthetic: true, ts: Date.now() },
      { role: 'assistant', content: '…', isTyping: true, pendingKey, ts: Date.now() + 1 },
    ]));

    try {
      const result = await confirmAideyTool(tool, approvalData.confirmToken, areasRef.current, Object.keys(edits).length ? edits : undefined);
      fireToolEvents([tool]);
      const summary = formatConfirmedActionSummary(tool, result);
      // C-b: an action card this call completed reads "Done" now, and only now.
      setChatMessages(prev => markCardsDone(settlePlaceholder(prev, pendingKey, outcomeMessage({ tool, ok: true, summary })), tool, input));
    } catch (err) {
      setChatMessages(prev => settlePlaceholder(prev, pendingKey, outcomeMessage({
        tool, ok: false, reason: err.message,
        summary: `That didn't go through. ${err.message}. Let me know if you'd like to retry.`,
      })));
    } finally {
      // Cleared on failure too: a failed confirmation left pending used to be
      // re-run by the next message that happened to start with "ok".
      setPendingToolConfirmation(null);
    }
  }

  function handleApprovalCancel(msg) {
    setPendingToolConfirmation(null);
    setPendingTask(null);
    const tool = msg?.approvalData?.tool;
    setChatMessages(prev => replaceApprovalCard(prev, { cardId: msg.cardId }, [
      { role: 'assistant', content: 'No action taken.', historyContent: `No action taken. ${declinedMarker(tool)}`, ts: Date.now() },
    ]));
  }

  async function handleWorkloadConfirm(msg, approvedSuggestions) {
    setChatMessages(prev => prev.map(m =>
      m.cardId === msg.cardId ? { role: 'assistant', content: '…', isTyping: true, ts: Date.now() } : m
    ));
    try {
      const result = await confirmAideyTool('redistribute_workload', msg.confirmToken, areasRef.current, { approvedSuggestions });
      const summary = formatConfirmedActionSummary('redistribute_workload', result);
      window.dispatchEvent(new Event('aidey:tasksUpdated'));
      setChatMessages(prev => prev.map(m =>
        m.isTyping ? { role: 'assistant', content: summary, ts: Date.now() } : m
      ));
    } catch (err) {
      setChatMessages(prev => prev.map(m =>
        m.isTyping ? { role: 'assistant', content: `Couldn't move those tasks. ${err.message}. Let me know if you'd like to try again.`, ts: Date.now() } : m
      ));
    }
  }

  function handleWorkloadCancel(msg) {
    setChatMessages(prev => prev.map(m =>
      m.cardId === msg.cardId ? { role: 'assistant', content: 'No tasks moved.', ts: Date.now() } : m
    ));
  }

  async function handleAbsenceDelegationConfirm(msg, { leaveRequestId, delegations }) {
    setChatMessages(prev => prev.map(m =>
      m.cardId === msg.cardId ? { role: 'assistant', content: '…', isTyping: true, ts: Date.now() } : m
    ));
    try {
      const res = await api('/api/absence-delegation/execute', {
        method: 'POST',
        body: JSON.stringify({ leaveRequestId, delegations }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Delegation failed');
      const names = (data.summary || []).join('\n');
      window.dispatchEvent(new Event('aidey:tasksUpdated'));
      setChatMessages(prev => prev.map(m =>
        m.isTyping ? { role: 'assistant', content: `Done, I've delegated ${data.delegated} task${data.delegated === 1 ? '' : 's'} and sent a handover note to each person.${names ? `\n\n${names}` : ''}`, ts: Date.now() } : m
      ));
    } catch (err) {
      setChatMessages(prev => prev.map(m =>
        m.isTyping ? { role: 'assistant', content: `Couldn't complete the delegation: ${err.message}`, ts: Date.now() } : m
      ));
    }
  }

  function handleAbsenceDelegationCancel(msg) {
    setChatMessages(prev => prev.map(m =>
      m.cardId === msg.cardId ? { role: 'assistant', content: 'No tasks delegated.', ts: Date.now() } : m
    ));
  }

  // ── Session save on close ────────────────────────────────────────────────

  async function handleClose() {
    const userMessages = chatMessages.filter(m => m.role === 'user' && m.content);
    if (userMessages.length > 0) {
      const sessionId = sessionIdRef.current;
      const existing = loadSession(sessionId);
      let title = existing?.title;
      if (!title || title === userMessages[0].content.slice(0, 60)) {
        title = await generateSessionTitle(userMessages[0].content);
        // Update localStorage title too
        if (existing) {
          persistMessages(sessionId, chatMessages);
        }
      }
      // Save to DB (fire-and-forget)
      saveSessionToDb({
        id: sessionId,
        title,
        messages: chatMessages,
        createdAt: existing?.createdAt || new Date().toISOString(),
        projectId: projectIdProp,
      });
    }
    onClose?.();
  }

  // Expose handleClose to parent via ref so the popup titlebar can trigger a proper save
  if (closeRef) closeRef.current = handleClose;

  // ── Session resume on reopen ─────────────────────────────────────────────

  async function handleSessionContinue(sessionId) {
    setChatMessages(prev => prev.filter(m => m.role !== 'session_resume'));
    // Load full messages from DB
    const session = await loadSessionFromDb(sessionId);
    if (session?.messages?.length) {
      sessionIdRef.current = session.id;
      setChatMessages(session.messages);
    }
  }

  function handleSessionFresh() {
    setChatMessages(prev => prev.filter(m => m.role !== 'session_resume'));
    setChatMessages([{ role: 'assistant', content: chatGreetingFor(currentUser), ts: Date.now(), type: 'greeting' }]);
  }

  // Helper to push an approval card message into the chat
  function addApprovalCard(approvalData) {
    const cardId = `approval-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setChatMessages(prev => [...prev, { role: 'approval', approvalData, cardId, ts: Date.now() }]);
    return cardId;
  }

  // === @MENTION HANDLING ===
  function handleInputChange(e) {
    const value = e.target.value;
    setChatInput(value);
    // Typing clears the voice flag so only full voice submissions trigger intent detection
    wasVoiceInputRef.current = false;

    // Check for @mentions
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const afterAt = value.slice(lastAtIndex + 1);

      // Only show suggestions if the @ is at the beginning of a word
      const beforeAt = value.slice(0, lastAtIndex);
      const isValidMention = beforeAt === '' || /\s$/.test(beforeAt);

      if (isValidMention && !afterAt.includes(' ')) {
        // Suggest only the active workspace's mentionable (account-holding) people.
        const q = afterAt.toLowerCase();
        const suggestions = mentionableMembers
          .filter(m => (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q))
          .map(m => ({
            id: m.userId,
            name: m.name,
            email: m.email,
            handle: `@${(m.name || '').toLowerCase().replace(/\s+/g, '')}`,
            color: m.color,
            avatar: m.avatar,
          }));
        setMentionSuggestions(suggestions);
        setMentionQuery(afterAt);
        setShowMentions(suggestions.length > 0);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  }

  function selectMention(suggestion) {
    const lastAtIndex = chatInput.lastIndexOf('@');
    const beforeAt = chatInput.slice(0, lastAtIndex);
    const afterMention = chatInput.slice(lastAtIndex + 1 + mentionQuery.length);

    setChatInput(beforeAt + suggestion.handle + ' ' + afterMention);
    setShowMentions(false);
    setMentionSuggestions([]);
    setMentionQuery('');
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape' && showMentions) {
      setShowMentions(false);
      e.preventDefault();
    }
  }

  // === CHAT SUBMIT HANDLER (handles multi-action!) ===
  async function handleChatSubmit(e) {
    e.preventDefault();
    const input = chatInput.trim();
    if (!input) return;

    addMessage("user", input);
    setChatInput('');

    // Meeting Mode attendee collection
    if (pendingMeetingAttendees !== null) {
      const raw = input.trim();
      const attendees = /^(just me|no one|none|solo|only me)$/i.test(raw)
        ? []
        : raw.split(/[,;&]+/).map(n => ({ name: n.trim() })).filter(a => a.name.length > 0);
      activateMeetingMode(attendees, pendingMeetingAttendees.title || '');
      setChatInput('');
      return;
    }

    if (pendingToolConfirmation) {
      // Whole replies only. These matched any message that merely STARTED with
      // "ok", "sure" or "y" ("yesterday's…") as a confirmation, and "n…" ("now…")
      // as a decline. See utils/confirmationHistory.
      const yes = isConfirmReply(input);
      const no = isDeclineReply(input);

      if (yes) {
        const pending = pendingToolConfirmation;
        const pendingKey = `confirm-typed-${Date.now()}`;
        // The typed "yes" is already in the conversation. Remove the card — it
        // used to stay LIVE, and was persisted with the session — and put the
        // in-flight placeholder at the end, after the "yes".
        setChatMessages(prev => [
          ...replaceApprovalCard(prev, { pending }, []),
          { role: 'assistant', content: '…', isTyping: true, pendingKey, ts: Date.now() },
        ]);
        const confirmInput = { ...(pendingToolConfirmation.input || {}) };
        if (pendingToolConfirmation.tool === 'send_invoice_chase') {
          const styleProfile = loadStyleProfile(currentUser);
          const learned = loadLearnedPatterns(currentUser?.id);
          confirmInput.writing_style = {
            toneKeywords: styleProfile?.toneKeywords || [],
            wordsToAvoid: styleProfile?.wordsToAvoid || [],
            signOff: styleProfile?.signOff || '',
            learnedSampleCount: learned?.sampleCount || 0,
          };
        }

        confirmAideyTool(pending.tool, pending.confirmToken, areasRef.current,
          confirmInput.writing_style ? { writing_style: confirmInput.writing_style } : undefined)
          .then((result) => {
            const summary = formatConfirmedActionSummary(pending.tool, result);
            setChatMessages((prev) => markCardsDone(settlePlaceholder(prev, pendingKey, outcomeMessage({ tool: pending.tool, ok: true, summary })), pending.tool, confirmInput));
            return result;
          })
          .catch((err) => {
            setChatMessages((prev) => settlePlaceholder(prev, pendingKey, outcomeMessage({
              tool: pending.tool, ok: false, reason: err.message,
              summary: `That didn't complete. ${err.message}. Let me know if you'd like to retry.`,
            })));
          })
          .finally(() => setPendingToolConfirmation(null));
        setChatInput("");
        return;
      }

      if (no) {
        const pending = pendingToolConfirmation;
        setPendingToolConfirmation(null);
        // Remove the card: after a typed "no" it used to stay with a live
        // Confirm button that would still do the thing.
        setChatMessages(prev => [
          ...replaceApprovalCard(prev, { pending }, []),
          { role: 'assistant', content: 'No action taken.', historyContent: `No action taken. ${declinedMarker(pending.tool)}`, ts: Date.now() },
        ]);
        setChatInput('');
        return;
      }

      // Anything else is a new request, not an answer. The card stays on screen
      // for an explicit click, but a later "ok thanks" must not silently run it.
      setPendingToolConfirmation(null);
    }

    // Allow users to cancel or start over at any time
    if (/^(cancel|stop|abort|start\s*over|never\s*mind|exit)$/i.test(input)) {
      setPendingTask(null);
      setPendingField(null);
      setVoiceConfirmation(null);
      wasVoiceInputRef.current = false;
      addMessage("assistant", "Cancelled. Nothing's been changed.");
      setChatInput("");
      return;
    }

    if (isMeetingTranscribing && /stop\s+transcrib/i.test(input)) {
      stopMeetingTranscription();
      setChatInput('');
      return;
    }

    // Tool-first orchestration: let Claude decide actions and tools before legacy parser fallback.
    const historyForAI = [
      ...chatMessages
        .filter((m) => (m.role === 'user' || m.role === 'assistant') && !m.isTyping)
        .map((m) => ({
          // `historyContent ?? content` — the ?? is a TRIGGER, not a verdict.
          //
          // Messages stored before 2026-09-21 have no historyContent, so they
          // fall back to `content`, which carries REAL CLIENT NAMES. Measured
          // that day: 80 persisted sessions, 599 messages, and ZERO
          // public-intake contacts of 22 — so no stored message could contain a
          // name that came from outside the business, and the fallback was safe.
          //
          // IT STOPS BEING SAFE THE DAY A SCHEDULING-LED CUSTOMER SIGNS UP,
          // because their clients arrive through the public booking page and
          // their names would sit in pre-existing transcripts.
          //
          // If you are reading this because that has happened: re-run the count
          // (public-intake contacts, and sessions whose messages contain one),
          // and if it is non-zero, drop the fallback and treat a message with no
          // historyContent as having no replayable content rather than replaying
          // the resolved text.
          role: m.role, content: m.historyContent ?? m.content, refs: m.refs,
        })),
      { role: 'user', content: input },
    ];

    addLoadingMessage();
    let aiData = null;
    try {
      aiData = await askAidey(historyForAI, {});
    } catch (_) {
      aiData = null;
    }

    if (aiData?.__creditsError) {
      setChatMessages((prev) => [
        ...prev.slice(0, -1),
        { role: 'assistant', content: "My AI engine is temporarily unavailable, the Anthropic account needs credits. Basic commands (create task, schedule meeting) still work, but conversational AI responses won't be available until credits are topped up.", ts: Date.now(), tone: 'problem' },
      ]);
      setChatInput('');
      return;
    }

    if (!aiData) {
      setChatMessages((prev) => [
        ...prev.slice(0, -1),
        { role: 'assistant', content: "Aidey timed out, please try again.", ts: Date.now(), tone: 'problem' },
      ]);
      setChatInput('');
      return;
    }

    // C-b: what this turn used (for the history tag), on the person's own message.
    recordTurnAreas(aiData?.areas);

    // The reply's actions run here; the server has already taken them out of `reply`. A reply that
    // was only an action shows that action's own line (or a plain apology for one we cannot run).
    const replyActs = runReplyActions(aiData);
    const aiReply = aiData?.reply?.trim() || replyActs?.line;

    // Integration check must come first — toolUsed is also true when integration is missing
    if (aiData?.integrationRequired) {
      setChatMessages((prev) => [
        ...prev.slice(0, -1),
        { role: 'integration-required', integrationName: aiData.integrationName, ts: Date.now() },
      ]);
      setChatInput('');
      return;
    }

    if (aiData?.needsConfirmation || aiData?.emailDraft || aiData?.quotedExternal || aiData?.toolUsed) {
      if (aiData?.needsConfirmation) {
        const nc = aiData.needsConfirmation;
        if (nc.type === 'workload_redistribution') {
          const cardId = `workload-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          setChatMessages((prev) => [
            ...prev.slice(0, -1),
            ...(aiReply ? [{ role: 'assistant', content: aiReply, ts: Date.now() }] : []),
            { role: 'workload_redistribution', suggestions: nc.suggestions || [], confirmToken: nc.confirmToken, cardId, ts: Date.now() + 1 },
          ]);
        } else if (nc.type === 'absence_delegation') {
          const cardId = `absence-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          setChatMessages((prev) => [
            ...prev.slice(0, -1),
            ...(aiReply ? [{ role: 'assistant', content: aiReply, ts: Date.now() }] : []),
            { role: 'absence_delegation', absenceData: nc.absenceData, leaveRequestId: nc.input?.leave_request_id, cardId, ts: Date.now() + 1 },
          ]);
        } else {
          setPendingToolConfirmation(nc);
          const cardId = `approval-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          setChatMessages((prev) => [
            ...prev.slice(0, -1),
            ...(aiReply ? [{ role: 'assistant', content: aiReply, ts: Date.now() }] : []),
            { role: 'approval', approvalData: nc, cardId, ts: Date.now() + 1 },
          ]);
        }
      } else if (aiData?.quotedExternal) {
        // Externally-written content: rendered as an attributed quote, never as a
        // message bubble, and never sent back to the model.
        const cardId = `q_${Date.now()}`;
        setChatMessages(prev => [...prev,
          ...(aiData.reply ? [{ role: 'assistant', content: aiData.reply, ts: Date.now() }] : []),
          { role: 'quoted_external', quoted: aiData.quotedExternal, cardId, ts: Date.now() + 1 },
        ]);
      } else if (aiData?.emailDraft) {
        const cardId = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        setChatMessages((prev) => [
          ...prev.slice(0, -1),
          ...(aiReply ? [{ role: 'assistant', content: aiReply, ts: Date.now() }] : []),
          { role: 'email_draft', emailDraft: aiData.emailDraft, cardId, ts: Date.now() + 1 },
        ]);
      } else {
        // Tool executed — fire refresh events so UI updates immediately
        fireToolEvents(aiData.toolNamesUsed);
        setChatMessages((prev) => [
          // C-b: a card whose action this turn performed (and the record says worked) is done.
          ...markCardsDoneFromOutcomes(prev.slice(0, -1), aiData.toolOutcomes),
          // `content` is what the OWNER sees, with real client names.
          // `historyContent` is what goes back to the model next turn: the same
          // sentence with tokens instead of names, plus the token->contactId map.
          // The display and the history diverge deliberately — see
          // lib/contactRefs.js. Without this, every name the references kept out
          // of this turn walks back into model context on the next one.
          { role: 'assistant', content: aiReply || fallbackForOutcomes(aiData.toolOutcomes), ts: Date.now(),
            historyContent: aiData.replyForHistory, refs: aiData.refs,
            // C-b: follow-ups Aidey can actually do from here (the server offers them only when
            // their gated tool is available to this chat). Each sends its instruction into this chat.
            ...(Array.isArray(aiData.actionCards) && aiData.actionCards.length ? { actionCards: aiData.actionCards } : {}) },
          ...(aiData.claimCorrection
            ? [{ role: 'assistant_correction', content: aiData.claimCorrection, ts: Date.now() + 1 }]
            : []),
        ]);
      }
      setChatInput('');
      return;
    }

    if (aiReply) {
      setChatMessages((prev) => [
        ...prev.slice(0, -1),
        // content = what the owner sees; historyContent = what is replayed.
        { role: 'assistant', content: aiReply, ts: Date.now(),
          historyContent: aiData.replyForHistory, refs: aiData.refs },
      ]);
      setChatInput('');
      return;
    }

    // Remove loading placeholder before legacy fallback.
    setChatMessages((prev) => (prev[prev.length - 1]?.isTyping ? prev.slice(0, -1) : prev));

    // ── Voice input: route through Claude pipeline same as typed text ──────
    if (wasVoiceInputRef.current) {
      wasVoiceInputRef.current = false;
    }

    // MULTI-ACTION: If parseUserInput returns array, loop over actions!
    const detailsList = parseUserInput(input, mentionableMembers);
    if (Array.isArray(detailsList)) {
      detailsList.filter(Boolean).forEach(action => handleSingleAction(action));
    } else {
      handleSingleAction(detailsList);
    }
    setChatInput("");
  }

  // ── Voice confirmation handlers ───────────────────────────────────────────
  function handleVoiceConfirm() {
    if (!voiceConfirmation) return;
    const { intent, summary, data } = voiceConfirmation;
    setVoiceConfirmation(null);

    if (intent === 'create_task' || intent === 'create_meeting') {
      const when = String(data.datetime || data.dueDate || '');
      const taskData = {
        title: data.title || summary,
        taskType: intent === 'create_meeting' ? 'Meeting' : 'Task',
        datetime: when || null,
        // The app saves from a date and a time; a voice datetime carried neither, so the save was
        // refused unseen while this said "Done" (release 7).
        ...(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(when) ? { date: when.slice(0, 10), time: when.slice(11, 16) } : {}),
        priority: data.priority || 'normal',
        completed: false,
      };
      // The app answers once it has saved (or not): its words, not a "Done" said before.
      if (onCreateTask) onCreateTask(taskData, (message) => { if (message) addMessage("assistant", message); });
      else addMessage("assistant", "I couldn't add that here. Open Tasks to add it.");
    } else if (intent === 'create_note') {
      const note = {
        id: String(Date.now()),
        title: data.title || summary,
        text: data.body || '',
        blocks: [],
        created: new Date().toISOString(),
      };
      sayWhenSaved(api('/api/notes', { method: 'POST', body: JSON.stringify(note) })
        .then((res) => { window.dispatchEvent(new Event("aidey:notesUpdated")); return res; }),
        `Done, "${data.title || summary}" saved to your notes.`, `"${data.title || summary}"`);
    } else if (intent === 'create_contact') {
      const saving = api('/api/contacts', {
        method: 'POST',
        body: JSON.stringify({
          name: data.name || summary,
          email: data.email || '',
          phone: data.phone || '',
          source: 'Voice',
          tags: [],
          isNew: true,
          interactions: [],
        }),
      }).then((res) => { window.dispatchEvent(new Event('aidey:contactsUpdated')); return res; });
      sayWhenSaved(saving, `Done, ${data.name || 'that contact'} is in your contacts.`, data.name || 'that contact');
    } else if (intent === 'create_invoice') {
      const saving = api('/api/invoices', {
        method: 'POST',
        body: JSON.stringify({
          contactName: data.client || '',
          issueDate: new Date().toISOString().slice(0, 10),
          dueDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
          status: 'draft',
          lineItems: data.amount ? [{ id: 1, description: data.description || 'Service', qty: 1, unitPrice: parseFloat(data.amount) || 0, tax: 0 }] : [],
        }),
      }).then((res) => { refreshInvoices(); return res; });
      sayWhenSaved(saving, `Done, draft invoice${data.client ? ` for ${data.client}` : ''} saved to Invoicing.`, 'the draft invoice');
    } else {
      addMessage("assistant", "Done.");
    }
  }

  function handleVoiceCancel() {
    setVoiceConfirmation(null);
  }

  // === Single Action Handler ===
  function handleSingleAction(details) {
    if (/transcrib(e|ing).*(meeting|call)|meeting.*transcrib(e|ing)|start.*meeting.*note|meeting.*note|i.?m in a meeting|start meeting mode/i.test(details.rawInput || details.title || '')) {
      addMessage('assistant', "Who's in the meeting with you? List their names separated by commas, or say \"just me\" to start without attendees.");
      setPendingMeetingAttendees({ title: details.title || '' });
      return;
    }

    // C-b: a switched-off area stops the offline parser too, not only the model's tools.
    const offArea = localActionAreas(details).find(a => areasRef.current[a] === false);
    if (offArea) {
      const label = AREA_TAG[offArea].label;
      addMessage('assistant', `${label} is turned off for this chat, so I have not done that. Turn ${label} back on at the top of the chat if you want me to.`);
      return;
    }

    // TODO: If any field missing, step-by-step ask user
    // This can be expanded for more robust field collection

    // 0z. Writing help — style-injected AI assistance
    if (details.action === "writing-help") {
      addMessage("assistant", "I can help with that. Paste what you've written so far, or describe what you need, I'll suggest improvements that match your voice.");
      setPendingTask(details);
      setPendingField("writingInput");
      return;
    }

    // 0a. Content generation
    if (details.action === "generate-content") {
      const type = details.contentType || "content";
      addMessage("assistant", `Sure! I can draft ${type} for you. What's the topic or brief?`);
      setPendingTask(details);
      setPendingField("contentBrief");
      return;
    }

    // 0a2. Agenda query
    if (details.action === "agenda" || /\b(agenda|what.*today|today.*tasks?|on my schedule|my day)\b/i.test(details.title || "")) {
      const rawInput = details.rawInput || "";
      const isWeek = /\b(this week|next 7 days|week ahead|weekly)\b/i.test(rawInput);
      const isTomorrow = /\btomorrow\b/i.test(rawInput);
      const checkDate = isTomorrow ? getTomorrowISO() : getTodayISO();

      if (isWeek) {
        // Show 7-day summary
        const today = new Date(getTodayISO());
        const lines = [];
        for (let i = 0; i < 7; i++) {
          const d = new Date(today);
          d.setDate(d.getDate() + i);
          const ds = toLocalDateStr(d);
          const dayTasks = userSchedule.filter(t => t.date === ds && !t.completed);
          if (dayTasks.length > 0) {
            const dayLabel = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : d.toLocaleDateString('en-GB', { weekday: 'long' });
            lines.push(`**${dayLabel} (${fmtDate(ds)})**`);
            dayTasks.forEach(t => lines.push(`  • ${t.title}${t.time ? ' at ' + fmtTime(t.time) : ''}`));
          }
        }
        addMessage("assistant", lines.length > 0
          ? `Here's your week ahead:\n\n${lines.join('\n')}`
          : "You have nothing scheduled this week. Want me to add something?");
      } else {
        const label = isTomorrow ? 'tomorrow' : 'today';
        const dayTasks = userSchedule.filter(t => t.date === checkDate && !t.completed);
        if (dayTasks.length === 0) {
          addMessage("assistant", `You have nothing scheduled for ${label}. Want me to add something?`);
        } else {
          const sorted = [...dayTasks].sort((a, b) => (a.time || '').localeCompare(b.time || ''));
          const lines = sorted.map(t => `• ${t.title}${t.time ? ' at ' + fmtTime(t.time) : ''}${t.priority && t.priority !== 'Medium' ? ` (${t.priority})` : ''}`).join("\n");
          addMessage("assistant", `Here's your agenda for ${label}:\n\n${lines}`);
        }
      }
      return;
    }

    // 0b. Find free time slots
    if (details.action === "find-slots") {
      const checkDate = details.date || getTodayISO();
      const busyTasks = userSchedule.filter(t => t.date === checkDate && !t.completed && t.time);
      if (busyTasks.length === 0) {
        addMessage("assistant", `You have no scheduled tasks on ${fmtDate(checkDate)}. You're free all day! Want me to book something?`);
      } else {
        const busyTimes = busyTasks.map(t => t.time).sort().map(fmtTime).join(", ");
        const nextFree = findNextAvailable({ date: checkDate, time: "07:00" }, userSchedule);
        const slotLabel = nextFree
          ? (nextFree.dayOffset > 0 ? `${fmtDate(nextFree.date)} at ${fmtTime(nextFree.time)}` : fmtTime(nextFree.time))
          : "no free slots found today";
        addMessage("assistant", `On ${fmtDate(checkDate)} you're busy at: ${busyTimes}.\nNext available slot: **${slotLabel}**. Want me to book something there?`);
      }
      return;
    }

    // 0c2. Invoice creation from chat
    if (details.action === "create-invoice") {
      const rawInput = details.rawInput || details.title || "";
      const clientMatch = rawInput.match(/(?:invoice\s+for|bill)\s+([A-Za-z\s]+?)(?:\s+for|\s+\$|\s+£|\s+€|\s*$)/i);
      const amountMatch = rawInput.match(/[$£€]?\s*(\d+(?:[.,]\d{2})?)\s*(?:[$£€]|dollars?|pounds?)?/i);
      const client = clientMatch ? clientMatch[1].trim() : null;
      const amount = amountMatch ? parseFloat(amountMatch[1].replace(',', '.')) : null;

      if (!client) {
        addMessage("assistant", "Who is this invoice for? (Give me the client or company name)");
        setPendingTask({ ...details, amount });
        setPendingField("invoiceClient");
        return;
      }
      if (!amount) {
        addMessage("assistant", `Got it, invoice for **${client}**. What's the amount?`);
        setPendingTask({ ...details, client });
        setPendingField("invoiceAmount");
        return;
      }

      // Show preview for approval
      const preview = `📄 **Draft Invoice**\n**Client:** ${client}\n**Amount:** £${amount.toFixed(2)}\n**Description:** ${rawInput}\n**Status:** Draft\n\nDoes this look right? Say **"save it"** to add to your invoices, or **"cancel"** to start over.`;
      addMessage("assistant", preview);
      setPendingTask({ ...details, client, amount, rawInput });
      setPendingField("invoiceApproval");
      return;
    }

    // 0c2b. Send booking link via email
    if (details.action === "send-booking-link") {
      const rawInput = details.rawInput || "";
      const toMatch = rawInput.match(/\bto\s+([A-Za-z][A-Za-z\s]+?)(?:\s+my|\s+a|\s+the|\s*$)/i);
      const recipientName = toMatch ? toMatch[1].trim() : null;

      // Try to find their email in contacts
      let recipientEmail = null;
      if (recipientName) {
        const match = contacts.find(c => c.name?.toLowerCase().includes(recipientName.toLowerCase()));
        recipientEmail = match?.email || null;
      }

      // THE REAL BOOKING LINK, not the retired prototype's (2026-10-04).
      //
      // This read event types out of localStorage via models/scheduling — the
      // Scheduling hub's browser-only store (the hub is gone; its default hours are
      // the Scheduler's Availability tab). Nothing could ever put a PERSISTED
      // event type there, so this branch always found none and always answered "I
      // couldn't find your scheduling link. Set up your Scheduling Hub first" —
      // sending her to configure something that cannot save, while she had a
      // working booking page all along.
      //
      // The live system is /api/meetings with her bookingUsername, which is what
      // MeetingTypeBuilder shows her and what the client portal links to.
      const bookingUrl = currentUser?.bookingUsername
        ? `${window.location.origin}/book/${currentUser.bookingUsername}`
        : null;

      if (!bookingUrl) {
        // The honest version: it is her booking USERNAME that is missing. It is set on
        // the Scheduler page, beside the booking link (SchedulerDashboard.jsx); Settings
        // has no such field, which is where this used to send her.
        addMessage("assistant", "You do not have a booking username yet, so there is no link to share. You can set one on the Scheduler page, and then I can send it for you.");
        return;
      }

      if (!recipientName) {
        addMessage("assistant", `I'll share your booking link. Who should I send it to?`);
        setPendingTask({ bookingUrl });
        setPendingField("bookingLinkRecipient");
        return;
      }

      const emailLine = recipientEmail
        ? `\n\nReady to send, [click here to open in your email app](mailto:${recipientEmail}?subject=${encodeURIComponent('Book a time with me')}&body=${encodeURIComponent(`Hi ${recipientName},\n\nFeel free to book a time that works for you:\n\n${bookingUrl}\n\nLooking forward to speaking!`)})`
        : `\n\nI don't have an email for ${recipientName}. What's their address?`;

      addMessage("assistant", `Here's your booking link to share with **${recipientName}**:\n\n📅 ${bookingUrl}${emailLine}`);

      if (!recipientEmail) {
        setPendingTask({ bookingUrl, recipientName });
        setPendingField("bookingLinkEmail");
      }
      return;
    }

    // 0c3a. Add attendee to existing meeting
    if (details.action === "add-attendee") {
      const personName = details.addPersonName;
      const meetingHint = details.addToMeetingTitle;

      if (!personName) {
        addMessage("assistant", "Who would you like to add? Just give me their name.");
        return;
      }

      // Look up contact email
      const contact = contacts.find(c => c.name?.toLowerCase().includes(personName.toLowerCase()));
      const email = contact?.email || null;

      // Find matching meeting
      const matchedMeeting = meetingHint
        ? userSchedule.find(t => t.title.toLowerCase().includes(meetingHint.toLowerCase()))
        : null;

      if (meetingHint && !matchedMeeting) {
        // Show upcoming meetings to pick from
        const upcoming = userSchedule
          .filter(t => t.taskType === 'Meeting' || /meeting|call|appointment/i.test(t.title || ''))
          .slice(0, 5);
        if (upcoming.length > 0) {
          const opts = upcoming.map(t => `• "${t.title}"${t.date ? ` on ${fmtDate(t.date)}` : ''}`).join('\n');
          addMessage("assistant", `I couldn't find a meeting called "${meetingHint}". Did you mean one of these?\n\n${opts}`);
        } else {
          addMessage("assistant", `I couldn't find a meeting called "${meetingHint}". Which meeting should I add ${personName} to?`);
        }
        setPendingTask({ ...details, personName, contact, email });
        setPendingField("addAttendeeMeetingTitle");
        return;
      }

      if (matchedMeeting) {
        const when = fmtDateTime(matchedMeeting.date, matchedMeeting.time);
        const emailLine = email
          ? `\n\nShall I draft an email invite for ${personName}?`
          : `\n\nDo you have an email address for ${personName} so I can send them an invite?`;
        addMessage("assistant", `Got it, adding ${personName} to "${matchedMeeting.title}"${when ? ` on ${when}` : ''}.${emailLine}`);
        setPendingTask({ ...details, meeting: matchedMeeting, contact, personName, email });
        setPendingField("attendeeEmailConfirm");
      } else {
        // No meeting hint at all — list upcoming meetings
        const upcoming = userSchedule
          .filter(t => t.taskType === 'Meeting' || /meeting|call|appointment/i.test(t.title || ''))
          .slice(0, 5);
        if (upcoming.length > 0) {
          const opts = upcoming.map(t => `• "${t.title}"${t.date ? ` on ${fmtDate(t.date)}` : ''}`).join('\n');
          addMessage("assistant", `Which meeting would you like to add ${personName} to?\n\n${opts}`);
        } else {
          addMessage("assistant", `Which meeting should I add ${personName} to?`);
        }
        setPendingTask({ ...details, personName, contact, email });
        setPendingField("addAttendeeMeetingTitle");
      }
      return;
    }

    // 0c3. Travel intent
    if (details.action === "travel") {
      addMessage("assistant", "I can help with travel planning. Tell me where you need to be and when, and I'll create a travel task with any relevant booking links. Want to schedule something now?");
      return;
    }

    // 0c. Email commands
    if (details.action === "email-command") {
      const rawInput = details.rawInput || details.title || "";

      // Check if user wants to draft/send a specific email
      const isDraft = /\b(draft|compose|write|send)\b/i.test(rawInput);
      const toMatch = rawInput.match(/\b(?:email|mail|message)\s+(?:to\s+)?([A-Za-z][A-Za-z\s]+?)(?:\s+about|\s+re:|\s+regarding|\s*$)/i);
      const aboutMatch = rawInput.match(/\b(?:about|re:|regarding)\s+(.+)/i);
      const recipientName = toMatch ? toMatch[1].trim() : null;
      const subjectHint = aboutMatch ? aboutMatch[1].trim() : null;

      if (isDraft && recipientName) {
        // Try to find email in contacts
        const recipientMatch = contacts.find(c => c.name?.toLowerCase().includes(recipientName.toLowerCase()));
        const recipientEmail = recipientMatch?.email || null;

        if (subjectHint) {
          const subjectLine = subjectHint.charAt(0).toUpperCase() + subjectHint.slice(1);
          addMessage("assistant", `Drafting an email to **${recipientName}** about "${subjectLine}". What should the email say? (Or say "generate" and I'll write a draft for you)`);
          setPendingTask({ ...details, emailTo: recipientEmail || recipientName, emailToName: recipientName, emailSubject: subjectLine });
          setPendingField("emailBody");
        } else {
          addMessage("assistant", `Sure, I'll draft an email to **${recipientName}**. What's it about?`);
          setPendingTask({ ...details, emailTo: recipientEmail || recipientName, emailToName: recipientName });
          setPendingField("emailSubject");
        }
        return;
      }

      if (isDraft) {
        addMessage("assistant", "Who is this email for?");
        setPendingTask(details);
        setPendingField("emailTo");
        return;
      }

      // General email help
      addMessage("assistant", "I can draft an email for you, send your booking link, or open your inbox. Just tell me what you need.");
      return;
    }

    // 0d. Contact management
    if (details.action === "contacts") {
      const rawInput = details.rawInput || details.title || "";
      const lower = rawInput.toLowerCase();

      // SHOW / LIST
      if (/show contacts|list contacts|all contacts/.test(lower)) {
        if (contacts.length === 0) {
          addMessage("assistant", "You have no contacts saved yet. Say \"add contact\" to create one.");
        } else {
          const lines = contacts.slice(0, 10).map(c => `• **${c.name}**${c.company ? ` (${c.company})` : ""}${c.email ? `, ${c.email}` : ""}${c.tags?.length ? ` [${c.tags.join(", ")}]` : ""}`).join("\n");
          addMessage("assistant", `You have ${contacts.length} contact${contacts.length !== 1 ? "s" : ""}:\n${lines}${contacts.length > 10 ? `\n…and ${contacts.length - 10} more. Open CRM for the full list.` : ""}`);
        }
        return;
      }

      // FIND / SEARCH
      const findMatch = rawInput.match(/find contact[s]?\s+(.+)/i) || rawInput.match(/search contact[s]?\s+(.+)/i) || rawInput.match(/show contact[s]?\s+(.+)/i);
      if (findMatch) {
        const q = findMatch[1].trim().toLowerCase();
        const found = contacts.filter(c =>
          c.name?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q) ||
          c.company?.toLowerCase().includes(q)
        );
        if (found.length === 0) {
          addMessage("assistant", `No contacts found matching "${findMatch[1].trim()}". Try a different name, email or company.`);
        } else {
          const lines = found.slice(0, 5).map(c => `• **${c.name}**${c.company ? ` · ${c.company}` : ""}${c.email ? ` · ${c.email}` : ""}${c.phone ? ` · ${c.phone}` : ""}${c.tags?.length ? ` [${c.tags.join(", ")}]` : ""}`).join("\n");
          addMessage("assistant", `Found ${found.length} match${found.length !== 1 ? "es" : ""}:\n${lines}`);
        }
        return;
      }

      // ADD / NEW
      if (/add contact|new contact|create contact/.test(lower)) {
        const nameMatch = rawInput.match(/(?:add|new|create)\s+contact\s+(.+)/i);
        const extractedName = nameMatch ? nameMatch[1].trim() : null;
        if (extractedName) {
          const saving = api('/api/contacts', {
            method: 'POST',
            body: JSON.stringify({
              name: extractedName,
              email: '', phone: '', company: '',
              tags: [], source: 'Aidey Chat', isNew: true,
              notes: '', interactions: [],
            }),
          }).then((res) => { window.dispatchEvent(new Event('aidey:contactsUpdated')); return res; });
          sayWhenSaved(saving, `✅ Contact **${extractedName}** added! You can add their email, phone and tags in the CRM.\n\nWould you like to open Contacts now?`, extractedName);
        } else {
          addMessage("assistant", "What's the name of the contact you'd like to add?");
          setPendingTask({ ...details, contactAction: "add" });
          setPendingField("contactName");
        }
        return;
      }

      // Fallback
      addMessage("assistant", "I can show your contacts, search for someone, or add a new one. What would you like to do?");
      return;
    }

    // 0e. Meeting notes → navigate to MeetingRecorder or inline paste
    if (details.action === "zoom-transcripts") {
      setZoomPanelOpen(true);
      addMessage('assistant', 'Opening your recent meeting transcripts, Zoom, Google Meet, and Teams tabs are available. Any recordings with transcripts from the last 7 days will appear.');
      return;
    }

    if (details.action === "meeting-notes") {
      addMessage("assistant",
        `Let's turn a meeting into tasks. Do you already have a transcript, or want to record / paste one now?`
      );
      setTimeout(() => setMeetingChoiceOpen(true), 600);
      return;
    }

    // Carla, 2026-10-22 (decision 75 split): Undo for your own items, a card whenever other people
    // are affected. This path acts without the server (the model gave no answer), so it cannot show
    // a card: a meeting with invitees, or one that is someone else's, is left alone, and said so.
    const othersIn = (t) => othersInItem(t, currentUser?.id);
    const leftAlone = (t, verb) => addMessage("assistant",
      `"${t.title}" has other people in it, so I have not ${verb} it here: that needs a card first. Ask me again in a moment, or open it on the Calendar.`);

    // 1. Move
    if (details.action === "move") {
      const toMove = userSchedule.find(
        t => details.title && t.title.toLowerCase().includes(details.title.toLowerCase())
      );
      if (!toMove) {
        addMessage("assistant", "Which meeting or task do you want to move?");
        setPendingTask(details);
        setPendingField("title");
        return;
      }
      if (othersIn(toMove)) { leftAlone(toMove, 'moved'); return; }
      if (!details.date && !details.time) {
        addMessage("assistant", `What date/time should I move "${toMove.title}" to?`);
        setPendingTask({ ...details, moveTaskId: toMove.id });
        setPendingField("date");
        return;
      }
      // Decision 75: moved straight away, with Undo in the toast; said only once it is saved.
      const when = fmtDateTime(details.date || toMove.date, details.time || toMove.time);
      Promise.resolve(onMoveTask(toMove.id, details.date, details.time, {
        onUndone: (r) => addMessage("assistant", r && r.ok
          ? `Undone. "${toMove.title}" is back where it was.`
          : `I could not undo moving "${toMove.title}". ${(r && r.error) || ''}`.trim()),
      })).then((r) => {
        addMessage("assistant", r && r.ok
          ? `Moved "${toMove.title}" to ${when}. Tap Undo below if that was wrong.`
          : `I could not move "${toMove.title}". ${(r && r.error && r.error !== 'not-found') ? r.error : 'Nothing was changed.'}`);
      });
      return;
    }

    // 2. Cancel
    if (details.action === "cancel") {
      const toCancel = userSchedule.find(
        t => details.title && t.title.toLowerCase().includes(details.title.toLowerCase())
      );
      if (!toCancel) {
        addMessage("assistant", "Which meeting or task do you want to cancel?");
        setPendingTask(details);
        setPendingField("title");
        return;
      }
      if (othersIn(toCancel)) { leftAlone(toCancel, 'cancelled'); return; }
      // Decision 75: gone straight away; the shared delayed delete holds the DELETE for its Undo.
      const cancelled = onCancelTask(toCancel.id, {
        onRestored: ({ undone, error } = {}) => addMessage("assistant", undone
          ? `Undone. "${toCancel.title}" is back.`
          : `"${toCancel.title}" could not be cancelled, so it is back on your list. ${messageFromError(error, '')}`.trim()),
      });
      addMessage("assistant", cancelled
        ? `Cancelled "${toCancel.title}". Tap Undo below if that was wrong.`
        : `I could not find "${toCancel.title}" to cancel. Nothing was changed.`);
      return;
    }

    // 3. Create (default)
    // If no structured task/meeting info was detected, treat as general chat → AI
    const looksLikeTask = details.title || details.taskType || details.date || details.time;
    if (!looksLikeTask) {
      addLoadingMessage();
      // Build conversation history for context (full history, no slice)
      const historyForAI = chatMessages
        .filter(m => (m.role === 'user' || m.role === 'assistant') && !m.isTyping)
        // historyContent ?? content — see the TRIGGER note at the first of these
        // three sites: the fallback is safe only while no public-intake contact exists.
        .map(m => ({ role: m.role, content: m.historyContent ?? m.content, refs: m.refs }));
      // Replace trailing "…" placeholder with real reply
      askAidey(historyForAI, {}).then((data) => {
        const replyActs = runReplyActions(data);   // as in handleChatSubmit: actions run, never shown
        const reply = data?.reply?.trim() || replyActs?.line || null;
        recordTurnAreas(data?.areas);   // C-b: the history tag, as in handleChatSubmit
        if (data?.needsConfirmation) {
          const nc = data.needsConfirmation;
          if (nc.type === 'workload_redistribution') {
            const cardId = `workload-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
            setChatMessages(prev => [
              ...prev.slice(0, -1),
              ...(reply ? [{ role: 'assistant', content: reply, ts: Date.now() }] : []),
              { role: 'workload_redistribution', suggestions: nc.suggestions || [], confirmToken: nc.confirmToken, cardId, ts: Date.now() + 1 },
            ]);
          } else if (nc.type === 'absence_delegation') {
            const cardId = `absence-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
            setChatMessages(prev => [
              ...prev.slice(0, -1),
              ...(reply ? [{ role: 'assistant', content: reply, ts: Date.now() }] : []),
              { role: 'absence_delegation', absenceData: nc.absenceData, leaveRequestId: nc.input?.leave_request_id, cardId, ts: Date.now() + 1 },
            ]);
          } else {
            setPendingToolConfirmation(nc);
            const cardId = `approval-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
            setChatMessages(prev => [
              ...prev.slice(0, -1),
              ...(reply ? [{ role: 'assistant', content: reply, ts: Date.now() }] : []),
              { role: 'approval', approvalData: nc, cardId, ts: Date.now() + 1 },
            ]);
          }
        } else if (data?.emailDraft) {
          const cardId = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          setChatMessages(prev => [
            ...prev.slice(0, -1),
            ...(reply ? [{ role: 'assistant', content: reply, ts: Date.now() }] : []),
            { role: 'email_draft', emailDraft: data.emailDraft, cardId, ts: Date.now() + 1 },
          ]);
        } else {
          setChatMessages(prev => [
            ...prev.slice(0, -1),
            { role: 'assistant', content: reply || "I didn't quite catch that. I can book meetings, sort tasks, draft emails and invoices, or dig through your contacts. What do you need?", ts: Date.now() },
          ]);
        }
      });
      return;
    }

    // Step-by-step field collection for essential fields first
    const essentialFields = [
      { key: "title", prompt: "What should I call this meeting or task?" },
      { key: "date", prompt: "Which date should I schedule this for?" },
      { key: "time", prompt: "What time works best for you?" },
    ];

    // Check for missing essential fields first
    for (const field of essentialFields) {
      if (!details[field.key]) {
        setPendingTask(details);
        setPendingField(field.key);
        addMessage("assistant", field.prompt);
        return;
      }
    }

    // CONFLICT CHECK
    const conflict = findConflict(details, userSchedule, googleEvents, icalEvents, outlookEvents);
    if (conflict) {
      const nextSlotResult = findNextAvailable(details, userSchedule, details.durationMinutes);
      const prioritySwap = suggestPrioritySwap(conflict, details.priority || "Medium");

      const slotLabel = nextSlotResult
        ? (nextSlotResult.dayOffset > 0
          ? `${fmtDate(nextSlotResult.date)} at ${fmtTime(nextSlotResult.time)}`
          : fmtTime(nextSlotResult.time))
        : null;

      let conflictMsg = `You've already got "${conflict.title}" at ${fmtTime(details.time)} on ${fmtDate(details.date)}.`;

      if (slotLabel) {
        conflictMsg += ` The next free slot is ${slotLabel}, want me to book it there instead?`;
        if (prioritySwap) {
          conflictMsg += ` I can also move "${conflict.title}" out of the way if you need ${fmtTime(details.time)} specifically.`;
        }
        conflictMsg += ` Or say "book both" to keep them at the same time.`;
      } else {
        conflictMsg += ` Want to pick a different time, or book it anyway?`;
      }

      addMessage("assistant", conflictMsg);
      setPendingTask({
        ...details,
        suggestion: nextSlotResult?.time,
        suggestionDate: nextSlotResult?.date,
        conflictingEvent: conflict,
      });
      setPendingField("resolveConflict");
      return;
    }

    // Check if this is a meeting and needs meeting mode specification
    const isMeeting = details.title && (
      details.title.toLowerCase().includes('meeting') ||
      details.title.toLowerCase().includes('call') ||
      details.title.toLowerCase().includes('appointment')
    );

    if (isMeeting && !details.hasOwnProperty('meetingMode')) {
      setPendingTask(details);
      setPendingField("meetingMode");
      addMessage("assistant", "Is this meeting in person or online?");
      return;
    }

    // Handle online meeting platform selection
    if (details.meetingMode === "online" && !details.hasOwnProperty('meetingType')) {
      // ✅ Check user's default meeting type preference first
      const userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");
      const defaultMeetingType = userProfile?.preferences?.defaultMeetingType;

      if (defaultMeetingType && defaultMeetingType.trim() !== "") {
        // Use the user's default meeting type
        details.meetingType = defaultMeetingType;
        addMessage("assistant", `Using your default meeting platform: ${defaultMeetingType}`);
        // Continue processing with the default meeting type
      } else {
        // No default set, ask the user
        setPendingTask(details);
        setPendingField("meetingType");
        addMessage("assistant", "Which platform do you want to use for the online meeting? (Zoom, Google Meet, Teams, Other)");
        return;
      }
    }

    // Handle location based on meeting mode
    if (details.meetingMode === "online" && !details.hasOwnProperty('location')) {
      setPendingTask(details);
      setPendingField("location");

      // Provide specific instructions based on meeting type
      if (details.meetingType === "Google Meet") {
        if (isGoogleConnected()) {
          addMessage("assistant", `Would you like to generate a ${details.meetingType} link or paste your own? (Say 'generate' or paste the link)`);
        } else {
          addMessage("assistant", `To auto-generate Google Meet links, you need to connect your Google account first. You can paste a meeting link manually, or type 'generate' after connecting Google Calendar.`);
        }
      } else {
        addMessage("assistant", `Would you like to generate a ${details.meetingType || "meeting"} link or paste your own? (Say 'generate' or paste the link)`);
      }
      return;
    }

    if (details.meetingMode === "in person" && !details.hasOwnProperty('location')) {
      setPendingTask(details);
      setPendingField("location");
      addMessage("assistant", "What is the address or room for the meeting?");
      return;
    }

    // Apply defaults silently — only ask if the user explicitly set these in the original message
    // No priority default: a task with none takes her usual one on the server (Aidey Backend/lib/learntDefaults.js).
    if (!details.hasOwnProperty('reminderOffset')) details.reminderOffset = 15;
    if (!details.hasOwnProperty('details')) details.details = "";

    // Delegate / Assign - Handle task delegation and mentions
    if (details.assignedTo) {
      if (Array.isArray(details.assignedTo)) {
        // Handle @mentions (array of user IDs)
        details.assignedTeam = details.assignedTo;
        details.assignedUsers = details.assignedTo.map(userId => rosterMap.get(userId)).filter(Boolean);
      } else {
        // Handle traditional text assignment
        details.assignedTeam = [details.assignedTo];
      }
      delete details.assignedTo;
    }

    // Handle attendees for meetings
    if (details.attendees && Array.isArray(details.attendees)) {
      details.meetingAttendees = details.attendees.map(userId => rosterMap.get(userId)).filter(Boolean);
    }

    // Add creator information
    details.createdBy = currentUser.id;
    details.createdByUser = currentUser;

    // Apply consistent field fallbacks before task creation
    if (!details.reminderOffset && details.reminderOffset !== null) details.reminderOffset = 15;
    if (!details.details) details.details = "";
    if (details.location === undefined) details.location = "";

    // Normalise both field formats so every view can find this task:
    // • datetime + deadline  (used by tasksForDate, sortTasksByUrgency, WeekView)
    // • date + time          (used by CalendarView, getAllTasksForDate, SuperAideyChat)
    if (details.date && details.time && !details.datetime) {
      details.datetime = new Date(`${details.date}T${details.time}`).toISOString();
    }
    if (details.datetime) {
      const dt = new Date(details.datetime);
      details.date = toLocalDateStr(dt);
      details.time = dt.toTimeString().slice(0, 5);
      if (!details.deadline) {
        const durationMs = (details.durationMinutes || 60) * 60 * 1000;
        details.deadline = new Date(dt.getTime() + durationMs).toISOString();
      }
    }

    // All required fields present — create task! The app answers { ok } once it has saved; the
    // "Scheduled" line waits for that (release 7: it was said before, and after a failed save too).
    const taskSaved = Promise.resolve(onCreateTask(details, (message, error) => {
      if (error) {
        addMessage("assistant", `❌ Failed to create task: ${error}`);
        return;
      } else if (message) {
        addMessage("assistant", message);
      }
    }));

    // Enhanced confirmation message
    let confirmationMessage = `✅ Scheduled: "${details.title}" for ${fmtDateTime(details.date, details.time)}`;
    if (details.meetingMode === "online" && details.meetingType) {
      confirmationMessage += ` (${details.meetingType} meeting)`;
    } else if (details.meetingMode === "in person" && details.location) {
      confirmationMessage += ` at ${details.location}`;
    }

    // Add assignment/attendee information
    if (details.assignedUsers && details.assignedUsers.length > 0) {
      const assignedNames = details.assignedUsers.map(user => user.name).join(", ");
      confirmationMessage += `\n👥 Assigned to: ${assignedNames}`;
    }

    if (details.meetingAttendees && details.meetingAttendees.length > 0) {
      const attendeeNames = details.meetingAttendees.map(user => user.name).join(", ");
      confirmationMessage += `\n📧 Attendees: ${attendeeNames}`;
    }

    taskSaved.then((out) => { if (out && out.ok === true) addMessage("assistant", confirmationMessage); }).catch(() => {});

    // Auto-create travel task for in-person meetings (only if user said they need to travel)
    if (shouldAutoCreateTravelTask(details, currentUser?.preferences?.travel) && onCreateTask) {
      try {
        // FROM THE ACCOUNT (2026-10-04). This read localStorage['aidey_travel_prefs'],
        // a key NOTHING WRITES — Settings → Travel saves to preferences.travel on the
        // server, which is what Briefing/BriefingCard.jsx:161 reads. So her travel
        // buffer was ignored here and the hard-coded 60 minutes was always used.
        const travelPrefs = currentUser?.preferences?.travel || {};
        const info = details.travelInfo;
        const mode = details.travelMode || info?.recommendedMode || 'driving';
        const modeEmoji = MODE_EMOJI[mode] || '🚗';
        // Use real travel time if available, otherwise pref/default
        const bufferMins = info?.bufferMins || travelPrefs.travelBuffer || 60;
        const meetingDt = new Date(details.datetime || `${details.date}T${details.time || '09:00'}`);
        const travelStart = new Date(meetingDt.getTime() - bufferMins * 60 * 1000);

        const travelPrefsFlightLabel = { 'economy': 'Economy', 'premium-economy': 'Premium economy', 'business': 'Business class', 'first': 'First class' }[travelPrefs.flightClass] || '';
        const travelPrefsHotelLabel = { 'budget': 'Budget', 'mid-range': 'Mid-range', 'premium': 'Premium', 'luxury': 'Luxury / 5-star' }[travelPrefs.hotelTier] || '';

        const links = details.travelFrom
          ? getBookingLinks(details.travelFrom, details.location, mode)
          : null;

        let travelNotes = `${modeEmoji} Travel to: ${details.location}`;
        if (details.travelFrom) travelNotes += `\n📍 From: ${details.travelFrom}`;
        if (info) travelNotes += `\n🗺 Distance: ${info.distanceText}, ${info.travelMins} mins`;
        travelNotes += `\n📅 For: "${details.title}"\n⏰ Arrive by: ${formatTaskClock(meetingDt)}`;
        if (travelPrefsFlightLabel && mode === 'flight') travelNotes += `\n✈️ Preferred class: ${travelPrefsFlightLabel}`;
        if (travelPrefs.seatPreference && travelPrefs.seatPreference !== 'no-preference' && mode === 'flight') travelNotes += `\n💺 Seat: ${travelPrefs.seatPreference}`;
        if (travelPrefsHotelLabel && (info?.needsHotel || mode === 'flight')) travelNotes += `\n🏨 Hotel preference: ${travelPrefsHotelLabel}`;
        if (travelPrefs.hotelChain && (info?.needsHotel || mode === 'flight')) travelNotes += ` (${travelPrefs.hotelChain})`;
        if (links?.primary) travelNotes += `\n🔗 ${links.primary.label}: ${links.primary.url}`;
        if (links?.secondary) travelNotes += `\n🔗 ${links.secondary.label}: ${links.secondary.url}`;
        if (links?.hotels && (info?.needsHotel || mode === 'flight')) travelNotes += `\n🔗 ${links.hotels.label}: ${links.hotels.url}`;
        if (travelPrefs.notes) travelNotes += `\n📝 ${travelPrefs.notes}`;

        const travelTask = {
          title: `${modeEmoji} Travel to ${details.location}`,
          date: toLocalDateStr(travelStart),
          time: travelStart.toTimeString().slice(0, 5),
          datetime: travelStart.toISOString(),
          deadline: meetingDt.toISOString(),
          durationMinutes: bufferMins,
          priority: 'High',
          category: 'Travel',
          notes: travelNotes,
        };

        onCreateTask(travelTask, () => { });
      } catch { /* silently skip */ }
    }

    // Follow-up prompt — set pendingField so "no thanks" is handled and doesn't re-trigger task creation
    if (isMeeting) {
      setTimeout(() => {
        setPendingField("followUp");
        setPendingTask(details);
        addMessage("assistant", "Anything else for this one, follow-up task, calendar invite, or are we all good?");
      }, details.noTravel ? 400 : 900);
    }

    // Note: Additional confirmation message is handled by the onCreateTask callback
  }

  // === FIELD STEP HANDLER ===
  async function handleFieldInput(input) {
    // Always add the user's input as a visible message first
    addMessage("user", input);

    // Allow users to cancel or start over at any time
    if (/^(cancel|stop|abort|start\s*over|never\s*mind|exit)$/i.test(input.trim())) {
      setPendingTask(null);
      setPendingField(null);
      addMessage("assistant", "Cancelled. Nothing's been changed.");
      return;
    }

    const task = { ...pendingTask };
    let proceed = false;

    if (pendingField === "title") {
      task.title = input.trim();
      proceed = true;
    } else if (pendingField === "date") {
      // Accept various date formats
      if (input.toLowerCase().includes("today")) {
        task.date = getTodayISO();
      } else if (input.toLowerCase().includes("tomorrow")) {
        task.date = getTomorrowISO();
      } else {
        task.date = input.trim();
      }
      proceed = true;
    } else if (pendingField === "time") {
      let t = input.trim();
      // Handle various time formats
      const timeMatch = t.match(/(\d{1,2})(?:[.:](\d{2}))?\s*(am|pm)?/i);
      if (timeMatch) {
        let hours = parseInt(timeMatch[1], 10);
        let minutes = timeMatch[2] ? parseInt(timeMatch[2], 10) : 0;
        if (/pm/i.test(timeMatch[3]) && hours < 12) hours += 12;
        if (/am/i.test(timeMatch[3]) && hours === 12) hours = 0;

        // Apply same logic: Assume PM for ambiguous late-hour times (6-11) unless specified
        if (!timeMatch[3] && hours >= 6 && hours <= 11) {
          hours += 12; // Assume PM
        }

        t = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
      } else if (/^\d{1,2}$/.test(t)) {
        let hours = parseInt(t, 10);
        // Apply same logic for single digit hours
        if (hours >= 6 && hours <= 11) {
          hours += 12; // Assume PM
        }
        t = hours.toString().padStart(2, "0") + ":00";
      }
      task.time = t;
      proceed = true;
    } else if (pendingField === "priority") {
      if (/high/i.test(input)) task.priority = "High";
      else if (/low/i.test(input)) task.priority = "Low";
      else if (/medium/i.test(input)) task.priority = "Medium";
      else if (/^no|none|skip$/i.test(input.trim())) delete task.priority; // none chosen: her usual one on the server (lib/learntDefaults), else Medium
      else {
        addMessage("assistant", "Please say 'high', 'medium', or 'low' priority (or 'skip' for medium).");
        return;
      }
      proceed = true;
    } else if (pendingField === "reminderOffset") {
      if (/^no|none|skip$/i.test(input.trim())) {
        task.reminderOffset = null; // No reminder
      } else if (!isNaN(parseInt(input))) {
        task.reminderOffset = parseInt(input);
      } else {
        addMessage("assistant", "I didn't get that. How many minutes before do you want to be reminded? (or say 'none' to skip)");
        return;
      }
      proceed = true;
    } else if (pendingField === "contactName") {
      const name = input.trim();
      const saving = api('/api/contacts', {
        method: 'POST',
        body: JSON.stringify({
          name,
          email: '', phone: '', company: '',
          tags: [], source: 'Aidey Chat', isNew: true,
          notes: '', interactions: [],
        }),
      }).then((res) => { window.dispatchEvent(new Event('aidey:contactsUpdated')); return res; });
      sayWhenSaved(saving, `✅ Contact **${name}** added! You can add their email, phone and tags in the CRM.`, name);
      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === "details") {
      if (/^no|none|skip$/i.test(input.trim())) {
        task.details = "";
      } else {
        task.details = input.trim();
      }
      proceed = true;
    } else if (pendingField === "writingInput") {
      if (/^(no|none|skip|cancel)$/i.test(input.trim())) {
        addMessage("assistant", "Cancelled.");
        setPendingTask(null);
        setPendingField(null);
        return;
      }
      const writingText = input.trim();
      // Silently analyse the user's writing sample to build learned patterns
      analyseWritingSample(writingText, currentUser?.id);
      addLoadingMessage();
      const _styleProfile = loadStyleProfile(currentUser);
      const _patterns = loadLearnedPatterns(currentUser?.id);
      const _systemPrompt = buildStyleSystemPrompt(_styleProfile, _patterns);
      setPendingTask(null);
      setPendingField(null);
      const _writingHistory = [
        ...chatMessages
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .slice(-12)
          // historyContent ?? content — see the TRIGGER note at the first of these
        // three sites: the fallback is safe only while no public-intake contact exists.
        .map((m) => ({ role: m.role, content: m.historyContent ?? m.content, refs: m.refs })),
        { role: 'user', content: writingText },
      ];
      askAidey(_writingHistory, { system: _systemPrompt }).then((data) => {
        const suggestion = data?.reply?.trim() || null;
        setChatMessages(prev => [
          ...prev.slice(0, -1),
          { role: 'assistant', content: suggestion || "I wasn't able to get a suggestion right now, try again in a moment.", ts: Date.now() },
        ]);
      });
      return;
    } else if (pendingField === "contentBrief") {
      if (/^no|none|skip$/i.test(input.trim())) {
        addMessage("assistant", "Cancelled.");
        setPendingTask(null);
        setPendingField(null);
        return;
      }
      const contentType = task.contentType || "content";
      const brief = input.trim();
      addLoadingMessage();
      const _styleProfile2 = loadStyleProfile(currentUser);
      const _patterns2 = loadLearnedPatterns(currentUser?.id);
      const _systemPrompt2 = buildStyleSystemPrompt(_styleProfile2, _patterns2);
      setPendingTask(null);
      setPendingField(null);
      const _contentHistory = [
        ...chatMessages
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .slice(-12)
          // historyContent ?? content — see the TRIGGER note at the first of these
        // three sites: the fallback is safe only while no public-intake contact exists.
        .map((m) => ({ role: m.role, content: m.historyContent ?? m.content, refs: m.refs })),
        { role: 'user', content: `Help me with this ${contentType}: ${brief}` },
      ];
      askAidey(_contentHistory, { system: _systemPrompt2 }).then((data) => {
        const suggestion = data?.reply?.trim() || null;
        setChatMessages(prev => [
          ...prev.slice(0, -1),
          { role: 'assistant', content: suggestion || `I wasn't able to fetch suggestions right now. Want me to create a task to track your ${contentType}?`, ts: Date.now() },
        ]);
      });
      return;
    } else if (pendingField === "createContentTask") {
      if (/^yes|yeah|ok|sure|create|yep/i.test(input.trim())) {
        const contentTask = {
          ...task,
          action: "create",
          title: `Draft ${task.contentType || "content"}: ${(task.details || "").slice(0, 40)}`,
          date: getTodayISO(),
          time: "09:00",
        };
        delete contentTask.contentBrief;
        setPendingTask(null);
        setPendingField(null);
        handleSingleAction(contentTask);
      } else {
        addMessage("assistant", "Cancelled.");
        setPendingTask(null);
        setPendingField(null);
      }
      return;
    } else if (pendingField === "openEmail") {
      if (/^yes|yeah|ok|sure|open|yep/i.test(input.trim())) {
        addMessage("assistant", "Opening Email Dashboard... Click 'Email' in the sidebar to get there.");
      } else {
        addMessage("assistant", "Cancelled.");
      }
      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === "location") {
      if (/^no|none|skip$/i.test(input.trim())) {
        task.location = "";
      } else if (/^generate/i.test(input) && task.meetingType) {
        if (task.meetingType === "Google Meet") {
          // Check if Google is connected
          if (!isGoogleConnected()) {
            addMessage("assistant", "To generate Google Meet links, you need to connect your Google account first. Please connect Google Calendar from the main dashboard, then try again.");
            setPendingTask(task);
            setPendingField("location");
            return;
          }

          // Check if Google Meet API is available
          if (!isGoogleMeetAvailable()) {
            addMessage("assistant", "Google Meet API is not available. Please make sure Google Calendar is properly connected and try again.");
            setPendingTask(task);
            setPendingField("location");
            return;
          }

          addMessage("assistant", `Generating Google Meet link...`);

          // Create Google Meet meeting
          const meetingDetails = formatMeetingForGoogleMeet(task);

          createGoogleMeet(meetingDetails)
            .then((result) => {
              if (result.success && result.meetLink) {
                task.location = result.meetLink;
                task.googleMeetId = result.meetingId;
                addMessage("assistant", `✅ Google Meet link generated! Your meeting link: ${result.meetLink}`);
                setPendingTask(task);
                setPendingField(null);
                handleSingleAction(task);
              } else {
                addMessage("assistant", "Failed to generate Google Meet link. Please paste your meeting link manually.");
                setPendingTask(task);
                setPendingField("location");
              }
            })
            .catch((error) => {
              console.error('Google Meet creation error:', error);
              addMessage("assistant", `Error generating Google Meet link: ${error.message}. Please paste your meeting link manually.`);
              setPendingTask(task);
              setPendingField("location");
            });
          return;
        } else if (task.meetingType === "Teams") {
          // Check if Teams is connected
          if (!isTeamsConnected()) {
            addMessage("assistant", "To generate Microsoft Teams links, you need to connect your Microsoft Outlook account first. Please connect Outlook from the Profile section, then try again.");
            setPendingTask(task);
            setPendingField("location");
            return;
          }

          addMessage("assistant", `Generating Microsoft Teams link...`);

          // Create Teams meeting
          const meetingDetails = {
            title: task.title,
            date: task.date,
            time: task.time,
            duration: 60, // Default 1 hour
            details: task.details || `Microsoft Teams meeting for: ${task.title}`
          };

          createTeamsMeeting(meetingDetails)
            .then((result) => {
              if (result.success && result.meetingLink) {
                task.location = result.meetingLink;
                task.teamsEventId = result.eventId;
                addMessage("assistant", `✅ Microsoft Teams link generated! Your meeting link: ${result.meetingLink}`);
                setPendingTask(task);
                setPendingField(null);
                handleSingleAction(task);
              } else {
                addMessage("assistant", `Failed to generate Teams link: ${result.error || 'Unknown error'}. Please paste your meeting link manually.`);
                setPendingTask(task);
                setPendingField("location");
              }
            })
            .catch((error) => {
              console.error('Teams meeting creation error:', error);
              addMessage("assistant", `Error generating Teams link: ${error.message}. Please paste your meeting link manually.`);
              setPendingTask(task);
              setPendingField("location");
            });
          return;
        } else {
          // For other meeting types (custom, etc.)
          addMessage("assistant", `Generating a ${task.meetingType} link...`);
          // TODO: Integrate other meeting platforms here
          setTimeout(() => {
            addMessage("assistant", `Sorry, auto-generation for ${task.meetingType} isn't set up yet. Please paste your meeting link.`);
            setPendingTask(task);
            setPendingField("location");
          }, 1000);
          return;
        }
      } else if (/\b(online|virtual|remote|zoom|google\s*meet|teams|video\s*call|video\s*meeting)\b/i.test(input)) {
        // User is clarifying it's an online meeting — switch mode and ask for link
        task.meetingMode = "online";
        delete task.travelFrom; // no travel needed
        if (/zoom/i.test(input)) task.meetingType = "Zoom";
        else if (/google\s*meet/i.test(input)) task.meetingType = "Google Meet";
        else if (/teams/i.test(input)) task.meetingType = "Teams";
        else if (!task.meetingType) task.meetingType = "Other";
        const platform = task.meetingType || "meeting";
        setPendingTask(task);
        setPendingField("location");
        addMessage("assistant", `Got it, switching to an online ${platform}. Would you like to generate a ${platform} link or paste your own?`);
        return;
      } else {
        task.location = input.trim();
      }
      // For in-person meetings, ask where they're travelling from before creating
      if (task.meetingMode === "in person" && !task.hasOwnProperty('travelFrom')) {
        setPendingTask(task);
        setPendingField("travelFrom");
        addMessage("assistant", `Got it, ${task.location}. Will you need to travel to get there? If so, where are you heading from? (or say 'local' if you're already nearby)`);
        return;
      }
      proceed = true;
    } else if (pendingField === "travelFrom") {
      if (/^(?:local|nearby|no|nope|skip|close|already\s+there|I.?m\s+close)/i.test(input.trim())) {
        task.travelFrom = null;
        task.noTravel = true;
        proceed = true;
      } else if (/^walk/i.test(input.trim())) {
        task.travelFrom = null;
        task.travelMode = "walking";
        task.noTravel = true;
        proceed = true;
      } else {
        // Has a from-location — look up real travel time
        task.travelFrom = input.trim();
        addMessage("assistant", "Let me check travel times for you…");
        setPendingTask(task);
        setPendingField("arrangeTravel"); // hold here while async runs

        getTravelInfo(task.travelFrom, task.location).then(info => {
          task.travelInfo = info || null;

          if (info) {
            const { distanceKm, recommendedMode, travelMins, needsHotel, driving, transit } = info;
            const modeLabel = MODE_LABEL[recommendedMode] || recommendedMode;
            const modeEmoji = MODE_EMOJI[recommendedMode] || '🚗';
            // Pick the best duration text to show
            const byTrain = transit?.durationText;
            const byCar = driving?.durationText;
            let timeStr = '';
            if (recommendedMode === 'train' && byTrain) timeStr = `${byTrain} by train`;
            else if (recommendedMode === 'driving' && byCar) timeStr = `${byCar} by car`;
            else if (byTrain || byCar) timeStr = `${byTrain || byCar}`;
            else timeStr = `about ${travelMins} mins`;

            let msg = `${modeEmoji} ${task.travelFrom} to ${task.location} is ${timeStr} (${distanceKm} km). I'd suggest ${modeLabel}.`;
            if (needsHotel) msg += ` That's quite a distance, you may want to consider staying overnight.`;
            msg += `\n\nWould you like me to arrange the travel?`;

            // Check for same-day in-person meetings at other locations
            const clusterMatches = findSameDayInPersonMeetings(task, userSchedule);
            if (clusterMatches.length > 0) {
              const otherLocations = [...new Set(clusterMatches.map(t => t.location))].join(' and ');
              msg += `\n\n💡 You also have an in-person meeting at ${otherLocations} on the same day. Grouping them closer together could cut your travel costs, want me to suggest a better schedule?`;
            }

            setPendingTask({ ...task, travelInfo: info });
            addMessage("assistant", msg);
          } else {
            // API unavailable — ask manually
            setPendingTask(task);
            setPendingField("travelMode");
            addMessage("assistant", `How are you planning to get there, driving, train, flight, or taxi?`);
          }
        });
        return; // wait for async
      }
    } else if (pendingField === "travelMode") {
      // Fallback when no API data — user picks mode manually
      if (/fly|flight|plane|air/i.test(input)) task.travelMode = "flight";
      else if (/train|rail|tube|underground|metro/i.test(input)) task.travelMode = "train";
      else if (/coach|bus/i.test(input)) task.travelMode = "bus";
      else if (/driv|car/i.test(input)) task.travelMode = "driving";
      else if (/taxi|uber|cab/i.test(input)) task.travelMode = "taxi";
      else if (/walk/i.test(input)) task.travelMode = "walking";
      else task.travelMode = input.trim();
      proceed = true;
    } else if (pendingField === "arrangeTravel") {
      // User responds to "Would you like me to arrange the travel?"
      const answer = input.trim().toLowerCase();
      const info = task.travelInfo;
      const mode = task.travelMode || info?.recommendedMode || "driving";

      if (/^(?:no|nope|no\s+thanks|nah|i.?ll\s+sort|i.?ll\s+handle)/i.test(answer)) {
        // User will sort it themselves
        if (info) task.travelMode = info.recommendedMode;
        addMessage("assistant", `No problem, I'll block out ${info ? info.travelMins + ' mins' : 'travel time'} in your calendar.`);
        proceed = true;
      } else if (/driv|car/i.test(answer)) {
        task.travelMode = "driving";
        addMessage("assistant", `Got it, I'll block out ${info?.driving?.durationText || 'driving time'} for the journey.`);
        proceed = true;
      } else if (/walk/i.test(answer)) {
        task.travelMode = "walking";
        task.noTravel = true;
        proceed = true;
      } else if (/^(?:yes|yeah|yep|sure|please|ok|go\s+ahead|arrange)/i.test(answer)) {
        // Arrange it — build booking links and notes
        task.travelMode = mode;
        const links = getBookingLinks(task.travelFrom || '', task.location, mode);
        const modeEmoji = MODE_EMOJI[mode] || '🚗';
        const modeLabel = MODE_LABEL[mode] || mode;
        const bufferMins = info?.bufferMins || 75;

        let msg = `${modeEmoji} All sorted! I've blocked out ${bufferMins} mins for travel by ${modeLabel}.\n\n`;
        if (links.primary) msg += `📌 ${links.primary.label}: ${links.primary.url}\n`;
        if (links.secondary) msg += `📌 ${links.secondary.label}: ${links.secondary.url}\n`;
        if (info?.needsHotel && links.hotels) msg += `🏨 ${links.hotels.label}: ${links.hotels.url}\n`;
        msg = msg.trim();

        addMessage("assistant", msg);
        proceed = true;
      } else {
        // Didn't recognise — reprompt
        addMessage("assistant", `Just say yes to let me help, or tell me how you're getting there (driving, train, etc.)`);
        return;
      }
    } else if (pendingField === "followUp") {
      setPendingTask(null);
      setPendingField(null);
      if (/yes|sure|ok|follow.?up|calendar|invite/i.test(input)) {
        addMessage("assistant", "What should the follow-up be? Just tell me and I'll get it set up.");
      } else {
        addMessage("assistant", "All sorted! Let me know if there's anything else you need.");
      }
      return;
    } else if (pendingField === "meetingMode") {
      if (/online/i.test(input)) {
        task.meetingMode = "online";
        setPendingTask(task);
        setPendingField("meetingType");
        addMessage("assistant", "Which platform do you want to use for the online meeting? (Zoom, Google Meet, Teams, Other)");
        return;
      } else if (/in\s*person|office|address|room/i.test(input)) {
        task.meetingMode = "in person";
        setPendingTask(task);
        setPendingField("location");
        addMessage("assistant", "What is the address or room for the meeting?");
        return;
      } else if (/^yes|ok|sure$/i.test(input.trim())) {
        // If user just says "yes", assume they want online meeting (most common)
        task.meetingMode = "online";
        setPendingTask(task);
        setPendingField("meetingType");
        addMessage("assistant", "Great! Which platform do you want to use for the online meeting? (Zoom, Google Meet, Teams, Other)");
        return;
      } else {
        addMessage("assistant", "Please say 'in person' or 'online'.");
        return;
      }
    } else if (pendingField === "meetingType") {
      if (/zoom/i.test(input)) task.meetingType = "Zoom";
      else if (/google\s*meet|meet/i.test(input)) task.meetingType = "Google Meet";
      else if (/teams/i.test(input)) task.meetingType = "Teams";
      else task.meetingType = "Other";
      setPendingTask(task);
      setPendingField("location");
      addMessage("assistant", `Would you like to generate a ${task.meetingType} link or paste your own? (Say 'generate' or paste the link)`);
      return;
    } else if (pendingField === "resolveConflict") {
      const ans = input.trim();

      // Extract a specific time if the user named one ("move it to 3pm", "how about 4:30")
      const timeInAnswer = (() => {
        const m = ans.match(/\b(\d{1,2})(?:[.:h](\d{2}))?\s*(am|pm)\b/i)
          || ans.match(/\bmove\b.+\b(\d{1,2})(?:[.:h](\d{2}))?\s*(am|pm)?\b/i);
        if (!m) return null;
        let h = parseInt(m[1]), min = m[2] ? parseInt(m[2]) : 0;
        if (/pm/i.test(m[3]) && h < 12) h += 12;
        if (/am/i.test(m[3]) && h === 12) h = 0;
        if (!m[3] && h >= 1 && h <= 7) h += 12; // assume pm for ambiguous early afternoon
        return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
      })();

      const wantsOverride = /book\s+both|same\s+time|anyway|go\s+ahead|just\s+(do|book|create|add)\s+it|override|double.?book/i.test(ans);
      const wantsSwap = /move\s+(the\s+)?(other|existing|first|original|old)|swap(\s+them)?|shift\s+(it|the\s+other)/i.test(ans);
      const wantsReschedule = timeInAnswer
        || /^(yes|yeah|yep|sure|ok|that\s+works?|sounds?\s+good|perfect|great|go\s+for\s+it|move\s+it|reschedule)/i.test(ans)
        || /\b(book\s+it\s+(there|at\s+that|in\s+that|then)|use\s+(that\s+)?(slot|time|one)|put\s+it\s+there|that\s+slot|that\s+time)\b/i.test(ans);

      if (wantsOverride) {
        delete task.suggestion; delete task.suggestionDate; delete task.conflictingEvent;
        addMessage("assistant", `Done, I've booked it in alongside the existing meeting.`);
        proceed = true;

      } else if (timeInAnswer) {
        // User gave an explicit time
        task.time = timeInAnswer;
        delete task.suggestion; delete task.suggestionDate; delete task.conflictingEvent;
        addMessage("assistant", `Perfect, I'll book it at ${fmtTime(timeInAnswer)}.`);
        proceed = true;

      } else if (wantsSwap && task.conflictingEvent) {
        const clashTask = task.conflictingEvent;
        const clashNext = findNextAvailable(
          { date: clashTask.date, time: clashTask.time },
          userSchedule,
          task.durationMinutes
        );
        if (clashNext && othersInItem(clashTask, currentUser?.id)) {
          // Other people are in the existing one: not moved without a card (decision 75 split).
          addMessage("assistant", `"${clashTask.title}" has other people in it, so I have not moved it here: that needs a card first. I'll book both at the same time; move it from the Calendar if you want to.`);
        } else if (clashNext) {
          // Decision 75: the same move with Undo; said only once it is saved.
          const where = `${clashNext.dayOffset > 0 ? fmtDate(clashNext.date) + ' at ' : ''}${fmtTime(clashNext.time)}`;
          Promise.resolve(onMoveTask(clashTask.id, clashNext.date || clashTask.date, clashNext.time)).then((r) => {
            addMessage("assistant", r && r.ok
              ? `I've moved "${clashTask.title}" to ${where} to free up the slot. Tap Undo below if that was wrong.`
              : `I could not move "${clashTask.title}", so both are booked at the same time. ${(r && r.error && r.error !== 'not-found') ? r.error : ''}`.trim());
          });
        } else {
          addMessage("assistant", `Couldn't find a free slot for "${clashTask.title}", I'll book both at the same time.`);
        }
        delete task.suggestion; delete task.suggestionDate; delete task.conflictingEvent;
        proceed = true;

      } else if (wantsReschedule && task.suggestion) {
        task.time = task.suggestion;
        if (task.suggestionDate) task.date = task.suggestionDate;
        delete task.suggestion; delete task.suggestionDate; delete task.conflictingEvent;
        addMessage("assistant", `Done, I'll put it at ${fmtTime(task.time)}.`);
        proceed = true;

      } else {
        // Didn't understand — give a gentle, specific re-prompt
        const hint = task.suggestion ? fmtTime(task.suggestion) : 'a different time';
        addMessage("assistant", `I can move it to ${hint}, shift the existing meeting out of the way, or book both at the same time, which works best?`);
        return;
      }
    } else if (pendingField === "attendeeEmailConfirm") {
      const { meeting, personName, email: existingEmail } = task;
      if (/^(yes|yeah|sure|ok|please|send|yep|go ahead)/i.test(input.trim())) {
        if (existingEmail) {
          const when = fmtDateTime(meeting?.date, meeting?.time);
          const subject = encodeURIComponent(`Invite: ${meeting?.title}`);
          const bodyText = `Hi ${personName},\n\nYou've been invited to:\n\n${meeting?.title}\n${when ? `When: ${when}\n` : ''}${meeting?.location ? `Where: ${meeting?.location}\n` : ''}\nLooking forward to seeing you there!`;
          const mailtoLink = `mailto:${existingEmail}?subject=${subject}&body=${encodeURIComponent(bodyText)}`;
          addMessage("assistant", `Here's the invite, click to open in your email app:\n\n📧 [Send invite to ${personName}](${mailtoLink})`);
        } else {
          addMessage("assistant", `What's ${personName}'s email address?`);
          setPendingTask(task);
          setPendingField("attendeeEmailAddress");
          return;
        }
      } else {
        addMessage("assistant", `No problem, ${personName} has been noted as an attendee.`);
      }
      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === "attendeeEmailAddress") {
      const { meeting, personName } = task;
      const emailAddr = input.trim();
      if (emailAddr.includes('@')) {
        const when = fmtDateTime(meeting?.date, meeting?.time);
        const subject = encodeURIComponent(`Invite: ${meeting?.title}`);
        const bodyText = `Hi ${personName},\n\nYou've been invited to:\n\n${meeting?.title}\n${when ? `When: ${when}\n` : ''}${meeting?.location ? `Where: ${meeting?.location}\n` : ''}\nLooking forward to seeing you there!`;
        const mailtoLink = `mailto:${emailAddr}?subject=${subject}&body=${encodeURIComponent(bodyText)}`;
        addMessage("assistant", `Here's the invite, click to open in your email app:\n\n📧 [Send invite to ${personName}](${mailtoLink})`);
      } else {
        addMessage("assistant", `That doesn't look like a valid email. No worries, ${personName} has been noted as an attendee.`);
      }
      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === "addAttendeeMeetingTitle") {
      const { personName, email } = task;
      const hint = input.trim();
      const meeting = userSchedule.find(t => t.title.toLowerCase().includes(hint.toLowerCase()));
      if (!meeting) {
        addMessage("assistant", `I couldn't find a meeting matching "${hint}". Can you give me more of the title?`);
        return;
      }
      const when = fmtDateTime(meeting.date, meeting.time);
      const emailLine = email
        ? `\n\nShall I draft an email invite for ${personName}?`
        : `\n\nDo you have an email address for ${personName} so I can send them an invite?`;
      addMessage("assistant", `Got it, adding ${personName} to "${meeting.title}"${when ? ` on ${when}` : ''}.${emailLine}`);
      setPendingTask({ ...task, meeting, email });
      setPendingField("attendeeEmailConfirm");
      return;
    } else if (pendingField === "suggestNextSlot") {
      if (/^yes|ok|sure$/i.test(input.trim()) && task.suggestion) {
        task.time = task.suggestion;
        delete task.suggestion;
        proceed = true;
      } else {
        addMessage("assistant", "What time should I book this for?");
        setPendingTask(task);
        setPendingField("time");
        return;
      }
    } else if (pendingField === "invoiceClient") {
      const name = input.trim();
      if (!task.amount) {
        addMessage("assistant", `Got it, invoice for **${name}**. What's the amount?`);
        setPendingTask({ ...task, client: name });
        setPendingField("invoiceAmount");
      } else {
        const preview = `📄 **Draft Invoice**\n**Client:** ${name}\n**Amount:** £${parseFloat(task.amount).toFixed(2)}\n**Status:** Draft\n\nDoes this look right? Say **"save it"** to add to your invoices, or **"cancel"** to start over.`;
        addMessage("assistant", preview);
        setPendingTask({ ...task, client: name });
        setPendingField("invoiceApproval");
      }
      return;
    } else if (pendingField === "invoiceAmount") {
      const amountMatch = input.match(/[$£€]?\s*(\d+(?:[.,]\d{2})?)/);
      if (!amountMatch) {
        addMessage("assistant", "I didn't catch that amount. Please enter a number, e.g. 500 or £1,200.00");
        return;
      }
      const amount = parseFloat(amountMatch[1].replace(',', '.'));
      const preview = `📄 **Draft Invoice**\n**Client:** ${task.client}\n**Amount:** £${amount.toFixed(2)}\n**Status:** Draft\n\nDoes this look right? Say **"save it"** to add to your invoices, or **"cancel"** to start over.`;
      addMessage("assistant", preview);
      setPendingTask({ ...task, amount });
      setPendingField("invoiceApproval");
      return;
    } else if (pendingField === "invoiceApproval") {
      if (/^(save|yes|yep|ok|sure|save it|looks good|send it|confirm|go ahead)/i.test(input.trim())) {
        const saving = api('/api/invoices', {
          method: 'POST',
          body: JSON.stringify({
            contactName: task.client || '',
            issueDate: new Date().toISOString().slice(0, 10),
            dueDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
            status: 'draft',
            lineItems: task.amount ? [{ id: 1, description: task.rawInput || 'Service', qty: 1, unitPrice: parseFloat(task.amount) || 0, tax: 0 }] : [],
          }),
        }).then((res) => { refreshInvoices(); return res; });
        sayWhenSaved(saving, `✅ Draft invoice saved for **${task.client}** (${formatMoney(parseFloat(task.amount) || 0, currentUser?.baseCurrency || 'GBP')}). Head to **Invoicing** to add line items and send it to the client.`, 'the draft invoice');
      } else {
        addMessage("assistant", "No problem, invoice cancelled. Let me know if you need anything else.");
      }
      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === "emailTo") {
      const recipientInput = input.trim();
      let recipientEmail = null;
      // Check if it's already an email
      if (recipientInput.includes('@')) {
        recipientEmail = recipientInput;
      } else {
        const match = contacts.find(c => c.name?.toLowerCase().includes(recipientInput.toLowerCase()));
        recipientEmail = match?.email || null;
      }
      addMessage("assistant", `Email to **${recipientInput}**. What's it about?`);
      setPendingTask({ ...task, emailTo: recipientEmail || recipientInput, emailToName: recipientInput });
      setPendingField("emailSubject");
      return;
    } else if (pendingField === "emailSubject") {
      const subject = input.trim();
      addMessage("assistant", `Subject: "${subject}". What should the email say? (Say "generate" and I'll write a draft for you)`);
      setPendingTask({ ...task, emailSubject: subject });
      setPendingField("emailBody");
      return;
    } else if (pendingField === "emailBody") {
      const body = input.trim();
      const shouldGenerate = /^generate|^write it|^draft it/i.test(body);

      if (shouldGenerate) {
        addLoadingMessage();
        const prompt = `Write a short, professional email to ${task.emailToName || task.emailTo} about: ${task.emailSubject}. Keep it concise and friendly.`;
        askAidey([
          { role: 'user', content: prompt },
        ], {
          system: 'You are a professional email writing assistant. Write clear, concise emails. Do not include subject line in the body.',
        }).then((data) => {
          const draft = data?.reply?.trim() || null;
          const emailBody = draft || `Hi ${task.emailToName || 'there'},\n\nI wanted to reach out regarding ${task.emailSubject}.\n\nPlease let me know if you have any questions.\n\nBest regards`;
          const updatedTask = { ...task, emailBody };
          setPendingTask(updatedTask);
          const cardId = `approval-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          setChatMessages(prev => [
            ...prev.slice(0, -1),
            { role: 'approval', approvalData: { tool: 'send_email_legacy', input: { to: task.emailTo, subject: task.emailSubject, body: emailBody } }, cardId, ts: Date.now() },
          ]);
        });
        return;
      }

      // User typed the body directly
      const updatedTask = { ...task, emailBody: body };
      setPendingTask(updatedTask);
      addApprovalCard({ tool: 'send_email_legacy', input: { to: task.emailTo, subject: task.emailSubject, body } });
      return;
    } else if (pendingField === "emailRecipientAddress") {
      const addr = input.trim();
      if (addr.includes('@')) {
        const updatedEmailTask = { ...task, emailTo: addr };
        setPendingTask(updatedEmailTask);
        setPendingField(null);
        const cardId = `approval-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        setChatMessages(prev => [...prev, { role: 'approval', approvalData: { tool: 'send_email_legacy', input: { to: addr, subject: updatedEmailTask.emailSubject, body: updatedEmailTask.emailBody } }, cardId, ts: Date.now() }]);
      } else {
        addMessage("assistant", "That doesn't look like a valid email. Email discarded, let me know if you'd like to try again.");
        setPendingTask(null);
        setPendingField(null);
      }
      return;
    } else if (pendingField === "bookingLinkRecipient") {
      const name = input.trim();
      const recipientMatch = contacts.find(c => c.name?.toLowerCase().includes(name.toLowerCase()));
      const recipientEmail = recipientMatch?.email || null;

      const bookingUrl = task.bookingUrl;
      const mailBody = `Hi ${name},\n\nFeel free to book a time that works for you:\n\n${bookingUrl}\n\nLooking forward to speaking!`;
      if (recipientEmail) {
        const link = `mailto:${recipientEmail}?subject=${encodeURIComponent('Book a time with me')}&body=${encodeURIComponent(mailBody)}`;
        addMessage("assistant", `Here's your booking link to share with **${name}**:\n\n📅 ${bookingUrl}\n\n[Open email to ${name}](${link})`);
        setPendingTask(null);
        setPendingField(null);
      } else {
        addMessage("assistant", `What's **${name}'s** email address?`);
        setPendingTask({ ...task, recipientName: name, mailBody });
        setPendingField("bookingLinkEmail");
      }
      return;
    } else if (pendingField === "bookingLinkEmail") {
      const addr = input.trim();
      const { recipientName, bookingUrl, mailBody } = task;
      if (addr.includes('@')) {
        const link = `mailto:${addr}?subject=${encodeURIComponent('Book a time with me')}&body=${encodeURIComponent(mailBody || `Hi,\n\nHere's my booking link:\n\n${bookingUrl}`)}`;
        addMessage("assistant", `[Open email to ${recipientName || addr}](${link})`);
      } else {
        addMessage("assistant", "That doesn't look like a valid email, let me know if you'd like to try again.");
      }
      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === "meetingNotes") {
      const allUsers = mentionableMembers;
      const attendeeIds = task.attendees || [];
      const attendees = attendeeIds.map(id => rosterMap.get(id)).filter(Boolean);

      const actionItems = parseMeetingNotes(input, allUsers);

      if (actionItems.length === 0) {
        addMessage("assistant",
          "I couldn't find any clear action items in those notes.\n\nTry formats like:\n• \"Ben will update the report\"\n• \"@sam send the proposals by Friday\"\n• \"- [ ] Fix login bug – Ben\"\n\nPaste the notes again using one of those patterns."
        );
        // stay in meetingNotes pending state
        return;
      }

      // Create a task for each action item — persist to the workspace-scoped API
      // (was a localStorage orphan that never reached the DB).
      const today = getTodayISO();
      const createdTasks = [];

      for (const item of actionItems) {
        const isDept = item.assigneeIds && item.assigneeIds.length > 0;
        const assignedTeam = isDept ? item.assigneeIds : (item.assigneeId ? [item.assigneeId] : []);
        const details = isDept
          ? `Action item for ${item.assigneeDept} department from meeting notes.`
          : "Action item from meeting notes.";
        try {
          await api('/api/tasks', {
            method: 'POST',
            body: JSON.stringify({
              title: item.title,
              // No priority: nobody chose one, so it takes her usual one (Aidey Backend/lib/learntDefaults.js), else Medium.
              date: today,
              datetime: new Date().toISOString(),
              taskType: "Meeting",
              assignedTeam,
              details,
            }),
          });
          const displayName = isDept
            ? `${item.assigneeDept} team (${item.assigneeIds.map(id => allUsers.find(u => u.id === id)?.name).filter(Boolean).join(", ")})`
            : (item.assigneeName || "Unassigned");
          createdTasks.push({ title: item.title, assignedTeam, userId: item.assigneeId, displayName });
        } catch (_) { /* skip a failed item, keep going */ }
      }

      window.dispatchEvent(new Event('aidey:tasksUpdated'));

      const taskLines = createdTasks
        .map(t => `• **${t.displayName}**: ${t.title}`)
        .join("\n");

      // Release 7: no line saying the summary went to the attendees. Nothing in this flow sends it to anyone.

      const unassigned = createdTasks.filter(t => !t.assignedTeam?.length && t.displayName === "Unassigned").length;
      const assignmentNote = unassigned > 0
        ? `\n\n⚠ ${unassigned} task${unassigned > 1 ? "s" : ""} couldn't be assigned to a specific person, they'll appear in your task list. Add department/job title info in Team Management so Aidey can route these automatically next time.`
        : "";

      addMessage("assistant",
        `✅ Created ${createdTasks.length} action item${createdTasks.length !== 1 ? "s" : ""} from the meeting:\n\n${taskLines}${assignmentNote}\n\nThese are saved to your task list, and anyone they're assigned to will see them.`
      );

      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === 'meetingTranscriptReview') {
      if (/^(approve|yes|y|go ahead|confirm|create)/i.test(input.trim())) {
        const items = task.transcriptActionItems || [];
        if (!items.length) {
          addMessage('assistant', 'No transcript action items found to create.');
          setPendingTask(null);
          setPendingField(null);
          return;
        }

        const today = getTodayISO();
        const allUsers = mentionableMembers;
        const createdTasks = [];

        // Persist to the workspace-scoped tasks API (was a localStorage orphan).
        for (const item of items) {
          const isDept = item.assigneeIds && item.assigneeIds.length > 0;
          const assignedTeam = isDept ? item.assigneeIds : (item.assigneeId ? [item.assigneeId] : []);
          const details = isDept
            ? `Action item for ${item.assigneeDept} department from transcript.`
            : 'Action item from meeting transcript.';
          try {
            await api('/api/tasks', {
              method: 'POST',
              body: JSON.stringify({
                title: item.title,
                // No priority: nobody chose one, so it takes her usual one (Aidey Backend/lib/learntDefaults.js), else Medium.
                date: today,
                datetime: new Date().toISOString(),
                taskType: 'Meeting',
                assignedTeam,
                details,
              }),
            });
            const displayName = isDept
              ? `${item.assigneeDept} team (${item.assigneeIds.map((id) => allUsers.find((u) => u.id === id)?.name).filter(Boolean).join(', ')})`
              : (item.assigneeName || 'Unassigned');
            createdTasks.push({ title: item.title, displayName });
          } catch (_) { /* skip a failed item, keep going */ }
        }

        window.dispatchEvent(new Event('aidey:tasksUpdated'));
        addMessage('assistant', `Created ${createdTasks.length} task(s) from the transcript, saved to your task list.`);
      } else {
        addMessage('assistant', 'No tasks created. Transcript note is saved.');
      }
      setPendingTask(null);
      setPendingField(null);
      return;
    }

    if (proceed) {
      setPendingTask(task);
      setPendingField(null);
      handleSingleAction(task);
    }
  }

  // ── Block C: the look (AideyChat/chat.css, the v2 kit). RESTYLE ONLY: every handler the
  // render calls below is the one the old two renders called, unchanged; the popup and the
  // page now share one tree (the page also gained the assistant_correction branch, which only
  // the popup had). An action card's button fills the composer and submits the same form a typed
  // message does, so it goes through exactly the same path and the same confirmation gate.
  if (sessionRef) sessionRef.current = sessionIdRef.current;
  const composerFormRef = useRef(null);
  const composerInputRef = useRef(null);
  const [queuedSend, setQueuedSend] = useState(false);

  function submitComposer() {
    const form = composerFormRef.current;
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  }

  useEffect(() => {
    if (!queuedSend) return;
    setQueuedSend(false);
    submitComposer();
  }, [queuedSend]); // eslint-disable-line react-hooks/exhaustive-deps

  // The composer grows with what is typed, up to 140px, then scrolls.
  useEffect(() => {
    const ta = composerInputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 140)}px`;
  }, [chatInput]);

  // The welcome reads from the top (the scroll-to-latest effect above would start it scrolled).
  const isPhoneWidth = useBreakpoint() === 'phone';
  const welcomeNow = chatMessages.some(m => m.type === 'greeting') && chatMessages.every(m => m.role === 'assistant' && !m.isTyping);
  useEffect(() => {
    if (!welcomeNow) return undefined;
    const t = setTimeout(() => { if (chatScrollRef.current) chatScrollRef.current.scrollTop = 0; }, 30);
    return () => clearTimeout(t);
  }, [chatMessages, welcomeNow]);

  function sendInstruction(text) {
    setChatInput(text);
    setQueuedSend(true);
  }

  // Enter sends, Shift+Enter is a new line (the old single-line input sent on Enter too).
  function handleComposerKeyDown(e) {
    handleKeyDown(e);
    if (e.defaultPrevented) return;
    if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent && e.nativeEvent.isComposing)) {
      e.preventDefault();
      submitComposer();
    }
  }

  const variant = isPopup ? 'popup' : 'page';
  const visibleMessages = chatMessages.filter(m => !m.synthetic);
  // Empty (decision 78): the greeting and the composer. A message addressed to the person (a
  // colleague's handover, one they asked for by tapping a notification) still shows under it.
  const greetingMsg = visibleMessages.find(m => m.role === 'assistant' && m.type === 'greeting');
  const isWelcome = !!greetingMsg && visibleMessages.every(m => m.role === 'assistant' && !m.isTyping);
  const openingNotes = visibleMessages.filter(m => m !== greetingMsg);
  // C-b: the popup's summary greeting. Facts from what the screens show this person, at most two;
  // none when nothing is notable (utils/chatSummary). Invoices only in a business workspace and
  // only once the Invoices list really loaded.
  const summaryFacts = isPopup && isWelcome && !isChildAccount ? popupSummaryFacts({
    tasks: userSchedule,
    invoices,
    invoicesUsable: !isPersonalWorkspace && !!invoicesLoaded && !invoicesLoadFailed,
    timezone: currentUser?.timezone,
  }) : [];
  // A fact opens its screen. Personal has no Calendar page: its calendar is the Family page's tab
  // (decision 56), reached the way the sidebar reaches it.
  function openFact(fact) {
    if (fact.view === 'Calendar' && isPersonalWorkspace) {
      setDeepLink('family_tab', 'calendar');
      if (typeof setFilter === 'function') setFilter('Family');
    } else {
      const path = { Tasks: '/tasks', Calendar: '/calendar', Invoices: '/invoices' }[fact.view];
      window.dispatchEvent(new CustomEvent('aidey:navigate', { detail: path }));
    }
    // On a phone the popup covers the page it just opened.
    if (isPhoneWidth && typeof onClose === 'function') onClose();
  }

  // C-b: action cards under an answer. The button sends the card's instruction into this chat;
  // "Done" only once a confirmed call that does this card's action came back ok.
  // "Draft created" (create_invoice, Carla 2026-10-21): the draft is already written. Open goes to
  // it; Undo deletes it, and the card says "Undone" only once the server has (a sent invoice is
  // refused there, and the reason shows on the card). The mark is saved with the chat.
  function openDraftInvoice(c) {
    setDeepLink('invoice', String(c.invoiceId));
    window.dispatchEvent(new CustomEvent('aidey:navigate', { detail: '/invoices' }));
    if (isPhoneWidth && typeof onClose === 'function') onClose();
  }
  async function undoDraftInvoice(c) {
    const key = `${c.kind}-${c.id}`;
    setDraftCardError((prev) => ({ ...prev, [key]: null }));
    try {
      await api(`/api/invoices/${c.invoiceId}`, { method: 'DELETE' });
    } catch (err) {
      setDraftCardError((prev) => ({ ...prev, [key]: messageFromError(err, 'The draft could not be deleted.') }));
      return;
    }
    setChatMessages((prev) => prev.map((msg) => (Array.isArray(msg.actionCards) && msg.actionCards.includes(c)
      ? { ...msg, actionCards: msg.actionCards.map((x) => (x === c ? { ...x, undone: true } : x)) }
      : msg)));
  }

  function renderActionCards(m) {
    if (!Array.isArray(m.actionCards) || !m.actionCards.length) return null;
    return (
      <div className="achat-acards">
        {m.actionCards.map(c => {
          // Email Phase 1: the inbox and an email, for the PERSON (the model only got ids and flags).
          // Someone else's words, so always rendered as text, never as HTML.
          if (c.kind === 'email_list' && c.display && Array.isArray(c.display.emails)) {
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-mail" data-testid="chat-email-list">
                <ol className="achat-mail__list">
                  {c.display.emails.map((e) => (
                    <li key={e.id} className={`achat-mail__row${e.unread ? ' achat-mail__row--unread' : ''}`}>
                      <span className="achat-mail__ref">{e.ref}.</span>
                      <span className="achat-mail__text">
                        <span className="achat-mail__from">{e.fromName ? `${e.fromName} <${e.fromEmail}>` : e.fromEmail}{e.date ? ` · ${formatTaskWhen(new Date(e.date))}` : ''}</span>
                        <span className="achat-mail__subject">{e.subject}</span>
                        {e.preview && <span className="achat-mail__preview">{e.preview}</span>}
                      </span>
                    </li>
                  ))}
                </ol>
                <Button variant="secondary" size="sm" onClick={() => {
                  window.dispatchEvent(new CustomEvent('aidey:navigate', { detail: '/emails' }));
                  if (isPhoneWidth && typeof onClose === 'function') onClose();
                }}>Open Emails</Button>
              </div>
            );
          }
          // Chat gaps (c): the connected calendars' events and the client bookings, for the person.
          if (c.kind === 'calendar_events' && c.display && Array.isArray(c.display.events)) {
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-mail" data-testid="chat-calendar-events">
                <span className="achat-mail__from">From your connected calendars</span>
                <ol className="achat-mail__list">
                  {c.display.events.map((e) => (
                    <li key={e.ref} className="achat-mail__row">
                      <span className="achat-mail__text">
                        <span className="achat-mail__subject">{e.title}</span>
                        <span className="achat-mail__from">{e.allDay ? 'All day' : formatTaskWhen(new Date(e.start))}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            );
          }
          // Email Phase 2: Unsubscribe opens the email's OWN https page in the person's browser (never
          // called by Aidey's server). A link that is not plain https gets no button.
          if (c.kind === 'open_link' && c.display) {
            const u = safeUnsubscribeUrl(c.display.url);
            if (!u) return null;
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-mail" data-testid="chat-open-link">
                <span className="achat-mail__from">{c.display.note}</span>
                <a className="ui-btn ui-btn--secondary ui-btn--sm" href={u.href} target="_blank" rel="noopener noreferrer">{c.display.label}</a>
              </div>
            );
          }
          if (c.kind === 'cleanup' && c.display && Array.isArray(c.display.suggestions)) {
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-mail" data-testid="chat-cleanup">
                <ol className="achat-mail__list">
                  {c.display.suggestions.map((g) => {
                    const u = safeUnsubscribeUrl(g.unsubscribeUrl);
                    return (
                      <li key={g.sender} className="achat-mail__row">
                        <span className="achat-mail__text">
                          <span className="achat-mail__subject">{g.name ? `${g.name} <${g.sender}>` : g.sender}</span>
                          <span className="achat-mail__from">{g.count} emails in your inbox</span>
                          <span className="achat-mail__actions">
                            {u && <a className="ui-btn ui-btn--tertiary ui-btn--sm" href={u.href} target="_blank" rel="noopener noreferrer" aria-label={`Unsubscribe from ${g.sender}`}>Unsubscribe</a>}
                            <Button variant="tertiary" size="sm" aria-label={`Archive all from ${g.sender}`} onClick={() => sendInstruction(`Archive every email from ${g.sender}.`)}>Archive all</Button>
                            <Button variant="tertiary" size="sm" aria-label={`Block ${g.sender}`} onClick={() => sendInstruction(`Block ${g.sender}.`)}>Block</Button>
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            );
          }
          // Chat gaps (i): a form's answers by question, written by clients: for the person only.
          if (c.kind === 'form_answers' && c.display && Array.isArray(c.display.entries)) {
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-mail" data-testid="chat-form-answers">
                <ol className="achat-mail__list">
                  {c.display.entries.map((e) => (
                    <li key={e.id} className="achat-mail__row">
                      <span className="achat-mail__text">
                        <span className="achat-mail__from">{String(e.received || '').slice(0, 10)}</span>
                        {(e.pairs || []).map(([q, a], i) => (
                          <span key={i} className="achat-mail__preview"><strong>{q}</strong> {a}</span>
                        ))}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            );
          }
          // Chat gaps (f): a private list (journal titles), for the person only.
          if (c.kind === 'personal_list' && c.display && Array.isArray(c.display.items)) {
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-mail" data-testid="chat-personal-list">
                <ol className="achat-mail__list">
                  {c.display.items.map((i) => (
                    <li key={i.id} className="achat-mail__row">
                      <span className="achat-mail__text">
                        <span className="achat-mail__subject">{i.title}</span>
                        {i.date && <span className="achat-mail__from">{String(i.date).slice(0, 10)}</span>}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            );
          }
          // Chat gaps (d): the invoices, with the client's name, for the person.
          if (c.kind === 'invoices' && c.display && Array.isArray(c.display.invoices)) {
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-mail" data-testid="chat-invoices">
                <ol className="achat-mail__list">
                  {c.display.invoices.map((i) => (
                    <li key={i.id} className="achat-mail__row">
                      <span className="achat-mail__text">
                        <span className="achat-mail__subject">{i.number || 'Draft'} · {formatMoney(Number(i.total) || 0, i.currency || 'GBP')} · {i.status}</span>
                        <span className="achat-mail__from">{i.contactName || 'No client'}{i.due ? ` · due ${i.due}` : ''}</span>
                      </span>
                    </li>
                  ))}
                </ol>
                <Button variant="secondary" size="sm" onClick={() => {
                  window.dispatchEvent(new CustomEvent('aidey:navigate', { detail: '/invoices' }));
                  if (isPhoneWidth && typeof onClose === 'function') onClose();
                }}>Open Invoices</Button>
              </div>
            );
          }
          if (c.kind === 'bookings' && c.display && Array.isArray(c.display.bookings)) {
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-mail" data-testid="chat-bookings">
                <ol className="achat-mail__list">
                  {c.display.bookings.map((b) => (
                    <li key={b.id} className="achat-mail__row">
                      <span className="achat-mail__ref">{b.ref}.</span>
                      <span className="achat-mail__text">
                        <span className="achat-mail__subject">{b.meetingType || 'Booking'} · {formatTaskWhen(new Date(b.start))}</span>
                        <span className="achat-mail__from">{b.guestName ? `${b.guestName} <${b.guestEmail}>` : b.guestEmail}</span>
                        {/* Build everything: a booking's details carry the guest's own answers. */}
                        {Array.isArray(b.answers) && b.answers.map(([q, a], i) => (
                          <span key={i} className="achat-mail__preview"><strong>{q}</strong> {a}</span>
                        ))}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            );
          }
          // Build everything: the family chat, in the family's own words.
          if (c.kind === 'family_chat' && c.display && Array.isArray(c.display.messages)) {
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-mail" data-testid="chat-family-chat">
                <ol className="achat-mail__list">
                  {c.display.messages.map((m) => (
                    <li key={m.id} className="achat-mail__row">
                      <span className="achat-mail__text">
                        <span className="achat-mail__from">{m.from}{m.when ? ` · ${formatTaskWhen(new Date(m.when))}` : ''}</span>
                        <span className="achat-mail__preview">{m.body}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            );
          }
          // Build everything: an invoice's PDF, downloaded the Invoices screen's way (utils/invoicePdf).
          if (c.kind === 'invoice_pdf' && c.display) {
            const failed = draftCardError[`${c.kind}-${c.id}`];
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-mail" data-testid="chat-invoice-pdf">
                <Button variant="secondary" size="sm" onClick={async () => {
                  const msg = await downloadInvoicePdf({ id: c.display.id, number: c.display.number });
                  setDraftCardError((prev) => ({ ...prev, [`${c.kind}-${c.id}`]: msg || null }));
                }}>{`Download ${c.display.number || 'invoice'} (PDF)`}</Button>
                {failed && <span className="achat-mail__from" role="alert">{failed}</span>}
              </div>
            );
          }
          // Build everything: a workflow test run, step by step (nothing was sent).
          if (c.kind === 'workflow_test' && c.display && Array.isArray(c.display.lines)) {
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-mail" data-testid="chat-workflow-test">
                <ol className="achat-mail__list">
                  {c.display.lines.map((l, i) => (
                    <li key={i} className="achat-mail__row">
                      <span className="achat-mail__text">
                        <span className="achat-mail__subject">{l.step}</span>
                        <span className="achat-mail__preview">{l.summary}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            );
          }
          // Build everything: a contact's history, in each entry's own words (some came from emails).
          if (c.kind === 'contact_history' && c.display && Array.isArray(c.display.entries)) {
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-mail" data-testid="chat-contact-history">
                <ol className="achat-mail__list">
                  {c.display.entries.map((e) => (
                    <li key={e.id} className="achat-mail__row">
                      <span className="achat-mail__text">
                        <span className="achat-mail__from">{e.kind}{e.when ? ` · ${formatTaskWhen(new Date(e.when))}` : ''}</span>
                        {e.summary && <span className="achat-mail__preview">{e.summary}</span>}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            );
          }
          // Build everything: a meeting transcript's summary, decisions and action items (other people's words).
          if (c.kind === 'transcript_summary' && c.display) {
            const d = c.display;
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-mail" data-testid="chat-transcript-summary">
                {d.title && <span className="achat-mail__subject">{d.title}</span>}
                {d.summary && <span className="achat-mail__preview">{d.summary}</span>}
                {Array.isArray(d.decisions) && d.decisions.length > 0 && (
                  <><span className="achat-mail__from">Decisions</span>
                    <ul className="achat-mail__list">{d.decisions.map((x, i) => <li key={i} className="achat-mail__preview">{x}</li>)}</ul></>
                )}
                {Array.isArray(d.actionItems) && d.actionItems.length > 0 && (
                  <><span className="achat-mail__from">Action items</span>
                    <ul className="achat-mail__list">{d.actionItems.map((x, i) => <li key={i} className="achat-mail__preview">{x}</li>)}</ul></>
                )}
              </div>
            );
          }
          if (c.kind === 'email_message' && c.display) {
            const d = c.display;
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-mail" data-testid="chat-email-message">
                <span className="achat-mail__from">{d.fromName ? `${d.fromName} <${d.fromEmail}>` : d.fromEmail}{d.date ? ` · ${formatTaskWhen(new Date(d.date))}` : ''}</span>
                <span className="achat-mail__subject">{d.subject}</span>
                <div className="achat-mail__body">{d.body}</div>
              </div>
            );
          }
          // Release 7: open_page. A button to the page where something is done (the server's
          // list of real pages, lib/chatCapabilities CHAT_PAGES).
          if (c.kind === 'open_page') {
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-acard" data-testid="chat-action-card">
                <span className="achat-acard__stripe" style={{ '--ui-bg': v(AREA_TAG.tasks.color) }} aria-hidden="true" />
                <div className="achat-acard__text">
                  <span className="achat-acard__title">{c.title}</span>
                  <span className="achat-acard__meta">{c.meta}</span>
                </div>
                <Button variant="secondary" size="sm" onClick={() => {
                  window.dispatchEvent(new CustomEvent('aidey:navigate', { detail: c.path }));
                  if (isPhoneWidth && typeof onClose === 'function') onClose();
                }}>{c.label}</Button>
              </div>
            );
          }
          if (c.kind === 'invoice_draft') {
            const failed = draftCardError[`${c.kind}-${c.id}`];
            return (
              <div key={`${c.kind}-${c.id}`} className="achat-acard" data-testid="chat-action-card">
                <span className="achat-acard__stripe" style={{ '--ui-bg': v(AREA_TAG.money.color) }} aria-hidden="true" />
                <div className="achat-acard__text">
                  <span className="achat-acard__title">{c.undone ? 'Undone, draft deleted' : c.title}</span>
                  <span className="achat-acard__meta" role={failed ? 'alert' : undefined}>{failed || c.meta}</span>
                </div>
                {!c.undone && (
                  <>
                    <Button variant="secondary" size="sm" onClick={() => openDraftInvoice(c)}>Open</Button>
                    <Button variant="tertiary" size="sm" onClick={() => undoDraftInvoice(c)}>Undo</Button>
                  </>
                )}
              </div>
            );
          }
          const off = c.area && areas[c.area] === false;
          return (
            <div key={`${c.kind}-${c.id}`} className="achat-acard" data-testid="chat-action-card">
              <span className="achat-acard__stripe" style={{ '--ui-bg': v((AREA_TAG[c.area] || AREA_TAG.tasks).color) }} aria-hidden="true" />
              <div className="achat-acard__text">
                <span className="achat-acard__title">{c.title}</span>
                <span className="achat-acard__meta">{off ? `${AREA_TAG[c.area].label} is off in this chat` : c.meta}</span>
              </div>
              {c.done ? (
                <span className="achat-acard__done" role="status">Done ✓</span>
              ) : (
                <Button variant="secondary" size="sm" disabled={off} onClick={() => sendInstruction(c.instruction)}>{c.label}</Button>
              )}
            </div>
          );
        })}
      </div>
    );
  }
  const firstName = (currentUser?.firstName || String(currentUser?.name || '').trim().split(/\s+/)[0] || '').trim();
  const hour = new Date().getHours();
  const partOfDay = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';

  // An assistant line: the Aidey mark beside it on the page, none in the narrow popup.
  const say = (key, children, wide = false) => (
    <div key={key} className={`achat-say${wide ? ' achat-say--wide' : ''}`}>
      {!isPopup && <span className="achat-av" aria-hidden="true">A</span>}
      <div className="achat-say__col">{children}</div>
    </div>
  );

  function renderChatMessage(m, i) {
    if (m.role === 'approval') {
      return say(i, (
        <ApprovalCard
          approvalData={m.approvalData}
          onConfirm={(editedBody) => handleApprovalConfirm(m, editedBody)}
          onCancel={() => handleApprovalCancel(m)}
        />
      ), true);
    }
    if (m.role === 'quoted_external') {
      /* Content written by someone OUTSIDE the business. It never entered the
         model's prompt (lib/externalText.js redacts it, and tool results
         re-enter context so it cannot be fetched with a tool either). This is
         the ONLY route by which it reaches a screen: quoted, attributed to the
         envelope sender, explicitly not vouched for, links plain text. */
      return say(m.cardId || i, (
        <QuotedExternal
          senderEmail={m.quoted?.senderEmail}
          text={m.quoted?.text}
          kind={m.quoted?.kind}
          receivedAt={m.quoted?.receivedAt}
        />
      ), true);
    }
    if (m.role === 'email_draft') {
      return say(m.cardId || i, (
        <EmailDraftCard
          draft={m.emailDraft}
          onSend={async (body) => {
            try {
              if (m.emailDraft.confirmToken) {
                // A chat draft's Send IS the send confirm: the card shows who, the subject and the
                // body, and the server sends to the recipient it signed (only the body can change).
                await confirmAideyTool('send_email', m.emailDraft.confirmToken, areasRef.current, { body });
              } else if (!m.emailDraft.to) {
                throw new Error('This draft has no recipient yet. Tell me who it is for and I will redo it');
              } else {
                // A draft Meeting Mode wrote for the owner (no chat card behind it).
                await api('/api/email/send', {
                  method: 'POST',
                  body: JSON.stringify({ to: m.emailDraft.to, subject: m.emailDraft.subject, body }),
                });
              }
              setChatMessages(prev => prev.map(msg =>
                msg.cardId === m.cardId
                  ? { role: 'assistant', content: `Sent. Your email to **${m.emailDraft.to}** is on its way.`, ts: Date.now() }
                  : msg
              ));
            } catch (err) {
              setChatMessages(prev => prev.map(msg =>
                msg.cardId === m.cardId
                  ? { role: 'assistant', content: `Couldn't send that email: ${err.message}`, ts: Date.now() }
                  : msg
              ));
            }
          }}
          onCancel={() => {
            setChatMessages(prev => prev.map(msg =>
              msg.cardId === m.cardId
                ? { role: 'assistant', content: 'No problem, let me know if you need anything else.', ts: Date.now() }
                : msg
            ));
          }}
        />
      ), true);
    }
    if (m.role === 'workload_redistribution') {
      return say(m.cardId || i, (
        <WorkloadRedistributionCard
          suggestions={m.suggestions || []}
          onConfirm={(approved) => handleWorkloadConfirm(m, approved)}
          onCancel={() => handleWorkloadCancel(m)}
        />
      ), true);
    }
    if (m.role === 'absence_delegation') {
      return say(m.cardId || i, (
        <AbsenceDelegationCard
          absenceData={m.absenceData}
          leaveRequestId={m.leaveRequestId}
          onConfirm={(payload) => handleAbsenceDelegationConfirm(m, payload)}
          onCancel={() => handleAbsenceDelegationCancel(m)}
        />
      ), true);
    }
    if (m.role === 'session_resume') {
      return say(i, (
        <SessionResumeCard
          sessionTitle={m.sessionTitle}
          sessionTime={m.sessionTime}
          onContinue={() => handleSessionContinue(m.sessionId)}
          onFresh={handleSessionFresh}
        />
      ), true);
    }
    if (m.role === 'assistant_correction') {
      // THE CORRECTION IS NOT AN ASSISTANT BUBBLE.
      //
      // The catch-all below would have rendered this role (it renders any
      // unknown role) but as an ordinary assistant bubble, which reads as
      // Aidey continuing to talk. A correction that looks like the thing it
      // is correcting is the same defect as a refusal nobody can see:
      // visible, and carrying none of its meaning.
      //
      // It is deliberately NOT in the assistant/user filter that builds
      // conversation history, so the model is never handed these words as
      // though it had said them. Block C: the full page shows it too (it only
      // reached the popup before).
      return (
        <div key={i} role="alert" className="achat-correction">
          <span className="achat-correction__label">Correction</span>
          {m.content}
        </div>
      );
    }
    if (m.role === 'integration-required') {
      return say(i, (
        <IntegrationPrompt
          integrationName={m.integrationName}
          onNavigate={() => { window.dispatchEvent(new CustomEvent('aidey:goto-settings', { detail: 'video' })); }}
        />
      ), true);
    }
    if (m.role === 'user') {
      return <div key={i} className="achat-user">{renderMessage(m.content)}</div>;
    }
    return say(i, (
      <>
        <div className={`achat-bubble${m.tone === 'problem' ? ' achat-bubble--problem' : ''}`}>
          {m.isTyping ? (
            <span className="achat-typing" role="status" aria-label="Aidey is typing">
              <span /><span /><span />
            </span>
          ) : renderMessage(m.content)}
        </div>
        {!m.isTyping && renderActionCards(m)}
      </>
    ), Array.isArray(m.actionCards) && m.actionCards.length > 0);
  }

  const placeholder = isPhoneWidth
    ? (isPersonalWorkspace ? 'Ask me to add shopping, plan your day...' : 'Ask Aidey anything… (@name to mention)')
    : isPersonalWorkspace
    ? "Ask me to add shopping, plan your day..."
    : isPopup
      ? "Ask anything… (@name to mention someone)"
      : "Ask Aidey to plan, draft, chase or summarise… (@name to mention someone)";

  return (
    <div className={`achat-chat achat-chat--${variant}${scrollable ? ' achat-chat--compact' : ''}${fullPage ? ' achat-chat--full' : ''}`}>
      {meetingModeActive && (
        <div className="achat-layer" style={{ zIndex: 2 }}>
          <MeetingMode
            attendees={meetingModeAttendees}
            meetingTitle={meetingModeTitle}
            onEnd={handleMeetingEnd}
            onDismiss={handleMeetingDismiss}
          />
        </div>
      )}
      {zoomPanelOpen && !meetingModeActive && (
        <div className="achat-layer" style={{ zIndex: 1 }}>
          <MeetingTranscripts onDismiss={() => setZoomPanelOpen(false)} />
        </div>
      )}
      {recorderOpen && !meetingModeActive && (
        <div className="achat-layer achat-layer--scroll" style={{ zIndex: 2 }}>
          <MeetingRecorder onClose={() => setRecorderOpen(false)} onCreateTask={onCreateMeetingTask} setFilter={setFilter} />
        </div>
      )}
      {meetingChoiceOpen && (
        <MeetingChoiceModal
          onTranscript={() => { setMeetingChoiceOpen(false); setZoomPanelOpen(true); }}
          onRecord={() => { setMeetingChoiceOpen(false); setRecorderOpen(true); }}
          onCancel={() => setMeetingChoiceOpen(false)}
        />
      )}
      {!isPopup && onClose && (
        <Button variant="tertiary" size="sm" className="achat-close" onClick={handleClose}>Close</Button>
      )}

      <div className="achat-scroll" ref={chatScrollRef} aria-live="polite">
        {loadingSession ? (
          <p className="achat-loading" role="status" data-testid="chat-opening">Opening this chat…</p>
        ) : sessionLoadFailed ? (
          <div className="achat-loadfail" role="alert" data-testid="chat-open-failed">
            <strong>This chat could not be opened.</strong>
            <span>Its messages did not load. Nothing has been changed or deleted. Try again in a moment.</span>
          </div>
        ) : isWelcome && !isPopup ? (
          <div className="achat-hero">
            <span className="achat-av achat-av--hero" aria-hidden="true">A</span>
            <div>
              <h2 className="achat-hero__title">Good {partOfDay}{firstName ? `, ${firstName}` : ''}.</h2>
              <p className="achat-hero__sub">{isChildAccount ? greetingMsg.content : 'How can I help?'}</p>
            </div>
            {openingNotes.length > 0 && (
              <div className="achat-hero__notes">
                {openingNotes.map((m, i) => <div key={i} className="achat-bubble">{renderMessage(m.content)}</div>)}
              </div>
            )}
          </div>
        ) : (
          <>
            {visibleMessages.map((m, i) => renderChatMessage(m, i))}
            {summaryFacts.length > 0 && (
              <ul className="achat-facts" aria-label="Today at a glance" data-testid="chat-summary">
                {summaryFacts.map(f => (
                  <li key={f.key}>
                    <button type="button" className="achat-fact" onClick={() => openFact(f)}>
                      <span>{f.text}</span>
                      <span className="achat-fact__go">Open {f.view === 'Calendar' ? 'calendar' : f.view.toLowerCase()} <span aria-hidden="true">→</span></span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <div className="achat-notes">
        {/* Voice permission banners: only once the mic has been tapped. Both showed on every visit (live,
            2026-10-08, the blocked one where the browser had already refused); the tap raises the prompt. */}
        {permissionState === 'unknown' && micTapped && (
          <Banner tone="info" action={<Button variant="secondary" size="sm" onClick={requestPermission}>Allow access</Button>}>
            Aidey needs microphone access to take voice input. Your audio is processed instantly and never stored.
          </Banner>
        )}
        {permissionState === 'denied' && micTapped && (
          <Banner tone="notice">Microphone access is blocked. Update your browser site permissions to enable voice input.</Banner>
        )}
        {/* Listening indicator */}
        {voiceState === 'listening' && <p className="achat-notes__line" role="status">Listening…</p>}
        {/* Voice confirmation bar */}
        {voiceConfirmation && (
          <Banner tone="info" action={(
            <span className="achat-notes__row">
              <Button variant="confirm" size="sm" onClick={handleVoiceConfirm}>Confirm</Button>
              <Button variant="tertiary" size="sm" onClick={handleVoiceCancel}>Cancel</Button>
            </span>
          )}>
            Create <strong>{voiceConfirmation.intent.replace('create_', '')}</strong>: {voiceConfirmation.summary}
          </Banner>
        )}
        {isMeetingTranscribing && (
          <Banner tone="info" action={<Button variant="dark" size="sm" onClick={stopMeetingTranscription}>Stop transcribing</Button>}>
            <strong>Live transcript:</strong> {liveMeetingTranscript || 'Listening...'}
          </Banner>
        )}
        {pendingTranscribePrompt && !isMeetingTranscribing && !meetingModeActive && (
          <div className="achat-notes__row">
            <Button
              variant="confirm"
              size="sm"
              onClick={() => {
                const prompt = pendingTranscribePrompt;
                activateMeetingMode(
                  Array.isArray(prompt.attendees) ? prompt.attendees : [],
                  prompt.title || prompt
                );
              }}
            >
              Yes, start notes
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => {
                setPendingTranscribePrompt(null);
                addMessage('assistant', 'No problem, just let me know if you need anything.');
              }}
            >
              Not now
            </Button>
          </div>
        )}
      </div>

      <div className="achat-foot">
        {/* @Mention suggestions, just above the composer */}
        {showMentions && mentionSuggestions.length > 0 && (
          <ul className="achat-mentions" aria-label="People you can mention">
            {mentionSuggestions.map((suggestion) => (
              <li key={suggestion.id}>
                <button type="button" className="achat-mention" onClick={() => selectMention(suggestion)}>
                  <Avatar name={suggestion.name} size={24} color="pastel-lilac" />
                  <span className="achat-mention__text">
                    <span className="achat-mention__handle">{suggestion.handle}</span>
                    <span className="achat-mention__who">{suggestion.name} · {suggestion.email}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <form
          ref={composerFormRef}
          onSubmit={pendingTask && pendingField ? e => {
            e.preventDefault();
            handleFieldInput(chatInput);
            setChatInput("");
          } : handleChatSubmit}
          className="achat-composer"
        >
          <Textarea
            ref={composerInputRef}
            rows={1}
            data-tour-id={isPopup ? undefined : 'chat-input'}
            aria-label="Message Aidey"
            value={chatInput}
            onChange={handleInputChange}
            onKeyDown={handleComposerKeyDown}
            className="achat-input"
            placeholder={placeholder}
          />
          <input
            ref={attachInputRef}
            type="file"
            accept={IMPORT_ACCEPT}
            className="achat-attach-input"
            data-testid="chat-attach-input"
            tabIndex={-1}
            aria-hidden="true"
            onChange={handleAttachChange}
          />
          <button
            type="button"
            className="achat-attach"
            aria-label="Attach a document to import"
            title="Attach a document to import (PDF, DOCX or TXT)"
            aria-describedby={attachError ? 'achat-attach-error' : undefined}
            onClick={() => { setAttachError(''); attachInputRef.current?.click(); }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }} aria-hidden="true">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
          <VoiceInput voiceState={voiceState} onClick={() => { setMicTapped(true); startListening(); }} />
          <Button type="submit" variant="confirm" size="sm">Send</Button>
        </form>
        {attachError && (
          <p id="achat-attach-error" className="achat-attach-error" role="alert">{attachError}</p>
        )}
        {ownImporter && createPortal(
          <DocumentImporter
            initialFile={ownImporter.file}
            onClose={() => setOwnImporter(null)}
            onViewProject={() => { setOwnImporter(null); if (typeof setFilter === 'function') setFilter('ProjectTracker'); }}
          />,
          document.body,
        )}
        {!isPopup && (
          <p className="achat-hint">Enter to send · Shift+Enter for a new line · Aidey can make mistakes, so check anything important.</p>
        )}
      </div>
    </div>
  );
}
